import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  alpha,
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  Grid,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import { useNavigate, useParams } from 'react-router-dom';
import {
  calculateBoardComboDiagnosticsFromComboKeys,
  calculateBoardImpactFromComboKeys,
  type BoardComboDiagnostic,
  type BoardDrawCategoryKey,
  type BoardImpactCategoryKey,
} from '../utils/boardImpact';
import {
  createPokerStudyBoardNodeChild,
  createPokerStudyNodeAction,
  getPokerStudyBoardWorkspace,
  getPokerStudyNodeNotes,
  getPokerStudyNodeRangeState,
  getPokerStudySpotDetail,
  listPokerStudyBoardPreflopRanges,
  listPokerStudyNodeNotesByNodeIds,
  listPokerStudyPreflopRangeTemplates,
  listPokerStudySpotPreflopRanges,
  updatePokerStudyBoardNode,
  updatePokerStudySpot,
  upsertPokerStudyBoardPreflopRange,
  upsertPokerStudyNodeNotes,
  upsertPokerStudyNodeRangeState,
} from '../services/pokerStudyService';
import type {
  PokerStudyBoard,
  PokerStudyBoardPreflopRange,
  PokerStudyBoardWorkspace,
  PokerStudyNodeAction,
  PokerStudyNodeNotes,
  PokerStudyNodeRangeState,
  PokerStudyPreflopRangeTemplate,
  PokerStudySpotPreflopRange,
} from '../types/pokerStudy';

type LocalRangeCell = {
  hand_class: string;
  combo_count_total: number;
  base_action_id: string | null;
  action_distribution: Record<string, number>;
  has_combo_overrides: boolean;
};

type LocalRangeState = {
  cells: Record<string, LocalRangeCell>;
  combo_overrides: Record<string, Record<string, string | null>>;
};

type QuickActionPreset = {
  action_type: 'check' | 'bet' | 'call' | 'raise' | 'fold';
  label: string;
  size_percentage: number | null;
};

type PreflopRangeKey = 'open_raise' | 'caller';

type NodeRangeAutosaveStatus = 'idle' | 'saving' | 'saved' | 'error';

type BoardImpactActionFilter = 'all' | string;

type BoardImpactActionSegment = {
  actionId: string;
  label: string;
  color: string;
  combos: number;
  percentage: number;
};

type BoardImpactInspectionState = {
  madeHandKeys: Set<BoardImpactCategoryKey>;
  drawKeys: Set<BoardDrawCategoryKey>;
  actionIds: Set<string>;
} | null;

type PreflopRangeSummary = {
  key: PreflopRangeKey;
  title: string;
  position: string;
  subtitle: string;
  handClasses: Set<string>;
  comboCount: number;
  percentageLabel: string;
  source: 'spot' | 'board' | 'empty';
  sourceLabel: string;
  sourceSpotRangeId: string | null;
};

const MATRIX_RANKS = ['A', 'K', 'Q', 'J', 'T', '9', '8', '7', '6', '5', '4', '3', '2'];
const CARD_SUITS = ['s', 'h', 'd', 'c'];

const SUIT_META: Record<string, { symbol: string; color: string; label: string }> = {
  s: { symbol: '♠', color: '#111827', label: 'picas' },
  h: { symbol: '♥', color: '#ef4444', label: 'corazones' },
  d: { symbol: '♦', color: '#2563eb', label: 'diamantes' },
  c: { symbol: '♣', color: '#16a34a', label: 'tréboles' },
};

function buildHandClass(rowRank: string, colRank: string): string {
  if (rowRank === colRank) {
    return `${rowRank}${colRank}`;
  }

  return MATRIX_RANKS.indexOf(rowRank) < MATRIX_RANKS.indexOf(colRank)
    ? `${rowRank}${colRank}s`
    : `${colRank}${rowRank}o`;
}

function buildMatrix(): string[][] {
  return MATRIX_RANKS.map((rowRank) =>
    MATRIX_RANKS.map((colRank) => buildHandClass(rowRank, colRank)),
  );
}

function buildTheoreticalCombos(handClass: string): string[] {
  const ranks = handClass.slice(0, 2).split('');
  const suffix = handClass.length === 3 ? handClass[2] : '';
  const suits = ['s', 'h', 'd', 'c'];

  if (!suffix) {
    const combos: string[] = [];
    for (let i = 0; i < suits.length; i += 1) {
      for (let j = i + 1; j < suits.length; j += 1) {
        combos.push(`${ranks[0]}${suits[i]}${ranks[1]}${suits[j]}`);
      }
    }
    return combos;
  }

  if (suffix === 's') {
    return suits.map((suit) => `${ranks[0]}${suit}${ranks[1]}${suit}`);
  }

  const combos: string[] = [];
  for (const suitA of suits) {
    for (const suitB of suits) {
      if (suitA === suitB) continue;
      combos.push(`${ranks[0]}${suitA}${ranks[1]}${suitB}`);
    }
  }
  return combos;
}

function getComboCountTotal(handClass: string): number {
  if (handClass.length === 2) return 6;
  if (handClass.endsWith('s')) return 4;
  return 12;
}

function comboUsesBlockedCard(combo: string, blockedCards: Set<string>): boolean {
  if (combo.length !== 4) {
    return false;
  }

  const firstCard = combo.slice(0, 2);
  const secondCard = combo.slice(2, 4);

  return blockedCards.has(firstCard) || blockedCards.has(secondCard);
}

function formatActionLabel(actionType: string, sizePercentage: number | null): string {
  const normalized = (actionType || '').toLowerCase();

  switch (normalized) {
    case 'check':
      return 'Check';
    case 'call':
      return 'Call';
    case 'fold':
      return 'Fold';
    case 'bet':
      return sizePercentage !== null && sizePercentage !== undefined
        ? `Bet ${sizePercentage}`
        : 'Bet';
    case 'raise':
      return sizePercentage !== null && sizePercentage !== undefined
        ? `Raise ${sizePercentage}`
        : 'Raise';
    default:
      return actionType;
  }
}

function getAggressiveActionColor(sizePercentage: number | null | undefined): string {
  if (sizePercentage === null || sizePercentage === undefined) {
    return 'rgba(239, 83, 80, 0.82)';
  }

  if (sizePercentage <= 25) return 'rgba(255, 171, 145, 0.78)';
  if (sizePercentage <= 33) return 'rgba(255, 138, 128, 0.82)';
  if (sizePercentage <= 50) return 'rgba(239, 83, 80, 0.86)';
  if (sizePercentage <= 75) return 'rgba(229, 57, 53, 0.9)';
  if (sizePercentage <= 100) return 'rgba(198, 40, 40, 0.94)';
  return 'rgba(183, 28, 28, 0.98)';
}

function getActionColorPreview(
  actionType: string | null | undefined,
  sizePercentage?: number | null,
): string {
  switch ((actionType || '').toLowerCase()) {
    case 'fold':
      return 'rgba(33, 150, 243, 0.66)';
    case 'check':
    case 'call':
      return 'rgba(46, 204, 113, 0.66)';
    case 'bet':
    case 'raise':
      return getAggressiveActionColor(sizePercentage);
    default:
      return 'rgba(255, 255, 255, 0.08)';
  }
}

function computeActionDistribution(
  handClass: string,
  baseActionId: string | null,
  comboOverridesForHandClass: Record<string, string | null>,
): Record<string, number> {
  const combos = buildTheoreticalCombos(handClass);
  const counts: Record<string, number> = {};

  for (const combo of combos) {
    const resolvedActionId =
      Object.prototype.hasOwnProperty.call(comboOverridesForHandClass, combo)
        ? comboOverridesForHandClass[combo]
        : baseActionId;

    if (!resolvedActionId) continue;

    counts[resolvedActionId] = (counts[resolvedActionId] || 0) + 1;
  }

  const totalAssigned = Object.values(counts).reduce((acc, value) => acc + value, 0);
  if (totalAssigned === 0) return {};

  const distribution: Record<string, number> = {};
  for (const [actionId, count] of Object.entries(counts)) {
    distribution[actionId] = Number(((count / combos.length) * 100).toFixed(2));
  }

  return distribution;
}

function normalizeComboOverridesForHandClass(
  baseActionId: string | null,
  comboOverridesForHandClass: Record<string, string | null>,
): Record<string, string> {
  return Object.fromEntries(
    Object.entries(comboOverridesForHandClass).filter(([, value]) => {
      if (!value) return false;
      if (baseActionId && value === baseActionId) return false;
      return true;
    }),
  ) as Record<string, string>;
}

function buildLocalRangeCell(
  handClass: string,
  baseActionId: string | null,
  comboOverridesForHandClass: Record<string, string | null>,
): LocalRangeCell {
  const normalizedOverrides = normalizeComboOverridesForHandClass(
    baseActionId,
    comboOverridesForHandClass,
  );

  return {
    hand_class: handClass,
    combo_count_total: getComboCountTotal(handClass),
    base_action_id: baseActionId,
    action_distribution: computeActionDistribution(handClass, baseActionId, normalizedOverrides),
    has_combo_overrides: Object.keys(normalizedOverrides).length > 0,
  };
}

function mapApiNodeRangeStateToLocal(apiState: PokerStudyNodeRangeState): LocalRangeState {
  const rawCells: Record<string, LocalRangeCell> = {};
  const rawComboOverrides: Record<string, Record<string, string | null>> = {};

  for (const cell of apiState.cells || []) {
    rawCells[cell.hand_class] = {
      hand_class: cell.hand_class,
      combo_count_total: cell.combo_count_total,
      base_action_id: cell.base_action_id,
      action_distribution: cell.action_distribution || {},
      has_combo_overrides: cell.has_combo_overrides,
    };
  }

  for (const item of apiState.combo_overrides || []) {
    if (!rawComboOverrides[item.hand_class]) {
      rawComboOverrides[item.hand_class] = {};
    }
    rawComboOverrides[item.hand_class][item.combo_key] = item.action_id;
  }

  const cells: Record<string, LocalRangeCell> = {};
  const comboOverrides: Record<string, Record<string, string | null>> = {};

  for (const [handClass, cell] of Object.entries(rawCells)) {
    const normalizedOverrides = normalizeComboOverridesForHandClass(
      cell.base_action_id,
      rawComboOverrides[handClass] || {},
    );

    comboOverrides[handClass] = normalizedOverrides;
    cells[handClass] = buildLocalRangeCell(handClass, cell.base_action_id, normalizedOverrides);
  }

  return {
    cells,
    combo_overrides: comboOverrides,
  };
}

function mapLocalNodeRangeStateToApi(localState: LocalRangeState): PokerStudyNodeRangeState {
  const cells = Object.values(localState.cells).map((cell) => {
    const normalizedOverrides = normalizeComboOverridesForHandClass(
      cell.base_action_id,
      localState.combo_overrides[cell.hand_class] || {},
    );

    const normalizedCell = buildLocalRangeCell(
      cell.hand_class,
      cell.base_action_id,
      normalizedOverrides,
    );

    return {
      hand_class: normalizedCell.hand_class,
      combo_count_total: normalizedCell.combo_count_total,
      base_action_id: normalizedCell.base_action_id,
      action_distribution: normalizedCell.action_distribution,
      has_combo_overrides: normalizedCell.has_combo_overrides,
    };
  });

  const combo_overrides = Object.entries(localState.combo_overrides).flatMap(
    ([handClass, combos]) => {
      const baseActionId = localState.cells[handClass]?.base_action_id ?? null;
      const normalizedOverrides = normalizeComboOverridesForHandClass(baseActionId, combos);

      return Object.entries(normalizedOverrides).map(([comboKey, actionId]) => ({
        hand_class: handClass,
        combo_key: comboKey,
        action_id: actionId,
      }));
    },
  );

  return {
    cells,
    combo_overrides,
  };
}

function buildCellActionSummaryFromRangeState(
  handClass: string,
  rangeState: LocalRangeState,
  blockedCards?: Set<string>,
): Record<string, number> {
  const cell = rangeState.cells[handClass];
  if (!cell) return {};

  const combos = buildTheoreticalCombos(handClass);
  const comboOverridesForHandClass = rangeState.combo_overrides[handClass] || {};
  const counts: Record<string, number> = {};

  for (const combo of combos) {
    if (blockedCards && comboUsesBlockedCard(combo, blockedCards)) {
      continue;
    }

    const resolvedActionId =
      Object.prototype.hasOwnProperty.call(comboOverridesForHandClass, combo)
        ? comboOverridesForHandClass[combo]
        : cell.base_action_id;

    if (!resolvedActionId) continue;

    counts[resolvedActionId] = (counts[resolvedActionId] || 0) + 1;
  }

  return counts;
}

function getActionAssignedHandClassCount(
  rangeState: LocalRangeState,
  actionId: string,
): number {
  return Object.keys(rangeState.cells).filter((handClass) => {
    const summary = buildCellActionSummaryFromRangeState(handClass, rangeState);
    return (summary[actionId] || 0) > 0;
  }).length;
}

function hasActionAssignedRange(rangeState: LocalRangeState, actionId: string): boolean {
  return getActionAssignedHandClassCount(rangeState, actionId) > 0;
}

function actionIdentityMatchesNodeAction(
  action: PokerStudyNodeAction,
  node: PokerStudyBoardWorkspace['nodes'][number],
): boolean {
  const sameType = action.action_type === node.action_taken_type;
  const sameSize =
    action.size_percentage === node.action_taken_size ||
    Number(action.size_percentage) === Number(node.action_taken_size);

  return sameType && sameSize;
}

function getActionIdTakenFromParentNode(
  parentNode: PokerStudyBoardWorkspace['nodes'][number],
  childNode: PokerStudyBoardWorkspace['nodes'][number],
): string | null {
  const matchingAction = parentNode.actions.find((action) =>
    actionIdentityMatchesNodeAction(action, childNode),
  );

  return matchingAction?.id ?? null;
}

function getHandClassesAssignedToAction(
  rangeState: LocalRangeState,
  actionId: string,
): Set<string> {
  const assigned = new Set<string>();

  Object.keys(rangeState.cells).forEach((handClass) => {
    const summary = buildCellActionSummaryFromRangeState(handClass, rangeState);

    if ((summary[actionId] || 0) > 0) {
      assigned.add(handClass);
    }
  });

  return assigned;
}

function getPaintedComboKeysFromRangeState(
  rangeState: LocalRangeState,
  blockedCards?: Set<string>,
  actionIdFilter?: string | null,
): string[] {
  const paintedComboKeys = new Set<string>();

  Object.values(rangeState.cells).forEach((cell) => {
    const combos = buildTheoreticalCombos(cell.hand_class);
    const comboOverridesForHandClass = rangeState.combo_overrides[cell.hand_class] || {};

    combos.forEach((combo) => {
      if (blockedCards && comboUsesBlockedCard(combo, blockedCards)) {
        return;
      }

      const resolvedActionId =
        Object.prototype.hasOwnProperty.call(comboOverridesForHandClass, combo)
          ? comboOverridesForHandClass[combo]
          : cell.base_action_id;

      if (!resolvedActionId) {
        return;
      }

      if (actionIdFilter && resolvedActionId !== actionIdFilter) {
        return;
      }

      paintedComboKeys.add(combo);
    });
  });

  return Array.from(paintedComboKeys);
}

function getResolvedActionIdForComboFromRangeState(
  rangeState: LocalRangeState,
  handClass: string,
  comboKey: string,
): string | null {
  const cell = rangeState.cells[handClass];

  if (!cell) {
    return null;
  }

  const comboOverridesForHandClass = rangeState.combo_overrides[handClass] || {};

  return Object.prototype.hasOwnProperty.call(comboOverridesForHandClass, comboKey)
    ? comboOverridesForHandClass[comboKey]
    : cell.base_action_id;
}

function formatBoardImpactPercentage(value: number): string {
  if (!Number.isFinite(value) || value <= 0) {
    return '0%';
  }

  return `${value.toFixed(1)}%`;
}

function intersectHandClassSets(
  left: Set<string>,
  right: Set<string>,
): Set<string> {
  const result = new Set<string>();

  left.forEach((handClass) => {
    if (right.has(handClass)) {
      result.add(handClass);
    }
  });

  return result;
}

function buildCellMixLabel(handClass: string, rangeState: LocalRangeState): string | null {
  const counts = buildCellActionSummaryFromRangeState(handClass, rangeState);
  const entries = Object.entries(counts);

  if (entries.length <= 1) return null;

  const total = entries.reduce((acc, [, count]) => acc + count, 0);
  if (!total) return null;

  const primaryPercentage = Math.round((entries[0][1] / total) * 100);
  return `${primaryPercentage}/${100 - primaryPercentage}`;
}

function resolveCellBackgroundStyle(
  handClass: string,
  nodeActions: PokerStudyNodeAction[],
  rangeState: LocalRangeState,
  isSelected: boolean,
): { backgroundColor?: string; backgroundImage?: string } {
  const counts = buildCellActionSummaryFromRangeState(handClass, rangeState);
  const entries = Object.entries(counts);

  if (entries.length === 0) {
    return {
      backgroundColor: isSelected ? 'action.selected' : 'background.paper',
    };
  }

  if (entries.length === 1) {
    const actionId = entries[0][0];
    const action = nodeActions.find((item) => item.id === actionId);

    return {
      backgroundColor: getActionColorPreview(action?.action_type, action?.size_percentage),
    };
  }

  const total = entries.reduce((acc, [, count]) => acc + count, 0);
  if (!total) {
    return {
      backgroundColor: isSelected ? 'action.selected' : 'background.paper',
    };
  }

  let currentStart = 0;
  const gradientStops = entries.map(([actionId, count]) => {
    const action = nodeActions.find((item) => item.id === actionId);
    const color = getActionColorPreview(action?.action_type, action?.size_percentage);
    const percentage = (count / total) * 100;
    const start = currentStart;
    const end = currentStart + percentage;
    currentStart = end;

    return `${color} ${start.toFixed(2)}%, ${color} ${end.toFixed(2)}%`;
  });

  return {
    backgroundImage: `linear-gradient(90deg, ${gradientStops.join(', ')})`,
    backgroundColor: isSelected ? 'action.selected' : 'background.paper',
  };
}

function buildNodeLabel(node: PokerStudyBoardWorkspace['nodes'][number]): string {
  if (node.is_root) {
    return `Inicio ${getStreetLabel(node.street).toLowerCase()}`;
  }

  if (node.action_taken_label) {
    return node.action_taken_label;
  }

  return `${getStreetLabel(node.street)} · ${node.acting_player}`;
}

function buildPathToNode(
  nodes: PokerStudyBoardWorkspace['nodes'],
  nodeId: string | null,
): PokerStudyBoardWorkspace['nodes'] {
  if (!nodeId) return [];

  const byId = new Map(nodes.map((node) => [node.id, node]));
  const path: PokerStudyBoardWorkspace['nodes'] = [];
  let current = byId.get(nodeId) ?? null;

  while (current) {
    path.unshift(current);
    current = current.parent_node_id ? byId.get(current.parent_node_id) ?? null : null;
  }

  return path;
}

function buildStoryActionLabel(
  workspace: PokerStudyBoardWorkspace,
  node: PokerStudyBoardWorkspace['nodes'][number],
): string {
  if (node.is_root) {
    return buildNodeLabel(node);
  }

  const parentNode = workspace.nodes.find((item) => item.id === node.parent_node_id);
  const actorLabel = parentNode
    ? getActorLabel(workspace, parentNode.acting_player)
    : '';

  const actionLabel = node.action_taken_label || buildNodeLabel(node);

  return actorLabel ? `${actorLabel} ${actionLabel}` : actionLabel;
}

function buildActivePathActionSummary(
  workspace: PokerStudyBoardWorkspace,
  activePath: PokerStudyBoardWorkspace['nodes'],
): string {
  const actionLabels = activePath
    .filter((node) => !node.is_root)
    .map((node) => buildStoryActionLabel(workspace, node));

  if (actionLabels.length === 0) {
    return 'Inicio de la secuencia';
  }

  return `Tras ${actionLabels.join(' → ')}`;
}

function getEffectiveStreetCardFromPath(
  activePath: PokerStudyBoardWorkspace['nodes'],
  street: 'turn' | 'river',
): string | null {
  const cardField = street === 'turn' ? 'turn_card' : 'river_card';

  for (const node of activePath) {
    const card = node[cardField];

    if (card) {
      return card;
    }
  }

  return null;
}

function getStreetEntryNodeFromPath(
  activePath: PokerStudyBoardWorkspace['nodes'],
  street: 'turn' | 'river',
): PokerStudyBoardWorkspace['nodes'][number] | null {
  return activePath.find((node) => node.street === street) ?? null;
}

function getRunoutCardFromNode(
  node: PokerStudyBoardWorkspace['nodes'][number],
  street: 'turn' | 'river',
): string | null {
  return street === 'turn' ? node.turn_card ?? null : node.river_card ?? null;
}

function runoutVariantMatchesEntryNode(
  entryNode: PokerStudyBoardWorkspace['nodes'][number],
  candidateNode: PokerStudyBoardWorkspace['nodes'][number],
): boolean {
  const sameParent = candidateNode.parent_node_id === entryNode.parent_node_id;
  const sameStreet = candidateNode.street === entryNode.street;
  const sameActionType = candidateNode.action_taken_type === entryNode.action_taken_type;

  const sameActionSize =
    candidateNode.action_taken_size === entryNode.action_taken_size ||
    Number(candidateNode.action_taken_size) === Number(entryNode.action_taken_size);

  return sameParent && sameStreet && sameActionType && sameActionSize;
}

function getRunoutVariantNodes(
  workspace: PokerStudyBoardWorkspace,
  entryNode: PokerStudyBoardWorkspace['nodes'][number] | null,
  street: 'turn' | 'river',
): PokerStudyBoardWorkspace['nodes'] {
  if (!entryNode || !entryNode.parent_node_id) {
    return [];
  }

  return workspace.nodes
    .filter((node) => {
      if (!runoutVariantMatchesEntryNode(entryNode, node)) {
        return false;
      }

      if (!isChildNodeConsistentWithParent(workspace, node)) {
        return false;
      }

      return Boolean(getRunoutCardFromNode(node, street));
    })
    .sort((a, b) => {
      const cardA = getRunoutCardFromNode(a, street) ?? '';
      const cardB = getRunoutCardFromNode(b, street) ?? '';
      return cardA.localeCompare(cardB);
    });
}

function actionMatchesChildNode(
  action: PokerStudyNodeAction,
  childNode: PokerStudyBoardWorkspace['nodes'][number],
): boolean {
  const sameType = childNode.action_taken_type === action.action_type;
  const sameSize =
    childNode.action_taken_size === action.size_percentage ||
    Number(childNode.action_taken_size) === Number(action.size_percentage);

  return sameType && sameSize;
}

function isChildNodeConsistentWithParent(
  workspace: PokerStudyBoardWorkspace,
  childNode: PokerStudyBoardWorkspace['nodes'][number],
): boolean {
  if (childNode.is_root || !childNode.parent_node_id) {
    return true;
  }

  const parentNode = workspace.nodes.find((node) => node.id === childNode.parent_node_id);

  if (!parentNode || !childNode.action_taken_type) {
    return false;
  }

  const expectedPayload = buildNextNodeFromActionPayload(
    workspace,
    parentNode,
    {
      action_type: childNode.action_taken_type,
      label: childNode.action_taken_label || '',
      size_percentage: childNode.action_taken_size ?? null,
    } as PokerStudyNodeAction,
    childNode.display_order,
  );

  return (
    childNode.street === expectedPayload.street &&
    childNode.acting_player === expectedPayload.acting_player &&
    childNode.node_kind === expectedPayload.node_kind
  );
}

function resolveRememberedDeepestNodeId(
  workspace: PokerStudyBoardWorkspace,
  startNodeId: string,
  selectedChildByNodeId: Record<string, string>,
): string {
  const nodeIds = new Set(workspace.nodes.map((node) => node.id));
  let currentNodeId = startNodeId;
  const visited = new Set<string>();

  while (true) {
    if (visited.has(currentNodeId)) {
      return currentNodeId;
    }

    visited.add(currentNodeId);

    const rememberedChildId = selectedChildByNodeId[currentNodeId];
    if (!rememberedChildId || !nodeIds.has(rememberedChildId)) {
      return currentNodeId;
    }

    const rememberedChild = workspace.nodes.find(
      (node) => node.id === rememberedChildId && node.parent_node_id === currentNodeId,
    );

    if (!rememberedChild || !isChildNodeConsistentWithParent(workspace, rememberedChild)) {
      return currentNodeId;
    }

    currentNodeId = rememberedChild.id;
  }
}

function getQuickActionPresets(activeNode: PokerStudyBoardWorkspace['nodes'][number]): QuickActionPreset[] {
  if (activeNode.node_kind === 'terminal') {
    return [];
  }

  const facingAggression =
    activeNode.action_taken_type === 'bet' || activeNode.action_taken_type === 'raise';

  if (facingAggression) {
    return [
      { action_type: 'fold', label: 'Fold', size_percentage: null },
      { action_type: 'call', label: 'Call', size_percentage: null },
      { action_type: 'raise', label: 'Raise 50', size_percentage: 50 },
      { action_type: 'raise', label: 'Raise 75', size_percentage: 75 },
    ];
  }

  return [
    { action_type: 'check', label: 'Check', size_percentage: null },
    { action_type: 'bet', label: 'Bet 25', size_percentage: 25 },
    { action_type: 'bet', label: 'Bet 33', size_percentage: 33 },
    { action_type: 'bet', label: 'Bet 50', size_percentage: 50 },
    { action_type: 'bet', label: 'Bet 75', size_percentage: 75 },
    { action_type: 'bet', label: 'Bet 125', size_percentage: 125 },
  ];
}

function findMatchingAction(
  actions: PokerStudyNodeAction[],
  preset: QuickActionPreset,
): PokerStudyNodeAction | null {
  return (
    actions.find((action) => {
      const sameType = action.action_type === preset.action_type;
      const sameSize =
        action.size_percentage === preset.size_percentage ||
        Number(action.size_percentage) === Number(preset.size_percentage);

      return sameType && sameSize;
    }) ?? null
  );
}

function getNextActingPlayer(currentPlayer: string): string {
  return currentPlayer === 'hero' ? 'villain' : 'hero';
}

function getNextStreet(street: string): string | null {
  switch ((street || '').toLowerCase()) {
    case 'flop':
      return 'turn';
    case 'turn':
      return 'river';
    case 'river':
      return null;
    default:
      return null;
  }
}

function getPostflopFirstActor(workspace: PokerStudyBoardWorkspace): string {
  const heroIpStatus = (workspace.spot.hero_ip_status || '').trim().toLowerCase();

  return heroIpStatus === 'ip' ? 'villain' : 'hero';
}

function shouldCloseStreetAfterAction(
  sourceNode: PokerStudyBoardWorkspace['nodes'][number],
  action: PokerStudyNodeAction,
): boolean {
  const actionType = (action.action_type || '').toLowerCase();
  const previousActionType = (sourceNode.action_taken_type || '').toLowerCase();

  if (actionType === 'call') {
    return true;
  }

  if (actionType === 'check' && previousActionType === 'check') {
    return true;
  }

  return false;
}

function buildNextNodeFromActionPayload(
  workspace: PokerStudyBoardWorkspace,
  sourceNode: PokerStudyBoardWorkspace['nodes'][number],
  action: PokerStudyNodeAction,
  displayOrder: number,
) {
  const actionType = (action.action_type || '').toLowerCase();
  const closesStreet = shouldCloseStreetAfterAction(sourceNode, action);
  const nextStreet = closesStreet ? getNextStreet(sourceNode.street) : sourceNode.street;

  if (actionType === 'fold') {
    return {
      street: sourceNode.street,
      acting_player: getNextActingPlayer(sourceNode.acting_player),
      node_kind: 'terminal',
      action_taken_type: action.action_type,
      action_taken_size: action.size_percentage,
      action_taken_label: formatActionLabel(action.action_type, action.size_percentage),
      display_order: displayOrder,
    };
  }

  if (closesStreet) {
    if (!nextStreet) {
      return {
        street: sourceNode.street,
        acting_player: getNextActingPlayer(sourceNode.acting_player),
        node_kind: 'terminal',
        action_taken_type: action.action_type,
        action_taken_size: action.size_percentage,
        action_taken_label: formatActionLabel(action.action_type, action.size_percentage),
        display_order: displayOrder,
      };
    }

    return {
      street: nextStreet,
      acting_player: getPostflopFirstActor(workspace),
      node_kind: 'street_start',
      action_taken_type: action.action_type,
      action_taken_size: action.size_percentage,
      action_taken_label: formatActionLabel(action.action_type, action.size_percentage),
      display_order: displayOrder,
    };
  }

  return {
    street: sourceNode.street,
    acting_player: getNextActingPlayer(sourceNode.acting_player),
    node_kind: 'decision',
    action_taken_type: action.action_type,
    action_taken_size: action.size_percentage,
    action_taken_label: formatActionLabel(action.action_type, action.size_percentage),
    display_order: displayOrder,
  };
}

function parseCardToken(value: string | null | undefined): { rank: string; suit: string } | null {
  const token = (value || '').trim();
  if (token.length !== 2) return null;

  return {
    rank: token[0].toUpperCase(),
    suit: token[1].toLowerCase(),
  };
}

function buildFullDeck(): string[] {
  return MATRIX_RANKS.flatMap((rank) => CARD_SUITS.map((suit) => `${rank}${suit}`));
}

function getBlockedCardsForNode(
  board: PokerStudyBoard,
  node: PokerStudyBoardWorkspace['nodes'][number],
): Set<string> {
  const blocked = new Set<string>();

  if (board.flop_card_1) blocked.add(board.flop_card_1);
  if (board.flop_card_2) blocked.add(board.flop_card_2);
  if (board.flop_card_3) blocked.add(board.flop_card_3);
  if (node.turn_card) blocked.add(node.turn_card);
  if (node.river_card) blocked.add(node.river_card);

  return blocked;
}

function getBlockedCardsForActiveLine(
  board: PokerStudyBoard,
  activeNode: PokerStudyBoardWorkspace['nodes'][number],
  activePath: PokerStudyBoardWorkspace['nodes'],
): Set<string> {
  const blocked = getBlockedCardsForNode(board, activeNode);

  for (const node of activePath) {
    if (node.turn_card) blocked.add(node.turn_card);
    if (node.river_card) blocked.add(node.river_card);
  }

  return blocked;
}

function PokerCard({ value }: { value: string | null | undefined }) {
  const parsed = parseCardToken(value);

  if (!parsed) {
    return (
      <Box
        sx={{
          width: 38,
          height: 48,
          borderRadius: 1.5,
          border: '1px dashed',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontWeight: 800,
        }}
      >
        ?
      </Box>
    );
  }

  const suit = SUIT_META[parsed.suit];

  return (
    <Box
      aria-label={`${parsed.rank} de ${suit?.label || parsed.suit}`}
      sx={{
        width: 42,
        minWidth: 42,
        maxWidth: 42,
        height: 52,
        minHeight: 52,
        maxHeight: 52,
        borderRadius: 1.75,
        bgcolor: suit?.color || 'background.paper',
        border: '1px solid',
        borderColor: 'rgba(255,255,255,0.28)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 1,
        boxSizing: 'border-box',
        flex: '0 0 auto',
        boxShadow: '0 2px 8px rgba(0,0,0,0.28)',
      }}
    >
      <Typography
        variant="h6"
        fontWeight={950}
        sx={{ color: '#ffffff', lineHeight: 0.95 }}
      >
        {parsed.rank}
      </Typography>
      <Typography
        variant="body2"
        fontWeight={950}
        sx={{ color: '#ffffff', lineHeight: 0.95 }}
      >
        {suit?.symbol || parsed.suit}
      </Typography>
    </Box>
  );
}

function MiniPokerCard({ value }: { value: string }) {
  const parsed = parseCardToken(value);
  const suit = parsed ? SUIT_META[parsed.suit] : null;

  return (
    <Box
      sx={{
        width: 33,
        minWidth: 33,
        height: 44,
        borderRadius: 1.15,
        bgcolor: suit?.color || 'background.paper',
        border: '1px solid',
        borderColor: 'rgba(255,255,255,0.32)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        lineHeight: 1,
        boxSizing: 'border-box',
        boxShadow: '0 1px 5px rgba(0,0,0,0.26)',
      }}
    >
      <Typography
        component="span"
        fontWeight={950}
        sx={{ color: '#ffffff', fontSize: 15.5, lineHeight: 0.95 }}
      >
        {parsed?.rank || '?'}
      </Typography>
      <Typography
        component="span"
        fontWeight={950}
        sx={{ color: '#ffffff', fontSize: 12.5, lineHeight: 0.95 }}
      >
        {suit?.symbol || '?'}
      </Typography>
    </Box>
  );
}

function CardPickerGrid({
  blockedCards,
  onSelectCard,
  layout = 'inline',
}: {
  blockedCards: Set<string>;
  onSelectCard: (card: string) => void;
  layout?: 'inline' | 'modal';
}) {
  const isModalLayout = layout === 'modal';
  const deck = isModalLayout
    ? CARD_SUITS.flatMap((suit) => MATRIX_RANKS.map((rank) => `${rank}${suit}`))
    : buildFullDeck();

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: isModalLayout ? 'repeat(13, 32px)' : 'repeat(4, 1fr)',
        gap: isModalLayout ? 0.25 : 0.5,
        justifyContent: isModalLayout ? 'center' : 'stretch',
        alignItems: 'center',
      }}
    >
      {deck.map((card) => {
        const isBlocked = blockedCards.has(card);

        return (
          <Box
            key={card}
            onClick={() => {
              if (!isBlocked) {
                onSelectCard(card);
              }
            }}
            sx={{
              width: isModalLayout ? 32 : 'auto',
              height: isModalLayout ? 40 : 'auto',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              opacity: isBlocked ? 0.28 : 1,
              cursor: isBlocked ? 'not-allowed' : 'pointer',
              pointerEvents: isBlocked ? 'none' : 'auto',
              transform: isModalLayout ? 'scale(0.68)' : 'scale(0.86)',
              transformOrigin: 'center',
            }}
          >
            <PokerCard value={card} />
          </Box>
        );
      })}
    </Box>
  );
}

function BoardDisplay({ board }: { board: PokerStudyBoard }) {
  if (board.board_mode !== 'exact_board') {
    return (
      <Chip
        label={board.board_family_label || 'Board family'}
        size="small"
        sx={{ fontWeight: 800 }}
      />
    );
  }

  return (
    <Stack direction="row" spacing={0.75} alignItems="center">
      <PokerCard value={board.flop_card_1} />
      <PokerCard value={board.flop_card_2} />
      <PokerCard value={board.flop_card_3} />
    </Stack>
  );
}

function getStreetLabel(street: string): string {
  switch ((street || '').toLowerCase()) {
    case 'flop':
      return 'FLOP';
    case 'turn':
      return 'TURN';
    case 'river':
      return 'RIVER';
    default:
      return street.toUpperCase();
  }
}

function formatPositionLabel(value: string | null | undefined): string {
  const normalized = (value || '').trim();

  if (!normalized) {
    return '-';
  }

  return normalized.toUpperCase();
}

function getActorLabel(workspace: PokerStudyBoardWorkspace, actor: string): string {
  if (actor === 'hero') {
    return formatPositionLabel(workspace.spot.hero_position);
  }

  if (actor === 'villain') {
    return formatPositionLabel(workspace.spot.villain_position);
  }

  return actor.toUpperCase();
}

function readStringFromRecord(
  source: Record<string, unknown> | null | undefined,
  key: string,
): string | null {
  const value = source?.[key];

  return typeof value === 'string' && value.trim() ? value.trim().toLowerCase() : null;
}

function getSpotPreflopPositions(workspace: PokerStudyBoardWorkspace) {
  const firstPosition =
    readStringFromRecord(workspace.spot.preflop_config, 'first_position') ||
    workspace.spot.hero_position;

  const secondPosition =
    readStringFromRecord(workspace.spot.preflop_config, 'second_position') ||
    workspace.spot.villain_position;

  return {
    firstPosition: firstPosition.toLowerCase(),
    secondPosition: secondPosition.toLowerCase(),
  };
}

function getRangeKeyForActor(
  workspace: PokerStudyBoardWorkspace,
  actor: string,
): 'open_raise' | 'caller' | null {
  const { firstPosition, secondPosition } = getSpotPreflopPositions(workspace);

  const actorPosition =
    actor === 'hero'
      ? workspace.spot.hero_position.toLowerCase()
      : actor === 'villain'
        ? workspace.spot.villain_position.toLowerCase()
        : null;

  if (!actorPosition) {
    return null;
  }

  if (actorPosition === firstPosition) {
    return 'open_raise';
  }

  if (actorPosition === secondPosition) {
    return 'caller';
  }

  return null;
}

function buildEffectivePreflopRangeByKey(
  spotRanges: PokerStudySpotPreflopRange[],
  boardRanges: PokerStudyBoardPreflopRange[],
): Record<string, Set<string>> {
  const result: Record<string, Set<string>> = {};

  for (const spotRange of spotRanges) {
    result[spotRange.range_key] = new Set(spotRange.range_state?.hand_classes || []);
  }

  for (const boardRange of boardRanges) {
    result[boardRange.range_key] = new Set(boardRange.range_state?.hand_classes || []);
  }

  return result;
}

function getCompatibleBoardRangeTemplateFamilies(rangeKey: string): string[] {
  if (rangeKey === 'open_raise') {
    return ['open_raise'];
  }

  if (rangeKey === 'caller') {
    return ['call_vs_open', 'defend_blind'];
  }

  return [];
}

function getBoardRangeTemplateHandClasses(template: PokerStudyPreflopRangeTemplate): string[] {
  return Array.isArray(template.range_state?.hand_classes)
    ? template.range_state.hand_classes
    : [];
}

function getPreflopRangeComboCount(handClasses: Iterable<string>): number {
  return Array.from(handClasses).reduce(
    (acc, handClass) => acc + getComboCountTotal(handClass),
    0,
  );
}

function formatPreflopRangePercentage(comboCount: number): string {
  if (!comboCount) {
    return '0%';
  }

  return `${((comboCount / 1326) * 100).toFixed(1)}%`;
}

function sortHandClassesByMatrixOrder(handClasses: string[]): string[] {
  const order = new Map<string, number>();

  buildMatrix().flat().forEach((handClass, index) => {
    order.set(handClass, index);
  });

  return [...handClasses].sort(
    (left, right) => (order.get(left) ?? 9999) - (order.get(right) ?? 9999),
  );
}

function isPocketPairHandClass(handClass: string): boolean {
  return handClass.length === 2 && handClass[0] === handClass[1];
}

function readNumberFromRecord(
  source: Record<string, unknown> | null | undefined,
  key: string,
): number | null {
  const value = source?.[key];

  if (typeof value === 'number' && Number.isFinite(value)) {
    return value;
  }

  if (typeof value === 'string' && value.trim()) {
    const parsed = Number(value.replace(',', '.'));
    return Number.isFinite(parsed) ? parsed : null;
  }

  return null;
}

function formatBbAmount(value: number | null): string {
  if (value === null || value === undefined || !Number.isFinite(value)) {
    return 'No calculado';
  }

  const rounded = Math.round(value * 100) / 100;
  return `${rounded}bb`;
}

function getDeadBlindAmount(positionA: string, positionB: string): number {
  const activePositions = new Set([
    positionA.trim().toLowerCase(),
    positionB.trim().toLowerCase(),
  ]);

  let deadBlinds = 0;

  if (!activePositions.has('sb')) {
    deadBlinds += 0.5;
  }

  if (!activePositions.has('bb')) {
    deadBlinds += 1;
  }

  return deadBlinds;
}

function getEstimatedFlopPotBb(workspace: PokerStudyBoardWorkspace): number | null {
  const spotType = (
    readStringFromRecord(workspace.spot.preflop_config, 'spot_type') ||
    workspace.spot.spot_type ||
    ''
  ).trim().toLowerCase();

  const { firstPosition, secondPosition } = getSpotPreflopPositions(workspace);

  let finalContributionBb: number | null = null;

  if (spotType === 'srp') {
    finalContributionBb = readNumberFromRecord(workspace.spot.preflop_config, 'open_raise_size_bb');
  } else if (spotType === '3bet') {
    finalContributionBb = readNumberFromRecord(workspace.spot.preflop_config, 'three_bet_size_bb');
  } else if (spotType === '4bet') {
    finalContributionBb = readNumberFromRecord(workspace.spot.preflop_config, 'four_bet_size_bb');
  } else if (spotType === 'rol') {
    finalContributionBb = readNumberFromRecord(workspace.spot.preflop_config, 'rol_size_bb');
  }

  if (finalContributionBb === null) {
    return null;
  }

  return finalContributionBb * 2 + getDeadBlindAmount(firstPosition, secondPosition);
}

function buildPreflopRangeSummaries(
  workspace: PokerStudyBoardWorkspace,
  spotRanges: PokerStudySpotPreflopRange[],
  boardRanges: PokerStudyBoardPreflopRange[],
): PreflopRangeSummary[] {
  const { firstPosition, secondPosition } = getSpotPreflopPositions(workspace);

  const spotRangeByKey = Object.fromEntries(
    spotRanges.map((range) => [range.range_key, range]),
  ) as Record<string, PokerStudySpotPreflopRange | undefined>;

  const boardRangeByKey = Object.fromEntries(
    boardRanges.map((range) => [range.range_key, range]),
  ) as Record<string, PokerStudyBoardPreflopRange | undefined>;

  return [
    {
      key: 'open_raise' as const,
      title: 'OR',
      position: firstPosition,
      subtitle: `${formatPositionLabel(firstPosition)} open raise`,
    },
    {
      key: 'caller' as const,
      title: 'Caller',
      position: secondPosition,
      subtitle: `${formatPositionLabel(secondPosition)} call`,
    },
  ].map((slot) => {
    const boardRange = boardRangeByKey[slot.key];
    const spotRange = spotRangeByKey[slot.key];
    const effectiveRange = boardRange || spotRange || null;
    const handClasses = new Set(effectiveRange?.range_state?.hand_classes || []);
    const comboCount = getPreflopRangeComboCount(handClasses);

    return {
      ...slot,
      handClasses,
      comboCount,
      percentageLabel: formatPreflopRangePercentage(comboCount),
      source: boardRange ? 'board' : spotRange ? 'spot' : 'empty',
      sourceLabel: boardRange ? 'Custom board' : spotRange ? 'Heredado spot' : 'Sin rango',
      sourceSpotRangeId: spotRange?.id || null,
    };
  });
}

function getBrushLabel(
  selectedAction: PokerStudyNodeAction | null,
  selectedActionId: string | null,
): string {
  if (selectedAction) {
    return formatActionLabel(selectedAction.action_type, selectedAction.size_percentage);
  }

  if (selectedActionId === 'clear') {
    return 'Clear';
  }

  return 'Ninguno';
}

function stripHtmlForPreview(value: string): string {
  return value
    .replace(/<br\s*\/?>/gi, ' ')
    .replace(/<\/div>/gi, ' ')
    .replace(/<\/p>/gi, ' ')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

function buildNodeNoteTitle(
  workspace: PokerStudyBoardWorkspace,
  node: PokerStudyBoardWorkspace['nodes'][number],
): string {
  if (node.is_root) {
    return `${getStreetLabel(node.street)} · Inicio`;
  }

  return `${getStreetLabel(node.street)} · ${buildStoryActionLabel(workspace, node)}`;
}

function HandTimeline({
  workspace,
  activeNode,
  activePath,
  activeNodeId,
  activeTurnNode,
  activeRiverNode,
  effectiveTurnCard,
  effectiveRiverCard,
  turnVariantNodes,
  riverVariantNodes,
  isWaitingForTurnCard,
  isWaitingForRiverCard,
  estimatedFlopPotBb,
  preflopRangeSummaries,
  onSelectNode,
  onBackToSpot,
  onOpenStreetCardPicker,
  onOpenPreflopRangeEditor,
}: {
  workspace: PokerStudyBoardWorkspace;
  activeNode: PokerStudyBoardWorkspace['nodes'][number];
  activePath: PokerStudyBoardWorkspace['nodes'];
  activeNodeId: string | null;
  activeTurnNode: PokerStudyBoardWorkspace['nodes'][number] | null;
  activeRiverNode: PokerStudyBoardWorkspace['nodes'][number] | null;
  effectiveTurnCard: string | null;
  effectiveRiverCard: string | null;
  turnVariantNodes: PokerStudyBoardWorkspace['nodes'];
  riverVariantNodes: PokerStudyBoardWorkspace['nodes'];
  isWaitingForTurnCard: boolean;
  isWaitingForRiverCard: boolean;
  estimatedFlopPotBb: number | null;
  preflopRangeSummaries: PreflopRangeSummary[];
  onSelectNode: (nodeId: string) => void;
  onBackToSpot: () => void;
  onOpenStreetCardPicker: (street: 'turn' | 'river') => void;
  onOpenPreflopRangeEditor: (rangeKey: PreflopRangeKey) => void;
}) {
  const activeStreet = activeNode.street;
  const displayedTurnNode = activeNode.street === 'turn' ? activeNode : activeTurnNode;
  const displayedRiverNode = activeNode.street === 'river' ? activeNode : activeRiverNode;

  const actionEvents = activePath
    .map((node, index) => {
      if (index === 0) return null;

      const parentNode = activePath[index - 1];

      return {
        id: node.id,
        node,
        parentNode,
        street: parentNode.street,
        label: buildStoryActionLabel(workspace, node),
        actionType: node.action_taken_type,
      };
    })
    .filter(Boolean) as Array<{
    id: string;
    node: PokerStudyBoardWorkspace['nodes'][number];
    parentNode: PokerStudyBoardWorkspace['nodes'][number];
    street: string;
    label: string;
    actionType: string | null | undefined;
  }>;

  const flopActions = actionEvents.filter((event) => event.street === 'flop');
  const turnActions = actionEvents.filter((event) => event.street === 'turn');
  const riverActions = actionEvents.filter((event) => event.street === 'river');

  const [playerInfoDialogPosition, setPlayerInfoDialogPosition] = useState<string | null>(null);

  type PlayerInfoDraft = {
    profile: string;
    vpip: string;
    pfr: string;
    threeBet: string;
    foldToThreeBet: string;
    cbet: string;
    foldToCbet: string;
    notes: string;
    hudImageLabel: string;
  };

  const [playerInfoDraftByPosition, setPlayerInfoDraftByPosition] = useState<
    Record<string, PlayerInfoDraft>
  >({});
  const [playerInfoSaveStatus, setPlayerInfoSaveStatus] = useState<
    'idle' | 'saving' | 'saved' | 'error'
  >('idle');

  function formatPlayerInfoProfileLabel(value: string | null | undefined): string {
    switch ((value || '').trim().toLowerCase()) {
      case 'hero':
        return 'Hero';
      case 'regular':
        return 'Regular';
      case 'recreational':
        return 'Recreacional';
      case 'unknown':
        return 'Desconocido';
      default:
        return 'Sin perfil';
    }
  }

  function readPlayerConfigRecord(key: string): Record<string, unknown> {
    const playerConfig = workspace.spot.player_config;

    if (!playerConfig || typeof playerConfig !== 'object') {
      return {};
    }

    const value = (playerConfig as Record<string, unknown>)[key];

    return value && typeof value === 'object' && !Array.isArray(value)
      ? (value as Record<string, unknown>)
      : {};
  }

  function readPlayerInfoString(
    source: Record<string, unknown>,
    key: string,
  ): string {
    const value = source[key];

    return typeof value === 'string' ? value : '';
  }

  function readInitialPlayerProfileForPosition(position: string): string {
    const normalizedPosition = position.trim().toLowerCase();
    const profiles = readPlayerConfigRecord('profiles');
    const profile = profiles[normalizedPosition];

    return typeof profile === 'string' && profile.trim() ? profile.trim().toLowerCase() : 'unknown';
  }

  function readInitialPlayerNotesForPosition(position: string): Record<string, unknown> {
    const normalizedPosition = position.trim().toLowerCase();
    const playerNotes = readPlayerConfigRecord('player_notes');
    const positionNotes = playerNotes[normalizedPosition];

    return positionNotes && typeof positionNotes === 'object' && !Array.isArray(positionNotes)
      ? (positionNotes as Record<string, unknown>)
      : {};
  }

  function buildEmptyPlayerInfoDraft(position: string): PlayerInfoDraft {
    const positionNotes = readInitialPlayerNotesForPosition(position);
    const stats = positionNotes.stats;

    const statsRecord =
      stats && typeof stats === 'object' && !Array.isArray(stats)
        ? (stats as Record<string, unknown>)
        : {};

    return {
      profile: readInitialPlayerProfileForPosition(position),
      vpip: readPlayerInfoString(statsRecord, 'vpip'),
      pfr: readPlayerInfoString(statsRecord, 'pfr'),
      threeBet: readPlayerInfoString(statsRecord, 'three_bet'),
      foldToThreeBet: readPlayerInfoString(statsRecord, 'fold_to_three_bet'),
      cbet: readPlayerInfoString(statsRecord, 'cbet'),
      foldToCbet: readPlayerInfoString(statsRecord, 'fold_to_cbet'),
      notes: readPlayerInfoString(positionNotes, 'notes'),
      hudImageLabel: readPlayerInfoString(positionNotes, 'hud_image_label'),
    };
  }

  function getPlayerInfoDraft(position: string): PlayerInfoDraft {
    const normalizedPosition = position.trim().toLowerCase();

    return (
      playerInfoDraftByPosition[normalizedPosition] ||
      buildEmptyPlayerInfoDraft(normalizedPosition)
    );
  }

  function updatePlayerInfoDraft(position: string, patch: Partial<PlayerInfoDraft>) {
    const normalizedPosition = position.trim().toLowerCase();

    setPlayerInfoSaveStatus('idle');

    setPlayerInfoDraftByPosition((current) => ({
      ...current,
      [normalizedPosition]: {
        ...(current[normalizedPosition] || buildEmptyPlayerInfoDraft(normalizedPosition)),
        ...patch,
      },
    }));
  }

  function buildPlayerInfoStatsPreview(draft: PlayerInfoDraft): string {
    const parts = [
      draft.vpip.trim() ? `VPIP ${draft.vpip.trim()}` : null,
      draft.pfr.trim() ? `PFR ${draft.pfr.trim()}` : null,
      draft.threeBet.trim() ? `3B ${draft.threeBet.trim()}` : null,
    ].filter(Boolean);

    return parts.length > 0 ? parts.join(' · ') : 'Añadir stats / notas';
  }

  function getPreflopPlayerInfoSummary(position: string) {
    const draft = getPlayerInfoDraft(position);

    return {
      mainLabel: formatPlayerInfoProfileLabel(draft.profile),
      detailLabel: buildPlayerInfoStatsPreview(draft),
    };
  }

  async function persistPlayerInfoDraft(position: string): Promise<boolean> {
    const normalizedPosition = position.trim().toLowerCase();
    const draft = getPlayerInfoDraft(normalizedPosition);
    const playerConfig =
      workspace.spot.player_config && typeof workspace.spot.player_config === 'object'
        ? workspace.spot.player_config
        : {};

    const profiles = readPlayerConfigRecord('profiles');
    const playerNotes = readPlayerConfigRecord('player_notes');
    const existingPositionNotes = playerNotes[normalizedPosition];

    const existingPositionNotesRecord =
      existingPositionNotes &&
      typeof existingPositionNotes === 'object' &&
      !Array.isArray(existingPositionNotes)
        ? (existingPositionNotes as Record<string, unknown>)
        : {};

    const existingStats = existingPositionNotesRecord.stats;
    const existingStatsRecord =
      existingStats && typeof existingStats === 'object' && !Array.isArray(existingStats)
        ? (existingStats as Record<string, unknown>)
        : {};

    const nextPlayerConfig = {
      ...playerConfig,
      profiles: {
        ...profiles,
        [normalizedPosition]: draft.profile.trim().toLowerCase() || 'unknown',
      },
      player_notes: {
        ...playerNotes,
        [normalizedPosition]: {
          ...existingPositionNotesRecord,
          notes: draft.notes.trim(),
          hud_image_label: draft.hudImageLabel.trim(),
          stats: {
            ...existingStatsRecord,
            vpip: draft.vpip.trim(),
            pfr: draft.pfr.trim(),
            three_bet: draft.threeBet.trim(),
            fold_to_three_bet: draft.foldToThreeBet.trim(),
            cbet: draft.cbet.trim(),
            fold_to_cbet: draft.foldToCbet.trim(),
          },
        },
      },
    };

    setPlayerInfoSaveStatus('saving');

    try {
      await updatePokerStudySpot(workspace.spot.id, {
        title: workspace.spot.title,
        game_type: workspace.spot.game_type,
        spot_type: workspace.spot.spot_type,
        hero_position: workspace.spot.hero_position,
        villain_position: workspace.spot.villain_position,
        initiative_role: workspace.spot.initiative_role,
        villain_profile: workspace.spot.villain_profile,
        effective_stack_bb: workspace.spot.effective_stack_bb,
        preflop_config: workspace.spot.preflop_config,
        player_config: nextPlayerConfig,
        general_tags: workspace.spot.general_tags,
        is_favorite: workspace.spot.is_favorite,
      });

      setPlayerInfoSaveStatus('saved');
      return true;
    } catch (error) {
      console.error(error);
      setPlayerInfoSaveStatus('error');
      return false;
    }
  }

  async function closePlayerInfoDialog() {
    if (!playerInfoDialogPosition || playerInfoSaveStatus === 'saving') {
      return;
    }

    const saved = await persistPlayerInfoDraft(playerInfoDialogPosition);

    if (saved) {
      setPlayerInfoDialogPosition(null);
    }
  }

  const activePlayerInfoDraft = playerInfoDialogPosition
    ? getPlayerInfoDraft(playerInfoDialogPosition)
    : null;

  function renderActionChips(events: typeof actionEvents) {
    if (events.length === 0) {
      return (
        <Typography variant="caption" color="text.secondary">
          Sin acciones registradas
        </Typography>
      );
    }

    return (
      <Stack direction="row" spacing={0.5} flexWrap="wrap">
        {events.map((event) => (
          <Chip
            key={event.id}
            size="small"
            label={event.label}
            color={event.node.id === activeNodeId ? 'primary' : 'default'}
            variant={event.node.id === activeNodeId ? 'filled' : 'outlined'}
            onClick={() => onSelectNode(event.node.id)}
            sx={{
              height: 22,
              fontWeight: 850,
              backgroundColor:
                event.node.id === activeNodeId
                  ? undefined
                  : getActionColorPreview(event.actionType),
            }}
          />
        ))}
      </Stack>
    );
  }

  function renderStreetDecisionLabel(street: string) {
    if (activeNode.street !== street) {
      return null;
    }

    return (
      <Chip
        size="small"
        color="primary"
        label={`Actúa ${getActorLabel(workspace, activeNode.acting_player)}`}
        sx={{ height: 22, fontWeight: 900 }}
      />
    );
  }

  function renderRunoutVariants(
    street: 'turn' | 'river',
    variants: PokerStudyBoardWorkspace['nodes'],
    effectiveCard: string | null,
  ) {
    if (variants.length === 0 && !effectiveCard) {
      return null;
    }

    return (
      <Stack direction="row" spacing={0.5} alignItems="center" flexWrap="wrap" sx={{ mt: 0.75 }}>
        {variants.map((variantNode) => {
          const card = getRunoutCardFromNode(variantNode, street);
          const isActive = Boolean(card && card === effectiveCard);

          if (!card) {
            return null;
          }

          return (
            <Chip
              key={variantNode.id}
              size="small"
              label={card}
              color={isActive ? 'primary' : 'default'}
              variant={isActive ? 'filled' : 'outlined'}
              onClick={() => {
                void onSelectNode(variantNode.id);
              }}
              sx={{ height: 22, fontWeight: 900 }}
            />
          );
        })}

        <Button
          size="small"
          variant="outlined"
          onClick={() => onOpenStreetCardPicker(street)}
          sx={{
            minHeight: 22,
            height: 22,
            px: 0.8,
            fontSize: 11,
            fontWeight: 900,
          }}
        >
          + carta
        </Button>
      </Stack>
    );
  }

  return (
    <Box
      sx={{
        display: 'grid',
        gridTemplateColumns: {
          xs: '1fr',
          lg: '1.08fr 1.62fr 0.72fr 0.72fr',
        },
        gap: 0.9,
        alignItems: 'stretch',
      }}
    >
      <Card
        variant="outlined"
        sx={{
          flex: 1.18,
          borderRadius: 2,
          bgcolor: activeStreet === 'preflop' ? 'action.selected' : 'background.paper',
        }}
      >
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Stack
            direction="row"
            spacing={1}
            alignItems="stretch"
            sx={{ height: '100%', minWidth: 0 }}
          >
            <Button
              variant="outlined"
              onClick={onBackToSpot}
              sx={{
                minHeight: 46,
                minWidth: 74,
                borderRadius: 2.25,
                px: 1,
                flexShrink: 0,
                color: '#e2e8f0',
                borderColor: 'rgba(148, 163, 184, 0.24)',
                bgcolor: 'rgba(255,255,255,0.035)',
                textTransform: 'none',
                '&:hover': {
                  bgcolor: 'rgba(76, 201, 216, 0.12)',
                  borderColor: 'rgba(76, 201, 216, 0.45)',
                  color: '#ffffff',
                },
              }}
              aria-label="Volver al spot"
            >
              <Stack spacing={0.25} alignItems="center" justifyContent="center">
                <Typography component="span" sx={{ fontSize: 18, fontWeight: 950, lineHeight: 1 }}>
                  ←
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontSize: 9.5,
                    fontWeight: 950,
                    lineHeight: 1,
                    color: '#94a3b8',
                    textTransform: 'uppercase',
                  }}
                >
                  Spot
                </Typography>
              </Stack>
            </Button>

            <Stack spacing={0.65} sx={{ flex: 1, height: '100%', minWidth: 0 }}>
              <Stack
                direction="row"
                justifyContent="space-between"
                alignItems="center"
                spacing={1}
                sx={{ minWidth: 0 }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={950}>
                  PREFLOP
                </Typography>

                <Chip
                  size="small"
                  label={formatBbAmount(estimatedFlopPotBb)}
                  sx={{ height: 20, fontWeight: 900, flexShrink: 0 }}
                />
              </Stack>

              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  display: 'grid',
                  gridTemplateColumns: {
                    xs: '1fr',
                    sm: 'repeat(2, minmax(0, 1fr))',
                  },
                  gap: 0.65,
                  alignItems: 'stretch',
                }}
              >
                {preflopRangeSummaries.map((summary) => {
                  const playerInfo = getPreflopPlayerInfoSummary(summary.position);

                  return (
                    <Box
                      key={summary.key}
                      sx={{
                        minWidth: 0,
                        borderRadius: 1.6,
                        border: '1px solid rgba(76, 201, 216, 0.22)',
                        bgcolor: 'rgba(2, 6, 23, 0.22)',
                        p: 0.55,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.4,
                      }}
                    >
                      <Typography
                        noWrap
                        title={`${summary.title} · ${formatPositionLabel(summary.position)}`}
                        sx={{
                          color: '#e0f2fe',
                          fontSize: 11.5,
                          fontWeight: 950,
                          lineHeight: 1.05,
                        }}
                      >
                        {summary.title} · {formatPositionLabel(summary.position)}
                      </Typography>

                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => onOpenPreflopRangeEditor(summary.key)}
                        sx={{
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          textTransform: 'none',
                          minHeight: 34,
                          px: 0.75,
                          py: 0.4,
                          borderRadius: 1.25,
                          borderColor:
                            summary.source === 'board'
                              ? 'rgba(34, 197, 94, 0.52)'
                              : summary.source === 'spot'
                                ? 'rgba(76, 201, 216, 0.42)'
                                : 'rgba(148, 163, 184, 0.24)',
                          bgcolor:
                            summary.source === 'board'
                              ? 'rgba(34, 197, 94, 0.08)'
                              : summary.source === 'spot'
                                ? 'rgba(76, 201, 216, 0.07)'
                                : 'rgba(255,255,255,0.025)',
                          '&:hover': {
                            bgcolor:
                              summary.source === 'board'
                                ? 'rgba(34, 197, 94, 0.13)'
                                : summary.source === 'spot'
                                  ? 'rgba(76, 201, 216, 0.12)'
                                  : 'rgba(255,255,255,0.055)',
                          },
                        }}
                      >
                        <Box sx={{ width: '100%', minWidth: 0 }}>
                          <Stack
                            direction="row"
                            spacing={0.65}
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ minWidth: 0 }}
                          >
                            <Typography
                              component="span"
                              noWrap
                              sx={{
                                color: '#f8fafc',
                                fontSize: 11.5,
                                fontWeight: 950,
                                lineHeight: 1.05,
                              }}
                            >
                              Rango
                            </Typography>

                            <Typography
                              component="span"
                              noWrap
                              sx={{
                                color: '#94a3b8',
                                fontSize: 10.5,
                                fontWeight: 800,
                                lineHeight: 1.05,
                                flexShrink: 0,
                              }}
                            >
                              {summary.sourceLabel}
                            </Typography>
                          </Stack>

                          <Typography
                            component="span"
                            noWrap
                            sx={{
                              display: 'block',
                              color: '#67e8f9',
                              fontSize: 11,
                              fontWeight: 950,
                              lineHeight: 1.05,
                              mt: 0.3,
                            }}
                          >
                            {summary.comboCount > 0
                              ? `${summary.comboCount} combos · ${summary.percentageLabel}`
                              : 'Editar rango'}
                          </Typography>
                        </Box>
                      </Button>

                      <Button
                        size="small"
                        variant="outlined"
                        onClick={() => {
                          setPlayerInfoSaveStatus('idle');
                          setPlayerInfoDialogPosition(summary.position);
                        }}
                        sx={{
                          justifyContent: 'flex-start',
                          textAlign: 'left',
                          textTransform: 'none',
                          minHeight: 34,
                          px: 0.75,
                          py: 0.4,
                          borderRadius: 1.25,
                          borderColor: 'rgba(148, 163, 184, 0.22)',
                          bgcolor: 'rgba(15, 23, 42, 0.22)',
                          '&:hover': {
                            borderColor: 'rgba(76, 201, 216, 0.38)',
                            bgcolor: 'rgba(76, 201, 216, 0.08)',
                          },
                        }}
                      >
                        <Box sx={{ width: '100%', minWidth: 0 }}>
                          <Stack
                            direction="row"
                            spacing={0.65}
                            justifyContent="space-between"
                            alignItems="center"
                            sx={{ minWidth: 0 }}
                          >
                            <Typography
                              component="span"
                              noWrap
                              sx={{
                                color: '#f8fafc',
                                fontSize: 11.5,
                                fontWeight: 950,
                                lineHeight: 1.05,
                              }}
                            >
                              Info jugador
                            </Typography>

                            <Typography
                              component="span"
                              noWrap
                              sx={{
                                color: '#94a3b8',
                                fontSize: 10.5,
                                fontWeight: 800,
                                lineHeight: 1.05,
                                flexShrink: 0,
                              }}
                            >
                              {playerInfo.mainLabel}
                            </Typography>
                          </Stack>

                          <Typography
                            component="span"
                            noWrap
                            sx={{
                              display: 'block',
                              color: '#cbd5e1',
                              fontSize: 11,
                              fontWeight: 900,
                              lineHeight: 1.05,
                              mt: 0.3,
                            }}
                          >
                            {playerInfo.detailLabel}
                          </Typography>
                        </Box>
                      </Button>
                    </Box>
                  );
                })}
              </Box>
            </Stack>
          </Stack>
        </CardContent>
      </Card>

      <Dialog
        open={Boolean(playerInfoDialogPosition)}
        onClose={() => {
          void closePlayerInfoDialog();
        }}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: '#0f172a',
            border: '1px solid rgba(148, 163, 184, 0.22)',
            boxShadow: '0 28px 80px rgba(0,0,0,0.52)',
          },
        }}
      >
        <DialogTitle sx={{ px: 3, pt: 2.4, pb: 1.2 }}>
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1.25}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
          >
            <Box>
              <Typography
                sx={{
                  color: '#f8fafc',
                  fontSize: 21,
                  fontWeight: 950,
                  lineHeight: 1.05,
                }}
              >
                Info jugador · {formatPositionLabel(playerInfoDialogPosition)}
              </Typography>

              <Typography
                sx={{
                  color: '#94a3b8',
                  fontSize: 13,
                  fontWeight: 750,
                  mt: 0.65,
                }}
              >
                Perfil, números y notas específicas de esta posición en la mano.
              </Typography>
            </Box>

            <Chip
              size="small"
              variant="outlined"
              label={
                playerInfoSaveStatus === 'saving'
                  ? 'Guardando...'
                  : playerInfoSaveStatus === 'saved'
                    ? 'Guardado'
                    : 'Autosave'
              }
              sx={{
                height: 26,
                fontWeight: 950,
                color:
                  playerInfoSaveStatus === 'saved'
                    ? '#86efac'
                    : playerInfoSaveStatus === 'saving'
                      ? '#67e8f9'
                      : '#cbd5e1',
                borderColor:
                  playerInfoSaveStatus === 'saved'
                    ? 'rgba(34, 197, 94, 0.35)'
                    : 'rgba(76, 201, 216, 0.3)',
                bgcolor:
                  playerInfoSaveStatus === 'saved'
                    ? 'rgba(34, 197, 94, 0.08)'
                    : 'rgba(76, 201, 216, 0.07)',
              }}
            />
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ px: 3, pt: 1 }}>
          {playerInfoDialogPosition && activePlayerInfoDraft ? (
            <Stack spacing={1.35}>
              <Box
                sx={{
                  borderRadius: 2.75,
                  border: '1px solid rgba(76, 201, 216, 0.24)',
                  bgcolor: 'linear-gradient(135deg, rgba(76, 201, 216, 0.1), rgba(15, 23, 42, 0.42))',
                  p: 1.35,
                  boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.04)',
                }}
              >
                <Stack
                  direction={{ xs: 'column', sm: 'row' }}
                  spacing={1}
                  justifyContent="space-between"
                  alignItems={{ xs: 'flex-start', sm: 'center' }}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography
                      sx={{
                        color: '#67e8f9',
                        fontSize: 11,
                        fontWeight: 950,
                        letterSpacing: 0.75,
                        textTransform: 'uppercase',
                        lineHeight: 1.1,
                      }}
                    >
                      Resumen visible en preflop
                    </Typography>

                    <Typography
                      sx={{
                        color: '#f8fafc',
                        fontSize: 20,
                        fontWeight: 950,
                        lineHeight: 1.1,
                        mt: 0.45,
                      }}
                    >
                      {formatPositionLabel(playerInfoDialogPosition)} ·{' '}
                      {formatPlayerInfoProfileLabel(activePlayerInfoDraft.profile)}
                    </Typography>

                    <Typography
                      sx={{
                        color: '#cbd5e1',
                        fontSize: 13,
                        fontWeight: 850,
                        mt: 0.35,
                      }}
                    >
                      {buildPlayerInfoStatsPreview(activePlayerInfoDraft)}
                    </Typography>
                  </Box>

                  <Chip
                    label={activePlayerInfoDraft.hudImageLabel ? 'HUD indicado' : 'Sin HUD'}
                    variant="outlined"
                    sx={{
                      height: 28,
                      fontWeight: 950,
                      color: activePlayerInfoDraft.hudImageLabel ? '#86efac' : '#94a3b8',
                      borderColor: activePlayerInfoDraft.hudImageLabel
                        ? 'rgba(34, 197, 94, 0.35)'
                        : 'rgba(148, 163, 184, 0.24)',
                      bgcolor: activePlayerInfoDraft.hudImageLabel
                        ? 'rgba(34, 197, 94, 0.08)'
                        : 'rgba(15, 23, 42, 0.34)',
                    }}
                  />
                </Stack>
              </Box>

              <Box
                sx={{
                  borderRadius: 2.75,
                  border: '1px solid rgba(148, 163, 184, 0.16)',
                  bgcolor: 'rgba(15, 23, 42, 0.34)',
                  p: 1.35,
                }}
              >
                <Typography
                  sx={{
                    color: '#e2e8f0',
                    fontSize: 12,
                    fontWeight: 950,
                    letterSpacing: 0.65,
                    textTransform: 'uppercase',
                    mb: 0.4,
                  }}
                >
                  1 · Perfil del jugador
                </Typography>

                <Typography
                  sx={{
                    color: '#94a3b8',
                    fontSize: 12,
                    fontWeight: 750,
                    mb: 1,
                  }}
                >
                  Define el tipo de jugador para interpretar los números y las notas.
                </Typography>

                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: {
                      xs: '1fr',
                      sm: 'repeat(4, minmax(0, 1fr))',
                    },
                    gap: 0.75,
                  }}
                >
                  {[
                    { value: 'hero', label: 'Hero' },
                    { value: 'regular', label: 'Regular' },
                    { value: 'recreational', label: 'Recreacional' },
                    { value: 'unknown', label: 'Desconocido' },
                  ].map((profileOption) => {
                    const isSelected = activePlayerInfoDraft.profile === profileOption.value;

                    return (
                      <Button
                        key={profileOption.value}
                        variant={isSelected ? 'contained' : 'outlined'}
                        onClick={() =>
                          updatePlayerInfoDraft(playerInfoDialogPosition, {
                            profile: profileOption.value,
                          })
                        }
                        sx={{
                          minHeight: 38,
                          borderRadius: 2,
                          px: 1,
                          fontWeight: 950,
                          textTransform: 'none',
                          color: isSelected ? '#082f49' : '#cbd5e1',
                          bgcolor: isSelected ? '#67e8f9' : 'rgba(2, 6, 23, 0.22)',
                          borderColor: isSelected
                            ? 'rgba(103, 232, 249, 0.9)'
                            : 'rgba(148, 163, 184, 0.22)',
                          boxShadow: isSelected
                            ? '0 10px 24px rgba(76, 201, 216, 0.18)'
                            : 'none',
                          '&:hover': {
                            bgcolor: isSelected ? '#a5f3fc' : 'rgba(76, 201, 216, 0.1)',
                            borderColor: 'rgba(76, 201, 216, 0.55)',
                          },
                        }}
                      >
                        {profileOption.label}
                      </Button>
                    );
                  })}
                </Box>
              </Box>

              <Box
                sx={{
                  borderRadius: 2.75,
                  border: '1px solid rgba(148, 163, 184, 0.16)',
                  bgcolor: 'rgba(15, 23, 42, 0.34)',
                  p: 1.35,
                }}
              >
                <Typography
                  sx={{
                    color: '#e2e8f0',
                    fontSize: 12,
                    fontWeight: 950,
                    letterSpacing: 0.65,
                    textTransform: 'uppercase',
                    mb: 0.4,
                  }}
                >
                  2 · Números principales
                </Typography>

                <Typography
                  sx={{
                    color: '#94a3b8',
                    fontSize: 12,
                    fontWeight: 750,
                    mb: 1,
                  }}
                >
                  Stats manuales rápidas para tener contexto durante el estudio.
                </Typography>

                <Grid container spacing={1}>
                  <Grid item xs={6} sm={4}>
                    <TextField
                      size="small"
                      label="VPIP"
                      value={activePlayerInfoDraft.vpip}
                      onChange={(event) =>
                        updatePlayerInfoDraft(playerInfoDialogPosition, {
                          vpip: event.target.value,
                        })
                      }
                      fullWidth
                      inputProps={{ inputMode: 'decimal' }}
                    />
                  </Grid>

                  <Grid item xs={6} sm={4}>
                    <TextField
                      size="small"
                      label="PFR"
                      value={activePlayerInfoDraft.pfr}
                      onChange={(event) =>
                        updatePlayerInfoDraft(playerInfoDialogPosition, {
                          pfr: event.target.value,
                        })
                      }
                      fullWidth
                      inputProps={{ inputMode: 'decimal' }}
                    />
                  </Grid>

                  <Grid item xs={6} sm={4}>
                    <TextField
                      size="small"
                      label="3Bet"
                      value={activePlayerInfoDraft.threeBet}
                      onChange={(event) =>
                        updatePlayerInfoDraft(playerInfoDialogPosition, {
                          threeBet: event.target.value,
                        })
                      }
                      fullWidth
                      inputProps={{ inputMode: 'decimal' }}
                    />
                  </Grid>

                  <Grid item xs={6} sm={4}>
                    <TextField
                      size="small"
                      label="Fold 3Bet"
                      value={activePlayerInfoDraft.foldToThreeBet}
                      onChange={(event) =>
                        updatePlayerInfoDraft(playerInfoDialogPosition, {
                          foldToThreeBet: event.target.value,
                        })
                      }
                      fullWidth
                      inputProps={{ inputMode: 'decimal' }}
                    />
                  </Grid>

                  <Grid item xs={6} sm={4}>
                    <TextField
                      size="small"
                      label="CBet"
                      value={activePlayerInfoDraft.cbet}
                      onChange={(event) =>
                        updatePlayerInfoDraft(playerInfoDialogPosition, {
                          cbet: event.target.value,
                        })
                      }
                      fullWidth
                      inputProps={{ inputMode: 'decimal' }}
                    />
                  </Grid>

                  <Grid item xs={6} sm={4}>
                    <TextField
                      size="small"
                      label="Fold CBet"
                      value={activePlayerInfoDraft.foldToCbet}
                      onChange={(event) =>
                        updatePlayerInfoDraft(playerInfoDialogPosition, {
                          foldToCbet: event.target.value,
                        })
                      }
                      fullWidth
                      inputProps={{ inputMode: 'decimal' }}
                    />
                  </Grid>
                </Grid>
              </Box>

              <Box
                sx={{
                  borderRadius: 2.75,
                  border: '1px solid rgba(148, 163, 184, 0.16)',
                  bgcolor: 'rgba(15, 23, 42, 0.34)',
                  p: 1.35,
                }}
              >
                <Typography
                  sx={{
                    color: '#e2e8f0',
                    fontSize: 12,
                    fontWeight: 950,
                    letterSpacing: 0.65,
                    textTransform: 'uppercase',
                    mb: 0.4,
                  }}
                >
                  3 · Notas y HUD
                </Typography>

                <Typography
                  sx={{
                    color: '#94a3b8',
                    fontSize: 12,
                    fontWeight: 750,
                    mb: 1,
                  }}
                >
                  Información cualitativa para recordar patrones, leaks o reads.
                </Typography>

                <Stack spacing={1}>
                  <TextField
                    size="small"
                    label="Resumen HUD / imagen"
                    value={activePlayerInfoDraft.hudImageLabel}
                    onChange={(event) =>
                      updatePlayerInfoDraft(playerInfoDialogPosition, {
                        hudImageLabel: event.target.value,
                      })
                    }
                    placeholder="Ej. HUD 2k manos, tendencia agresiva turn..."
                    fullWidth
                  />

                  <TextField
                    size="small"
                    label="Notas del jugador"
                    value={activePlayerInfoDraft.notes}
                    onChange={(event) =>
                      updatePlayerInfoDraft(playerInfoDialogPosition, {
                        notes: event.target.value,
                      })
                    }
                    placeholder="Notas específicas de adaptación, sizings, leaks, reads..."
                    multiline
                    minRows={4}
                    fullWidth
                  />
                </Stack>
              </Box>

              <Alert
                severity={
                  playerInfoSaveStatus === 'error'
                    ? 'error'
                    : playerInfoSaveStatus === 'saved'
                      ? 'success'
                      : 'info'
                }
                sx={{
                  borderRadius: 2.2,
                  bgcolor:
                    playerInfoSaveStatus === 'error'
                      ? 'rgba(127, 29, 29, 0.32)'
                      : 'rgba(2, 6, 23, 0.72)',
                  color: '#cffafe',
                  border: '1px solid rgba(76, 201, 216, 0.16)',
                  '& .MuiAlert-icon': {
                    color:
                      playerInfoSaveStatus === 'error'
                        ? '#fca5a5'
                        : playerInfoSaveStatus === 'saved'
                          ? '#86efac'
                          : '#22d3ee',
                  },
                }}
              >
                {playerInfoSaveStatus === 'saving'
                  ? 'Guardando información del jugador...'
                  : playerInfoSaveStatus === 'error'
                    ? 'No se pudo guardar la información del jugador. Revisa conexión o backend.'
                    : 'Los cambios se guardan automáticamente al cerrar este modal.'}
              </Alert>
            </Stack>
          ) : null}
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2.2 }}>
          <Button
            variant="outlined"
            disabled={playerInfoSaveStatus === 'saving'}
            onClick={() => {
              void closePlayerInfoDialog();
            }}
            sx={{
              borderRadius: 2,
              fontWeight: 900,
              textTransform: 'none',
            }}
          >
            {playerInfoSaveStatus === 'saving' ? 'Guardando...' : 'Cerrar'}
          </Button>
        </DialogActions>
      </Dialog>

      <Card
        variant="outlined"
        sx={{
          borderRadius: 2,
          bgcolor: activeStreet === 'flop' ? 'action.selected' : 'background.paper',
          borderColor: activeStreet === 'flop' ? 'primary.main' : 'divider',
        }}
      >
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={950}>
                FLOP
              </Typography>

              <Box sx={{ mt: 0.55 }}>
                <BoardDisplay board={workspace.board} />
              </Box>
            </Box>

            {renderStreetDecisionLabel('flop')}
          </Stack>

          <Box sx={{ mt: 1 }}>
            {renderActionChips(flopActions)}
          </Box>
        </CardContent>
      </Card>

      <Card
        variant="outlined"
        sx={{
          flex: 0.82,
          borderRadius: 2,
          bgcolor: activeStreet === 'turn' ? 'action.selected' : 'background.paper',
          borderColor: activeStreet === 'turn' ? 'primary.main' : 'divider',
          opacity: displayedTurnNode || activeStreet === 'turn' ? 1 : 0.7,
        }}
      >
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={950}>
                TURN
              </Typography>

              {effectiveTurnCard ? (
                <Box sx={{ mt: 0.55 }}>
                  <PokerCard value={effectiveTurnCard} />
                </Box>
              ) : isWaitingForTurnCard ? (
                <Box sx={{ mt: 0.65 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.55 }}
                  >
                    Selecciona carta para estudiar turn
                  </Typography>

                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => onOpenStreetCardPicker('turn')}
                    sx={{ mt: 0.25, fontWeight: 900 }}
                  >
                    Elegir carta
                  </Button>
                </Box>
              ) : (
                <Typography variant="body2" fontWeight={900} sx={{ mt: 0.35 }}>
                  Pendiente
                </Typography>
              )}
            </Box>

            {renderStreetDecisionLabel('turn')}
          </Stack>

          {effectiveTurnCard ? (
            <>
              {renderRunoutVariants('turn', turnVariantNodes, effectiveTurnCard)}

              <Box sx={{ mt: 1 }}>
                {renderActionChips(turnActions)}
              </Box>
            </>
          ) : null}
        </CardContent>
      </Card>

      <Card
        variant="outlined"
        sx={{
          flex: 0.82,
          borderRadius: 2,
          bgcolor: activeStreet === 'river' ? 'action.selected' : 'background.paper',
          borderColor: activeStreet === 'river' ? 'primary.main' : 'divider',
          opacity: displayedRiverNode || activeStreet === 'river' ? 1 : 0.7,
        }}
      >
        <CardContent sx={{ p: 1, '&:last-child': { pb: 1 } }}>
          <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={950}>
                RIVER
              </Typography>

              {effectiveRiverCard ? (
                <Box sx={{ mt: 0.55 }}>
                  <PokerCard value={effectiveRiverCard} />
                </Box>
              ) : isWaitingForRiverCard ? (
                <Box sx={{ mt: 0.65 }}>
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mb: 0.55 }}
                  >
                    Selecciona carta para estudiar river
                  </Typography>

                  <Button
                    size="small"
                    variant="contained"
                    onClick={() => onOpenStreetCardPicker('river')}
                    sx={{ mt: 0.25, fontWeight: 900 }}
                  >
                    Elegir carta
                  </Button>
                </Box>
              ) : (
                <Typography variant="body2" fontWeight={900} sx={{ mt: 0.35 }}>
                  Pendiente
                </Typography>
              )}
            </Box>

            {renderStreetDecisionLabel('river')}
          </Stack>

          {effectiveRiverCard ? (
            <>
              {renderRunoutVariants('river', riverVariantNodes, effectiveRiverCard)}

              <Box sx={{ mt: 1 }}>
                {renderActionChips(riverActions)}
              </Box>
            </>
          ) : null}
        </CardContent>
      </Card>
    </Box>
  );
}

function getBoardActiveNodeStorageKey(boardId: string): string {
  return `poker-study:board:${boardId}:active-node`;
}

export default function PokerStudyBoardPage() {
  const navigate = useNavigate();
  const { boardId } = useParams<{ boardId: string }>();

  const [workspace, setWorkspace] = useState<PokerStudyBoardWorkspace | null>(null);
  const [activeNodeId, setActiveNodeId] = useState<string | null>(null);

  const [isLoading, setIsLoading] = useState(true);
  const [isLoadingRangeState, setIsLoadingRangeState] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);

  const [isCreatingAction, setIsCreatingAction] = useState(false);
  const [isCreatingChildNode, setIsCreatingChildNode] = useState(false);

  const [nodeNotesContent, setNodeNotesContent] = useState('');
  const [nodeNotesAutosaveStatus, setNodeNotesAutosaveStatus] = useState<
    'idle' | 'saving' | 'saved' | 'error'
  >('idle');
  const [nodeRangeAutosaveStatus, setNodeRangeAutosaveStatus] =
    useState<NodeRangeAutosaveStatus>('idle');
  const [previousNodeNotes, setPreviousNodeNotes] = useState<PokerStudyNodeNotes[]>([]);
  const [expandedPreviousNoteNodeId, setExpandedPreviousNoteNodeId] = useState<string | null>(null);

  const nodeNotesLastSavedValueRef = useRef('');
  const nodeNotesAutosaveTimerRef = useRef<number | null>(null);
  const nodeNotesEditorRef = useRef<HTMLDivElement | null>(null);
  const nodeRangeAutosaveTimerRef = useRef<number | null>(null);
  const skipNextNodeRangeAutosaveRef = useRef(true);

  const [isSpotTheoryDialogOpen, setIsSpotTheoryDialogOpen] = useState(false);
  const [spotTheoryContent, setSpotTheoryContent] = useState('');
  const [isLoadingSpotTheory, setIsLoadingSpotTheory] = useState(false);

  const [customActionType, setCustomActionType] = useState<'bet' | 'raise'>('bet');
  const [customActionSize, setCustomActionSize] = useState<string>('33');
  const [quickActionModalFamily, setQuickActionModalFamily] =
    useState<QuickActionPreset['action_type'] | null>(null);
  const [streetCardPickerOpen, setStreetCardPickerOpen] = useState<'turn' | 'river' | null>(null);

  const [selectedActionId, setSelectedActionId] = useState<string | null>(null);
  const [spotPreflopRanges, setSpotPreflopRanges] = useState<PokerStudySpotPreflopRange[]>([]);
  const [boardPreflopRanges, setBoardPreflopRanges] = useState<PokerStudyBoardPreflopRange[]>([]);
  const [rangeTemplates, setRangeTemplates] = useState<PokerStudyPreflopRangeTemplate[]>([]);
  const [isLoadingRangeTemplates, setIsLoadingRangeTemplates] = useState(false);
  const [editingBoardPreflopRangeKey, setEditingBoardPreflopRangeKey] =
    useState<PreflopRangeKey | null>(null);
  const [boardPreflopRangeDraft, setBoardPreflopRangeDraft] = useState<Set<string>>(new Set());
  const [isBoardPreflopRangePainting, setIsBoardPreflopRangePainting] = useState(false);
  const [boardPreflopRangePaintMode, setBoardPreflopRangePaintMode] =
    useState<'select' | 'clear' | null>(null);
  const [isSavingBoardPreflopRange, setIsSavingBoardPreflopRange] = useState(false);
  const [selectedChildByNodeId, setSelectedChildByNodeId] = useState<Record<string, string>>({});
  const [boardImpactActionFilter, setBoardImpactActionFilter] =
    useState<BoardImpactActionFilter>('all');
  const [hoveredBoardImpactBucketKey, setHoveredBoardImpactBucketKey] =
    useState<BoardImpactCategoryKey | null>(null);
  const [hoveredBoardDrawBucketKey, setHoveredBoardDrawBucketKey] =
    useState<BoardDrawCategoryKey | null>(null);
  const [boardImpactInspection, setBoardImpactInspection] =
    useState<BoardImpactInspectionState>(null);
  const [hoveredComboSuit, setHoveredComboSuit] = useState<string | null>(null);
  const [selectedHandClass, setSelectedHandClass] = useState<string>('AA');
  const [rangeState, setRangeState] = useState<LocalRangeState>({
    cells: {},
    combo_overrides: {},
  });
  const [pathRangeStatesByNodeId, setPathRangeStatesByNodeId] = useState<
    Record<string, LocalRangeState>
  >({});
  const [isMatrixPainting, setIsMatrixPainting] = useState(false);
  const [isRangePaintModeEnabled, setIsRangePaintModeEnabled] = useState(false);
  const lastPaintedHandClassRef = useRef<string | null>(null);

  const matrix = useMemo(() => buildMatrix(), []);

  useEffect(() => {
    function handleGlobalMouseUp() {
      setIsMatrixPainting(false);
      lastPaintedHandClassRef.current = null;
    }

    window.addEventListener('mouseup', handleGlobalMouseUp);

    return () => {
      window.removeEventListener('mouseup', handleGlobalMouseUp);
    };
  }, []);

  const activeNode = useMemo(
    () => workspace?.nodes.find((node) => node.id === activeNodeId) ?? null,
    [workspace, activeNodeId],
  );

  const activeNodeActions = useMemo(() => activeNode?.actions ?? [], [activeNode]);

  const activePath = useMemo(
    () => (workspace && activeNodeId ? buildPathToNode(workspace.nodes, activeNodeId) : []),
    [workspace, activeNodeId],
  );

  const activeTurnNode = useMemo(
    () => activePath.find((node) => node.street === 'turn') ?? null,
    [activePath],
  );

  const activeRiverNode = useMemo(
    () => activePath.find((node) => node.street === 'river') ?? null,
    [activePath],
  );

  const effectiveTurnCard = activePath.length > 0
    ? getEffectiveStreetCardFromPath(activePath, 'turn')
    : null;
  const effectiveRiverCard = activePath.length > 0
    ? getEffectiveStreetCardFromPath(activePath, 'river')
    : null;

  const activeTurnEntryNode = activePath.length > 0
    ? getStreetEntryNodeFromPath(activePath, 'turn')
    : null;
  const activeRiverEntryNode = activePath.length > 0
    ? getStreetEntryNodeFromPath(activePath, 'river')
    : null;

  const turnVariantNodes = useMemo(
    () =>
      workspace
        ? getRunoutVariantNodes(workspace, activeTurnEntryNode, 'turn')
        : [],
    [workspace, activeTurnEntryNode],
  );

  const riverVariantNodes = useMemo(
    () =>
      workspace
        ? getRunoutVariantNodes(workspace, activeRiverEntryNode, 'river')
        : [],
    [workspace, activeRiverEntryNode],
  );

  const isWaitingForTurnCard = activeNode?.street === 'turn' && !effectiveTurnCard;
  const isWaitingForRiverCard = activeNode?.street === 'river' && !effectiveRiverCard;
  const isRangeMatrixBlocked = Boolean(isWaitingForTurnCard || isWaitingForRiverCard);

  const streetCardPickerBlockedCards = useMemo(() => {
    if (!workspace || !activeNode) {
      return new Set<string>();
    }

    return getBlockedCardsForActiveLine(workspace.board, activeNode, activePath);
  }, [workspace, activeNode, activePath]);

  const activeActorPreviousRangeConstraints = useMemo(() => {
    if (!workspace || !activeNode || activePath.length < 2) {
      return [];
    }

    const constraints: Array<{
      sourceNodeId: string;
      actionId: string;
      actionLabel: string;
    }> = [];

    for (let index = 1; index < activePath.length; index += 1) {
      const parentNode = activePath[index - 1];
      const childNode = activePath[index];

      if (parentNode.id === activeNode.id) {
        break;
      }

      if (parentNode.acting_player !== activeNode.acting_player) {
        continue;
      }

      const actionId = getActionIdTakenFromParentNode(parentNode, childNode);

      if (!actionId) {
        continue;
      }

      constraints.push({
        sourceNodeId: parentNode.id,
        actionId,
        actionLabel: buildStoryActionLabel(workspace, childNode),
      });
    }

    return constraints;
  }, [activeNode, activePath, workspace]);

  const childNodes = useMemo(
    () =>
      workspace?.nodes
        .filter(
          (node) =>
            node.parent_node_id === activeNodeId &&
            isChildNodeConsistentWithParent(workspace, node),
        )
        .sort((a, b) => {
          if (a.display_order !== b.display_order) return a.display_order - b.display_order;
          return a.created_at.localeCompare(b.created_at);
        }) ?? [],
    [workspace, activeNodeId],
  );

  const selectedAction = useMemo(
    () => activeNodeActions.find((action) => action.id === selectedActionId) ?? null,
    [activeNodeActions, selectedActionId],
  );

  const selectedActionAssignedHandClassCount = selectedAction
    ? getActionAssignedHandClassCount(rangeState, selectedAction.id)
    : 0;

  const theoreticalCombos = useMemo(
    () => buildTheoreticalCombos(selectedHandClass),
    [selectedHandClass],
  );

  const selectedCell = rangeState.cells[selectedHandClass] || null;
  const selectedHandAssignedActionId = selectedCell?.base_action_id ?? null;

  const selectedHandAssignedAction = useMemo(
    () => activeNodeActions.find((action) => action.id === selectedHandAssignedActionId) ?? null,
    [activeNodeActions, selectedHandAssignedActionId],
  );

  const effectivePreflopRangeByKey = useMemo(
    () => buildEffectivePreflopRangeByKey(spotPreflopRanges, boardPreflopRanges),
    [boardPreflopRanges, spotPreflopRanges],
  );

  const activeActorPreflopRange = useMemo(() => {
    if (!workspace || !activeNode) {
      return null;
    }

    const rangeKey = getRangeKeyForActor(workspace, activeNode.acting_player);

    if (!rangeKey) {
      return null;
    }

    return effectivePreflopRangeByKey[rangeKey] ?? null;
  }, [activeNode, effectivePreflopRangeByKey, workspace]);

  const availableHandClasses = useMemo(() => {
    let currentAvailable: Set<string> | null = activeActorPreflopRange
      ? new Set(activeActorPreflopRange)
      : null;

    for (const constraint of activeActorPreviousRangeConstraints) {
      const previousRangeState = pathRangeStatesByNodeId[constraint.sourceNodeId];

      if (!previousRangeState) {
        return currentAvailable;
      }

      const assignedForAction = getHandClassesAssignedToAction(
        previousRangeState,
        constraint.actionId,
      );

      currentAvailable =
        currentAvailable === null
          ? assignedForAction
          : intersectHandClassSets(currentAvailable, assignedForAction);
    }

    return currentAvailable;
  }, [
    activeActorPreflopRange,
    activeActorPreviousRangeConstraints,
    pathRangeStatesByNodeId,
  ]);

  const isAvailableRangeFiltered =
    Boolean(activeActorPreflopRange) || activeActorPreviousRangeConstraints.length > 0;

  const estimatedFlopPotBb = useMemo(
    () => (workspace ? getEstimatedFlopPotBb(workspace) : null),
    [workspace],
  );

  const preflopRangeSummaries = useMemo(
    () =>
      workspace
        ? buildPreflopRangeSummaries(workspace, spotPreflopRanges, boardPreflopRanges)
        : [],
    [boardPreflopRanges, spotPreflopRanges, workspace],
  );

  const editingBoardPreflopRangeSummary = useMemo(
    () =>
      preflopRangeSummaries.find((summary) => summary.key === editingBoardPreflopRangeKey) ?? null,
    [editingBoardPreflopRangeKey, preflopRangeSummaries],
  );

  const compatibleBoardRangeTemplates = useMemo(() => {
    if (!editingBoardPreflopRangeSummary || !workspace) {
      return [];
    }

    const allowedFamilies = getCompatibleBoardRangeTemplateFamilies(
      editingBoardPreflopRangeSummary.key,
    );

    const selectedPosition = editingBoardPreflopRangeSummary.position.trim().toLowerCase();
    const boardSpotType = workspace.spot.spot_type.trim().toLowerCase();

    return rangeTemplates.filter((template) => {
      const matchesPosition = template.position.trim().toLowerCase() === selectedPosition;
      const matchesFamily =
        allowedFamilies.length === 0 ||
        allowedFamilies.includes(template.range_family.trim().toLowerCase());

      const matchesSpotType =
        !template.spot_type ||
        template.spot_type.trim().toLowerCase() === boardSpotType;

      return matchesPosition && matchesFamily && matchesSpotType;
    });
  }, [editingBoardPreflopRangeSummary, rangeTemplates, workspace]);

  function openBoardPreflopRangeEditor(rangeKey: PreflopRangeKey) {
    const summary = preflopRangeSummaries.find((item) => item.key === rangeKey);

    if (!summary) {
      return;
    }

    setBoardPreflopRangeDraft(new Set(summary.handClasses));
    setEditingBoardPreflopRangeKey(rangeKey);

    if (rangeTemplates.length === 0) {
      void loadRangeTemplates();
    }
  }

  function closeBoardPreflopRangeEditor() {
    if (isSavingBoardPreflopRange) return;

    setEditingBoardPreflopRangeKey(null);
    setBoardPreflopRangeDraft(new Set());
    setIsBoardPreflopRangePainting(false);
    setBoardPreflopRangePaintMode(null);
  }

  function importTemplateIntoBoardPreflopDraft(template: PokerStudyPreflopRangeTemplate) {
    setBoardPreflopRangeDraft(new Set(getBoardRangeTemplateHandClasses(template)));
  }

  function applyBoardPreflopRangePaint(handClass: string, mode: 'select' | 'clear') {
    setBoardPreflopRangeDraft((current) => {
      const next = new Set(current);

      if (mode === 'select') {
        next.add(handClass);
      } else {
        next.delete(handClass);
      }

      return next;
    });
  }

  function startBoardPreflopRangePaint(handClass: string) {
    const mode = boardPreflopRangeDraft.has(handClass) ? 'clear' : 'select';

    setIsBoardPreflopRangePainting(true);
    setBoardPreflopRangePaintMode(mode);
    applyBoardPreflopRangePaint(handClass, mode);
  }

  function continueBoardPreflopRangePaint(handClass: string) {
    if (!isBoardPreflopRangePainting || !boardPreflopRangePaintMode) {
      return;
    }

    applyBoardPreflopRangePaint(handClass, boardPreflopRangePaintMode);
  }

  async function handleSaveBoardPreflopRange() {
    if (!boardId || !editingBoardPreflopRangeSummary) {
      return;
    }

    setIsSavingBoardPreflopRange(true);
    setErrorMessage(null);

    try {
      const saved = await upsertPokerStudyBoardPreflopRange(
        boardId,
        editingBoardPreflopRangeSummary.key,
        {
          position: editingBoardPreflopRangeSummary.position,
          label: editingBoardPreflopRangeSummary.title,
          source_spot_range_id: editingBoardPreflopRangeSummary.sourceSpotRangeId,
          range_state: {
            version: 1,
            hand_classes: sortHandClassesByMatrixOrder(Array.from(boardPreflopRangeDraft)),
          },
        },
      );

      setBoardPreflopRanges((current) => {
        const others = current.filter((range) => range.range_key !== saved.range_key);
        return [...others, saved];
      });

      closeBoardPreflopRangeEditor();
    } catch (error) {
      console.error(error);
      setErrorMessage('No se pudo guardar el rango preflop propio del board.');
    } finally {
      setIsSavingBoardPreflopRange(false);
    }
  }

  const availableComboCount = useMemo(() => {
    if (!availableHandClasses) {
      return 1326;
    }

    return Array.from(availableHandClasses).reduce(
      (total, handClass) => total + getComboCountTotal(handClass),
      0,
    );
  }, [availableHandClasses]);

  function isHandClassAvailableForActiveNode(handClass: string): boolean {
    if (!availableHandClasses) {
      return true;
    }

    return availableHandClasses.has(handClass);
  }

  const selectedHandComboAssignments = rangeState.combo_overrides[selectedHandClass] || {};

  const selectedHandAvailableCombos = useMemo(
    () => theoreticalCombos.filter((combo) => !comboUsesBlockedCard(combo, streetCardPickerBlockedCards)),
    [theoreticalCombos, streetCardPickerBlockedCards],
  );

  const selectedHandBlockedComboCount = theoreticalCombos.length - selectedHandAvailableCombos.length;

  const selectedHandSortedComboItems = useMemo(() => {
    const actionOrderById = new Map<string, number>();

    activeNodeActions.forEach((action, index) => {
      actionOrderById.set(action.id, index);
    });

    return theoreticalCombos
      .map((combo, originalIndex) => {
        const isBlockedCombo = comboUsesBlockedCard(combo, streetCardPickerBlockedCards);

        const comboAssignedActionId =
          !isBlockedCombo && Object.prototype.hasOwnProperty.call(selectedHandComboAssignments, combo)
            ? selectedHandComboAssignments[combo]
            : !isBlockedCombo
            ? selectedCell?.base_action_id ?? null
            : null;

        const comboAssignedAction =
          activeNodeActions.find((item) => item.id === comboAssignedActionId) ?? null;

        const actionOrder =
          comboAssignedActionId && actionOrderById.has(comboAssignedActionId)
            ? actionOrderById.get(comboAssignedActionId) ?? 999
            : 998;

        return {
          combo,
          originalIndex,
          isBlockedCombo,
          comboAssignedActionId,
          comboAssignedAction,
          actionOrder,
        };
      })
      .sort((left, right) => {
        if (left.isBlockedCombo !== right.isBlockedCombo) {
          return left.isBlockedCombo ? 1 : -1;
        }

        if (left.actionOrder !== right.actionOrder) {
          return left.actionOrder - right.actionOrder;
        }

        return left.originalIndex - right.originalIndex;
      });
  }, [
    activeNodeActions,
    selectedCell?.base_action_id,
    selectedHandComboAssignments,
    streetCardPickerBlockedCards,
    theoreticalCombos,
  ]);

  const paintedBoardImpactComboKeys = useMemo(
    () => getPaintedComboKeysFromRangeState(rangeState, streetCardPickerBlockedCards),
    [rangeState, streetCardPickerBlockedCards],
  );

  const boardImpactActionFilterOptions = useMemo(() => {
    const actionOptions = activeNodeActions
      .map((action) => {
        const comboKeys = getPaintedComboKeysFromRangeState(
          rangeState,
          streetCardPickerBlockedCards,
          action.id,
        );

        return {
          id: action.id,
          label: formatActionLabel(action.action_type, action.size_percentage),
          comboCount: comboKeys.length,
        };
      })
      .filter((option) => option.comboCount > 0);

    return [
      {
        id: 'all',
        label: 'Todo',
        comboCount: paintedBoardImpactComboKeys.length,
      },
      ...actionOptions,
    ];
  }, [activeNodeActions, paintedBoardImpactComboKeys.length, rangeState, streetCardPickerBlockedCards]);

  useEffect(() => {
    if (
      boardImpactActionFilter !== 'all' &&
      !boardImpactActionFilterOptions.some((option) => option.id === boardImpactActionFilter)
    ) {
      setBoardImpactActionFilter('all');
    }
  }, [boardImpactActionFilter, boardImpactActionFilterOptions]);

  const filteredBoardImpactComboKeys = useMemo(() => {
    if (boardImpactActionFilter === 'all') {
      return paintedBoardImpactComboKeys;
    }

    return getPaintedComboKeysFromRangeState(
      rangeState,
      streetCardPickerBlockedCards,
      boardImpactActionFilter,
    );
  }, [
    boardImpactActionFilter,
    paintedBoardImpactComboKeys,
    rangeState,
    streetCardPickerBlockedCards,
  ]);

  const boardImpact = useMemo(() => {
    if (!workspace) {
      return null;
    }

    return calculateBoardImpactFromComboKeys({
      comboKeys: filteredBoardImpactComboKeys,
      boardCards: [
        workspace.board.flop_card_1,
        workspace.board.flop_card_2,
        workspace.board.flop_card_3,
        effectiveTurnCard,
        effectiveRiverCard,
      ],
    });
  }, [effectiveRiverCard, effectiveTurnCard, filteredBoardImpactComboKeys, workspace]);

  const visibleBoardImpactBuckets = useMemo(
    () => boardImpact?.buckets.filter((bucket) => bucket.combos > 0) ?? [],
    [boardImpact],
  );

  const visibleBoardDrawBuckets = useMemo(
    () => boardImpact?.drawBuckets.filter((bucket) => bucket.combos > 0) ?? [],
    [boardImpact],
  );

  const boardComboDiagnostics = useMemo(
    () =>
      workspace
        ? calculateBoardComboDiagnosticsFromComboKeys({
            comboKeys: paintedBoardImpactComboKeys,
            boardCards: [
              workspace.board.flop_card_1,
              workspace.board.flop_card_2,
              workspace.board.flop_card_3,
              effectiveTurnCard,
              effectiveRiverCard,
            ],
          })
        : [],
    [effectiveRiverCard, effectiveTurnCard, paintedBoardImpactComboKeys, workspace],
  );

  const boardComboDiagnosticByComboKey = useMemo(
    () =>
      Object.fromEntries(
        boardComboDiagnostics.map((diagnostic) => [diagnostic.comboKey, diagnostic]),
      ) as Record<string, BoardComboDiagnostic | undefined>,
    [boardComboDiagnostics],
  );

  const boardImpactByActionId = useMemo(() => {
    if (!workspace) {
      return {} as Record<string, ReturnType<typeof calculateBoardImpactFromComboKeys>>;
    }

    const boardCards = [
      workspace.board.flop_card_1,
      workspace.board.flop_card_2,
      workspace.board.flop_card_3,
      effectiveTurnCard,
      effectiveRiverCard,
    ];

    return Object.fromEntries(
      boardImpactActionFilterOptions
        .filter((option) => option.id !== 'all')
        .map((option) => {
          const comboKeys = getPaintedComboKeysFromRangeState(
            rangeState,
            streetCardPickerBlockedCards,
            option.id,
          );

          return [
            option.id,
            calculateBoardImpactFromComboKeys({
              comboKeys,
              boardCards,
            }),
          ];
        }),
    ) as Record<string, ReturnType<typeof calculateBoardImpactFromComboKeys>>;
  }, [
    boardImpactActionFilterOptions,
    effectiveRiverCard,
    effectiveTurnCard,
    rangeState,
    streetCardPickerBlockedCards,
    workspace,
  ]);

  const boardImpactActionSegmentsByBucket = useMemo(() => {
    if (!boardImpact || boardImpactActionFilter !== 'all') {
      return {} as Record<string, BoardImpactActionSegment[]>;
    }

    const actionsById = new Map(activeNodeActions.map((action) => [action.id, action]));
    const result: Record<string, BoardImpactActionSegment[]> = {};

    boardImpact.buckets.forEach((bucket) => {
      if (bucket.combos <= 0) {
        return;
      }

      const segments = boardImpactActionFilterOptions
        .filter((option) => option.id !== 'all')
        .map((option) => {
          const action = actionsById.get(option.id);
          const actionImpact = boardImpactByActionId[option.id];
          const actionBucket = actionImpact?.buckets.find((item) => item.key === bucket.key);
          const combos = actionBucket?.combos ?? 0;

          return {
            actionId: option.id,
            label: option.label,
            color: action
              ? getActionColorPreview(action.action_type, action.size_percentage)
              : 'rgba(148, 163, 184, 0.55)',
            combos,
            percentage: bucket.combos > 0 ? (combos / bucket.combos) * 100 : 0,
          };
        })
        .filter((segment) => segment.combos > 0);

      result[bucket.key] = segments;
    });

    return result;
  }, [
    activeNodeActions,
    boardImpact,
    boardImpactActionFilter,
    boardImpactActionFilterOptions,
    boardImpactByActionId,
  ]);

  const boardImpactActionSegmentsByDrawBucket = useMemo(() => {
    if (!boardImpact || boardImpactActionFilter !== 'all') {
      return {} as Record<string, BoardImpactActionSegment[]>;
    }

    const actionsById = new Map(activeNodeActions.map((action) => [action.id, action]));
    const result: Record<string, BoardImpactActionSegment[]> = {};

    boardImpact.drawBuckets.forEach((bucket) => {
      if (bucket.combos <= 0) {
        return;
      }

      const segments = boardImpactActionFilterOptions
        .filter((option) => option.id !== 'all')
        .map((option) => {
          const action = actionsById.get(option.id);
          const actionImpact = boardImpactByActionId[option.id];
          const actionBucket = actionImpact?.drawBuckets.find((item) => item.key === bucket.key);
          const combos = actionBucket?.combos ?? 0;

          return {
            actionId: option.id,
            label: option.label,
            color: action
              ? getActionColorPreview(action.action_type, action.size_percentage)
              : 'rgba(148, 163, 184, 0.55)',
            combos,
            percentage: bucket.combos > 0 ? (combos / bucket.combos) * 100 : 0,
          };
        })
        .filter((segment) => segment.combos > 0);

      result[bucket.key] = segments;
    });

    return result;
  }, [
    activeNodeActions,
    boardImpact,
    boardImpactActionFilter,
    boardImpactActionFilterOptions,
    boardImpactByActionId,
  ]);

  const hoveredBoardImpactHandClasses = useMemo(() => {
    if (!boardImpact) {
      return new Set<string>();
    }

    if (hoveredBoardImpactBucketKey) {
      const bucket = boardImpact.buckets.find((item) => item.key === hoveredBoardImpactBucketKey);
      return new Set(bucket?.handClasses ?? []);
    }

    if (hoveredBoardDrawBucketKey) {
      const bucket = boardImpact.drawBuckets.find((item) => item.key === hoveredBoardDrawBucketKey);
      return new Set(bucket?.handClasses ?? []);
    }

    return new Set<string>();
  }, [boardImpact, hoveredBoardDrawBucketKey, hoveredBoardImpactBucketKey]);

  const topBarSortedActions = useMemo(() => {
    const getActionPriority = (actionType: string, sizePercentage?: number | null) => {
      if (actionType === 'fold') return 0;
      if (actionType === 'check') return 1;
      if (actionType === 'call') return 2;
      if (actionType === 'bet') return 3 + (sizePercentage ?? 0) / 1000;
      if (actionType === 'raise') return 4 + (sizePercentage ?? 0) / 1000;
      return 99;
    };

    return [...activeNodeActions].sort((left, right) => {
      return (
        getActionPriority(left.action_type, left.size_percentage) -
        getActionPriority(right.action_type, right.size_percentage)
      );
    });
  }, [activeNodeActions]);

  const topBarActionColumns = useMemo(() => {
    const items: Array<
      | {
          kind: 'action';
          key: string;
          id: string;
          label: string;
          action_type: string;
          size_percentage: number | null;
        }
      | {
          kind: 'clear';
          key: 'clear';
          id: 'clear';
          label: 'Clear';
        }
    > = [
      ...topBarSortedActions.map((action) => ({
        kind: 'action' as const,
        key: action.id,
        id: action.id,
        label: formatActionLabel(action.action_type, action.size_percentage),
        action_type: action.action_type,
        size_percentage: action.size_percentage,
      })),
      {
        kind: 'clear' as const,
        key: 'clear',
        id: 'clear',
        label: 'Clear',
      },
    ];

    const columns: typeof items[] = [];

    for (let index = 0; index < items.length; index += 3) {
      columns.push(items.slice(index, index + 3));
    }

    return columns.reverse();
  }, [topBarSortedActions]);

  const quickActionPresets = useMemo(
    () => (activeNode ? getQuickActionPresets(activeNode) : []),
    [activeNode],
  );

  const quickActionFamilies = useMemo(() => {
    const availableFamilies = new Set(
      quickActionPresets.map((preset) => preset.action_type),
    );

    const orderedFamilies: Array<QuickActionPreset['action_type'] | 'clear'> = [
      'check',
      'bet',
      'call',
      'raise',
      'fold',
      'clear',
    ];

    return orderedFamilies.filter((family) => family === 'clear' || availableFamilies.has(family));
  }, [quickActionPresets]);

  const quickActionModalPresets = useMemo(
    () =>
      quickActionModalFamily
        ? quickActionPresets.filter((preset) => preset.action_type === quickActionModalFamily)
        : [],
    [quickActionModalFamily, quickActionPresets],
  );

  function formatQuickActionFamilyLabel(family: QuickActionPreset['action_type'] | 'clear'): string {
    switch (family) {
      case 'check':
        return 'Check';
      case 'bet':
        return 'Bet';
      case 'call':
        return 'Call';
      case 'raise':
        return 'Raise';
      case 'fold':
        return 'Fold';
      case 'clear':
        return 'Clear';
      default:
        return family;
    }
  }

  function getQuickActionFamilyColor(family: QuickActionPreset['action_type'] | 'clear'): string {
    if (family === 'clear') {
      return 'rgba(148, 163, 184, 0.24)';
    }

    return getActionColorPreview(family);
  }

  async function handleQuickActionFamilyClick(
    family: QuickActionPreset['action_type'] | 'clear',
  ) {
    if (family === 'clear') {
      setSelectedActionId('clear');
      setIsRangePaintModeEnabled(true);
      return;
    }

    const familyPresets = quickActionPresets.filter((preset) => preset.action_type === family);

    if (family === 'bet' || family === 'raise' || familyPresets.length > 1) {
      if (family === 'bet' || family === 'raise') {
        setCustomActionType(family);
      }

      setQuickActionModalFamily(family);
      return;
    }

    const onlyPreset = familyPresets[0];

    if (onlyPreset) {
      await handleEnsureAction(onlyPreset);
      setIsRangePaintModeEnabled(true);
    }
  }

  async function loadRangeTemplates() {
    setIsLoadingRangeTemplates(true);

    try {
      const rows = await listPokerStudyPreflopRangeTemplates();
      setRangeTemplates(rows);
    } catch (error) {
      console.error(error);
      setErrorMessage('No se pudieron cargar las plantillas de rango.');
    } finally {
      setIsLoadingRangeTemplates(false);
    }
  }


  async function loadWorkspace(preferredNodeId?: string | null) {
    if (!boardId) {
      setErrorMessage('No se ha podido resolver el board.');
      setIsLoading(false);
      return;
    }

    const shouldShowInitialLoader = !workspace;

    if (shouldShowInitialLoader) {
      setIsLoading(true);
    }

    setErrorMessage(null);

    try {
      const response = await getPokerStudyBoardWorkspace(boardId);
      setWorkspace(response);

      setIsLoadingSpotTheory(true);

      const [spotRanges, boardRanges, spotDetail] = await Promise.all([
        listPokerStudySpotPreflopRanges(response.board.spot_id),
        listPokerStudyBoardPreflopRanges(boardId),
        getPokerStudySpotDetail(response.board.spot_id),
      ]);

      setSpotPreflopRanges(spotRanges);
      setBoardPreflopRanges(boardRanges);
      setSpotTheoryContent(spotDetail.spot_notes?.content || '');
      setIsLoadingSpotTheory(false);

      const storedActiveNodeId = window.localStorage.getItem(
        getBoardActiveNodeStorageKey(boardId),
      );

      const nextActiveNodeId =
        preferredNodeId && response.nodes.some((node) => node.id === preferredNodeId)
          ? preferredNodeId
          : storedActiveNodeId && response.nodes.some((node) => node.id === storedActiveNodeId)
          ? storedActiveNodeId
          : response.default_active_node_id;

      setActiveNodeId(nextActiveNodeId);
    } catch (error) {
      console.error(error);
      setErrorMessage('No se pudo cargar el workspace del board.');

      if (shouldShowInitialLoader) {
        setWorkspace(null);
        setActiveNodeId(null);
      }
    } finally {
      setIsLoadingSpotTheory(false);

      if (shouldShowInitialLoader) {
        setIsLoading(false);
      }
    }
  }

  async function loadActiveNodeRangeState(nodeId: string) {
    setIsLoadingRangeState(true);
    setErrorMessage(null);
    setNodeRangeAutosaveStatus('idle');
    skipNextNodeRangeAutosaveRef.current = true;

    try {
      const response = await getPokerStudyNodeRangeState(nodeId);
      setRangeState(mapApiNodeRangeStateToLocal(response));
    } catch (error) {
      console.error(error);
      setErrorMessage('No se pudo cargar el range-state del nodo activo.');
      setRangeState({
        cells: {},
        combo_overrides: {},
      });
      setNodeRangeAutosaveStatus('error');
    } finally {
      setIsLoadingRangeState(false);
    }
  }

  useEffect(() => {
    void loadWorkspace();
  }, [boardId]);

  useEffect(() => {
    function stopBoardPreflopRangePainting() {
      setIsBoardPreflopRangePainting(false);
      setBoardPreflopRangePaintMode(null);
    }

    window.addEventListener('mouseup', stopBoardPreflopRangePainting);

    return () => {
      window.removeEventListener('mouseup', stopBoardPreflopRangePainting);
    };
  }, []);

  useEffect(() => {
    if (!activeNodeId) return;
    void loadActiveNodeRangeState(activeNodeId);
  }, [activeNodeId]);

  useEffect(() => {
    if (!activeNodeId || isLoadingRangeState || isRangeMatrixBlocked) {
      return;
    }

    if (skipNextNodeRangeAutosaveRef.current) {
      skipNextNodeRangeAutosaveRef.current = false;
      return;
    }

    setNodeRangeAutosaveStatus('saving');

    if (nodeRangeAutosaveTimerRef.current) {
      window.clearTimeout(nodeRangeAutosaveTimerRef.current);
    }

    nodeRangeAutosaveTimerRef.current = window.setTimeout(() => {
      void persistCurrentNodeRangeState({ silent: true });
      nodeRangeAutosaveTimerRef.current = null;
    }, 700);

    return () => {
      if (nodeRangeAutosaveTimerRef.current) {
        window.clearTimeout(nodeRangeAutosaveTimerRef.current);
        nodeRangeAutosaveTimerRef.current = null;
      }
    };
  }, [activeNodeId, isLoadingRangeState, isRangeMatrixBlocked, rangeState]);

  useEffect(() => {
    if (!boardId || !activeNodeId) {
      return;
    }

    window.localStorage.setItem(
      getBoardActiveNodeStorageKey(boardId),
      activeNodeId,
    );
  }, [boardId, activeNodeId]);

  useEffect(() => {
    if (!activeNode) {
      setSelectedActionId(null);
      return;
    }

    setSelectedActionId((current) => {
      if (current === 'clear') return current;
      if (current && activeNode.actions.some((action) => action.id === current)) return current;
      return activeNode.actions[0]?.id ?? null;
    });
  }, [activeNode]);

  useEffect(() => {
    let isMounted = true;

    async function loadPathRangeStates() {
      const missingNodeIds = activeActorPreviousRangeConstraints
        .map((constraint) => constraint.sourceNodeId)
        .filter((nodeId) => !pathRangeStatesByNodeId[nodeId]);

      if (missingNodeIds.length === 0) {
        return;
      }

      try {
        const entries = await Promise.all(
          missingNodeIds.map(async (nodeId) => {
            const apiState = await getPokerStudyNodeRangeState(nodeId);
            return [nodeId, mapApiNodeRangeStateToLocal(apiState)] as const;
          }),
        );

        if (!isMounted) {
          return;
        }

        setPathRangeStatesByNodeId((current) => {
          const next = { ...current };

          entries.forEach(([nodeId, localState]) => {
            next[nodeId] = localState;
          });

          return next;
        });
      } catch (error) {
        console.error(error);
        setErrorMessage('No se pudieron cargar los rangos previos de la línea.');
      }
    }

    void loadPathRangeStates();

    return () => {
      isMounted = false;
    };
  }, [activeActorPreviousRangeConstraints, pathRangeStatesByNodeId]);

  useEffect(() => {
    if (!workspace || !activeNode || activeNode.is_root || !activeNode.parent_node_id) {
      return;
    }

    if (isChildNodeConsistentWithParent(workspace, activeNode)) {
      return;
    }

    const parentNode = workspace.nodes.find((node) => node.id === activeNode.parent_node_id);

    if (!parentNode) {
      return;
    }

    setSelectedChildByNodeId((current) => {
      if (current[parentNode.id] !== activeNode.id) {
        return current;
      }

      const next = { ...current };
      delete next[parentNode.id];
      return next;
    });

    setActiveNodeId(parentNode.id);
  }, [workspace, activeNode]);

  useEffect(() => {
    if (activePath.length <= 1) {
      return;
    }

    setSelectedChildByNodeId((current) => {
      const next = { ...current };
      let changed = false;

      for (let index = 0; index < activePath.length - 1; index += 1) {
        const parentNode = activePath[index];
        const childNode = activePath[index + 1];

        if (next[parentNode.id] !== childNode.id) {
          next[parentNode.id] = childNode.id;
          changed = true;
        }
      }

      return changed ? next : current;
    });
  }, [activePath]);

  useEffect(() => {
    function handleMouseUp() {
      setIsMatrixPainting(false);
    }

    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, []);

  async function handleSelectStreetCard(card: string) {
    if (!workspace || !activeNode || !streetCardPickerOpen) {
      return;
    }

    const street = streetCardPickerOpen;
    const cardField = street === 'turn' ? 'turn_card' : 'river_card';
    const entryNode = street === 'turn' ? activeTurnEntryNode : activeRiverEntryNode;

    if (!entryNode) {
      return;
    }

    setErrorMessage(null);

    const existingVariant = workspace.nodes.find((node) => {
      if (!runoutVariantMatchesEntryNode(entryNode, node)) {
        return false;
      }

      if (!isChildNodeConsistentWithParent(workspace, node)) {
        return false;
      }

      return getRunoutCardFromNode(node, street) === card;
    });

    if (existingVariant) {
      const saved = await persistCurrentNodeRangeState({ silent: true });

      if (!saved) {
        return;
      }

      setStreetCardPickerOpen(null);

      setSelectedChildByNodeId((current) => {
        if (!entryNode.parent_node_id) {
          return current;
        }

        return {
          ...current,
          [entryNode.parent_node_id]: existingVariant.id,
        };
      });

      setActiveNodeId(existingVariant.id);
      return;
    }

    const entryNodeHasContent =
      entryNode.actions.length > 0 ||
      workspace.nodes.some((node) => node.parent_node_id === entryNode.id);

    const shouldUpdateCurrentEntryNode =
      !getRunoutCardFromNode(entryNode, street) && !entryNodeHasContent;

    try {
      const saved = await persistCurrentNodeRangeState({ silent: true });

      if (!saved) {
        return;
      }

      if (shouldUpdateCurrentEntryNode) {
        await updatePokerStudyBoardNode(entryNode.id, {
          street: entryNode.street,
          acting_player: entryNode.acting_player,
          node_kind: entryNode.node_kind,
          action_taken_type: entryNode.action_taken_type,
          action_taken_size: entryNode.action_taken_size,
          action_taken_label: entryNode.action_taken_label,
          [cardField]: card,
          display_order: entryNode.display_order,
        });

        setStreetCardPickerOpen(null);
        await loadWorkspace(entryNode.id);
        return;
      }

      if (!entryNode.parent_node_id) {
        return;
      }

      const parentNode = workspace.nodes.find((node) => node.id === entryNode.parent_node_id);

      if (!parentNode) {
        return;
      }

      const createdVariant = await createPokerStudyBoardNodeChild(parentNode.id, {
        street: entryNode.street,
        acting_player: entryNode.acting_player,
        node_kind: entryNode.node_kind,
        action_taken_type: entryNode.action_taken_type,
        action_taken_size: entryNode.action_taken_size,
        action_taken_label: entryNode.action_taken_label,
        display_order: entryNode.display_order + 1,
        turn_card:
          street === 'turn'
            ? card
            : entryNode.turn_card ?? effectiveTurnCard ?? null,
        river_card:
          street === 'river'
            ? card
            : entryNode.river_card ?? effectiveRiverCard ?? null,
      });

      setStreetCardPickerOpen(null);

      setSelectedChildByNodeId((current) => ({
        ...current,
        [parentNode.id]: createdVariant.id,
      }));

      await loadWorkspace(createdVariant.id);
    } catch (error) {
      console.error(error);
      setErrorMessage('No se pudo guardar la variante de la calle.');
    }
  }

  const previousPathNodes = useMemo(() => {
    if (!activeNodeId) return [];
    return activePath.filter((node) => node.id !== activeNodeId);
  }, [activeNodeId, activePath]);

  const previousPathNodeIds = useMemo(
    () => previousPathNodes.map((node) => node.id),
    [previousPathNodes],
  );

  const previousPathNodeIdsKey = previousPathNodeIds.join('|');

  const previousNodeNotesByNodeId = useMemo(() => {
    return new Map(previousNodeNotes.map((note) => [note.node_id, note]));
  }, [previousNodeNotes]);

  const persistCurrentNodeNotes = useCallback(
    async (content = nodeNotesContent): Promise<boolean> => {
      if (!activeNodeId) return true;

      if (content === nodeNotesLastSavedValueRef.current) {
        return true;
      }

      setNodeNotesAutosaveStatus('saving');

      try {
        const saved = await upsertPokerStudyNodeNotes(activeNodeId, { content });
        nodeNotesLastSavedValueRef.current = saved.content;
        setNodeNotesAutosaveStatus('saved');
        return true;
      } catch (error) {
        console.error(error);
        setNodeNotesAutosaveStatus('error');
        setErrorMessage('No se pudieron guardar los apuntes del nodo.');
        return false;
      }
    },
    [activeNodeId, nodeNotesContent],
  );

  useEffect(() => {
    let isMounted = true;

    async function loadCurrentNodeNotes() {
      if (!activeNodeId) {
        setNodeNotesContent('');
        nodeNotesLastSavedValueRef.current = '';
        if (nodeNotesEditorRef.current) {
          nodeNotesEditorRef.current.innerHTML = '';
        }
        return;
      }

      setNodeNotesAutosaveStatus('idle');

      try {
        const response = await getPokerStudyNodeNotes(activeNodeId);
        const nextContent = response?.content || '';

        if (!isMounted) return;

        setNodeNotesContent(nextContent);
        nodeNotesLastSavedValueRef.current = nextContent;

        if (nodeNotesEditorRef.current) {
          nodeNotesEditorRef.current.innerHTML = nextContent;
        }
      } catch (error) {
        console.error(error);
        if (!isMounted) return;
        setNodeNotesAutosaveStatus('error');
        setErrorMessage('No se pudieron cargar los apuntes del nodo.');
      }
    }

    void loadCurrentNodeNotes();

    return () => {
      isMounted = false;
    };
  }, [activeNodeId]);

  useEffect(() => {
    let isMounted = true;

    async function loadPreviousNodeNotes() {
      if (previousPathNodeIds.length === 0) {
        setPreviousNodeNotes([]);
        return;
      }

      try {
        const response = await listPokerStudyNodeNotesByNodeIds(previousPathNodeIds);

        if (!isMounted) return;

        const notesWithContent = response.filter((note) => stripHtmlForPreview(note.content).length > 0);
        setPreviousNodeNotes(notesWithContent);
      } catch (error) {
        console.error(error);
        if (!isMounted) return;
        setErrorMessage('No se pudieron cargar los apuntes anteriores de la rama.');
      }
    }

    void loadPreviousNodeNotes();

    return () => {
      isMounted = false;
    };
  }, [previousPathNodeIdsKey]);

  useEffect(() => {
    if (!activeNodeId) return;

    if (nodeNotesContent === nodeNotesLastSavedValueRef.current) {
      return;
    }

    setNodeNotesAutosaveStatus('saving');

    if (nodeNotesAutosaveTimerRef.current) {
      window.clearTimeout(nodeNotesAutosaveTimerRef.current);
    }

    nodeNotesAutosaveTimerRef.current = window.setTimeout(() => {
      void persistCurrentNodeNotes(nodeNotesContent);
      nodeNotesAutosaveTimerRef.current = null;
    }, 850);

    return () => {
      if (nodeNotesAutosaveTimerRef.current) {
        window.clearTimeout(nodeNotesAutosaveTimerRef.current);
        nodeNotesAutosaveTimerRef.current = null;
      }
    };
  }, [activeNodeId, nodeNotesContent, persistCurrentNodeNotes]);

  function syncNodeNotesContentFromEditor() {
    const nextContent = nodeNotesEditorRef.current?.innerHTML || '';
    setNodeNotesContent(nextContent);
  }

  function applyNodeNotesCommand(command: 'bold' | 'italic' | 'insertUnorderedList') {
    nodeNotesEditorRef.current?.focus();
    document.execCommand(command, false);
    syncNodeNotesContentFromEditor();
  }

  function insertSuitInNodeNotes(symbol: string, color: string) {
    nodeNotesEditorRef.current?.focus();

    document.execCommand(
      'insertHTML',
      false,
      `<span style="color:${color};font-weight:900">${symbol}</span>&nbsp;`,
    );

    syncNodeNotesContentFromEditor();
  }

  async function persistCurrentNodeRangeState({
    silent = false,
  }: {
    silent?: boolean;
  } = {}): Promise<boolean> {
    if (!activeNodeId) return true;

    if (isRangeMatrixBlocked) {
      if (!silent) {
        setErrorMessage('Selecciona primero la carta de la calle antes de guardar el rango.');
      }

      return true;
    }

    if (!silent) {
      setErrorMessage(null);
    }

    setNodeRangeAutosaveStatus('saving');

    try {
      await upsertPokerStudyNodeRangeState(
        activeNodeId,
        mapLocalNodeRangeStateToApi(rangeState),
      );

      setNodeRangeAutosaveStatus('saved');
      return true;
    } catch (error) {
      console.error(error);
      setNodeRangeAutosaveStatus('error');
      setErrorMessage('No se pudo guardar el range-state del nodo.');
      return false;
    }
  }

  async function handleSelectNodeWithAutosave(nextNodeId: string) {
    if (!nextNodeId || nextNodeId === activeNodeId) {
      return;
    }

    if (!(await persistCurrentNodeNotes())) {
      return;
    }

    if (!(await persistCurrentNodeRangeState({ silent: true }))) {
      return;
    }

    setActiveNodeId(nextNodeId);
  }

  async function handleEnsureAction(preset: QuickActionPreset): Promise<PokerStudyNodeAction | null> {
    if (!activeNode) return null;

    const existing = findMatchingAction(activeNode.actions, preset);
    if (existing) {
      setSelectedActionId(existing.id);
      return existing;
    }

    setIsCreatingAction(true);
    setErrorMessage(null);

    try {
      const created = await createPokerStudyNodeAction(activeNode.id, {
        action_type: preset.action_type,
        label: preset.label,
        size_percentage: preset.size_percentage,
        display_order: activeNode.actions.length,
      });

      await loadWorkspace(activeNode.id);
      setSelectedActionId(created.id);
      return created;
    } catch (error) {
      console.error(error);
      setErrorMessage('No se pudo crear la acción del nodo.');
      return null;
    } finally {
      setIsCreatingAction(false);
    }
  }

  async function handleCreateCustomAction() {
    const parsedSize = Number(customActionSize);

    if (!Number.isFinite(parsedSize) || parsedSize < 0) {
      setErrorMessage('El tamaño debe ser un número válido.');
      return;
    }

    await handleEnsureAction({
      action_type: customActionType,
      label: formatActionLabel(customActionType, parsedSize),
      size_percentage: parsedSize,
    });
  }

  async function handleSelectOrCreateStoryAction(
    sourceNode: PokerStudyBoardWorkspace['nodes'][number],
    action: PokerStudyNodeAction,
  ) {
    if (!workspace) {
      return;
    }

    if (sourceNode.id === activeNode?.id && !hasActionAssignedRange(rangeState, action.id)) {
      setErrorMessage(
        `Pinta al menos una mano para ${formatActionLabel(
          action.action_type,
          action.size_percentage,
        )} antes de elegir esa línea.`,
      );
      return;
    }

    if (!(await persistCurrentNodeRangeState({ silent: true }))) {
      return;
    }

    const sourceChildrenCount =
      workspace.nodes.filter((node) => node.parent_node_id === sourceNode.id).length ?? 0;

    const expectedPayload = {
      ...buildNextNodeFromActionPayload(
        workspace,
        sourceNode,
        action,
        sourceChildrenCount,
      ),
      turn_card: sourceNode.turn_card ?? effectiveTurnCard ?? null,
      river_card: sourceNode.river_card ?? effectiveRiverCard ?? null,
    };

    const existingChild = workspace.nodes.find(
      (node) =>
        node.parent_node_id === sourceNode.id &&
        actionMatchesChildNode(action, node) &&
        node.street === expectedPayload.street &&
        node.acting_player === expectedPayload.acting_player &&
        node.node_kind === expectedPayload.node_kind,
    );

    if (existingChild) {
      setSelectedChildByNodeId((current) => ({
        ...current,
        [sourceNode.id]: existingChild.id,
      }));

      const deepestRememberedNodeId = resolveRememberedDeepestNodeId(
        workspace,
        existingChild.id,
        {
          ...selectedChildByNodeId,
          [sourceNode.id]: existingChild.id,
        },
      );

      setActiveNodeId(deepestRememberedNodeId);
      return;
    }

    if (!(await persistCurrentNodeRangeState({ silent: true }))) {
      return;
    }

    setIsCreatingChildNode(true);
    setErrorMessage(null);

    try {
      const createdNode = await createPokerStudyBoardNodeChild(
        sourceNode.id,
        expectedPayload,
      );

      setSelectedChildByNodeId((current) => ({
        ...current,
        [sourceNode.id]: createdNode.id,
      }));

      await loadWorkspace(createdNode.id);
    } catch (error) {
      console.error(error);
      setErrorMessage('No se pudo abrir o crear el siguiente punto de la historia.');
    } finally {
      setIsCreatingChildNode(false);
    }
  }

  function buildInspectionStateFromHandClass(handClass: string): BoardImpactInspectionState {
    if (!boardImpact) {
      return null;
    }

    const madeHandKeys = new Set<BoardImpactCategoryKey>();
    const drawKeys = new Set<BoardDrawCategoryKey>();
    const actionIds = new Set<string>();

    boardImpact.buckets.forEach((bucket) => {
      if (bucket.handClasses.includes(handClass)) {
        madeHandKeys.add(bucket.key);
      }
    });

    boardImpact.drawBuckets.forEach((bucket) => {
      if (bucket.handClasses.includes(handClass)) {
        drawKeys.add(bucket.key);
      }
    });

    const combos = buildTheoreticalCombos(handClass);
    const comboOverridesForHandClass = rangeState.combo_overrides[handClass] || {};
    const cell = rangeState.cells[handClass];

    combos.forEach((combo) => {
      if (comboUsesBlockedCard(combo, streetCardPickerBlockedCards)) {
        return;
      }

      const resolvedActionId =
        Object.prototype.hasOwnProperty.call(comboOverridesForHandClass, combo)
          ? comboOverridesForHandClass[combo]
          : cell?.base_action_id ?? null;

      if (resolvedActionId) {
        actionIds.add(resolvedActionId);
      }
    });

    return {
      madeHandKeys,
      drawKeys,
      actionIds,
    };
  }

  function buildInspectionStateFromCombo(comboKey: string): BoardImpactInspectionState {
    const diagnostic = boardComboDiagnosticByComboKey[comboKey];

    if (!diagnostic) {
      return null;
    }

    const actionId = getResolvedActionIdForComboFromRangeState(
      rangeState,
      diagnostic.handClass,
      comboKey,
    );

    return {
      madeHandKeys: new Set([diagnostic.madeHand.key]),
      drawKeys: new Set(diagnostic.draws.map((draw) => draw.key)),
      actionIds: actionId ? new Set([actionId]) : new Set<string>(),
    };
  }

  function handleComboClick(combo: string) {
    if (isRangeMatrixBlocked) return;
    if (comboUsesBlockedCard(combo, streetCardPickerBlockedCards)) return;
    if (!selectedActionId) return;

    setRangeState((current) => {
      const currentCell =
        current.cells[selectedHandClass] || buildLocalRangeCell(selectedHandClass, null, {});
      const currentOverrides = current.combo_overrides[selectedHandClass] || {};

      const nextOverrides: Record<string, string | null> =
        selectedActionId === 'clear'
          ? { ...currentOverrides, [combo]: null }
          : { ...currentOverrides, [combo]: selectedActionId };

      const normalizedOverrides = normalizeComboOverridesForHandClass(
        currentCell.base_action_id,
        nextOverrides,
      );

      return {
        cells: {
          ...current.cells,
          [selectedHandClass]: buildLocalRangeCell(
            selectedHandClass,
            currentCell.base_action_id,
            normalizedOverrides,
          ),
        },
        combo_overrides: {
          ...current.combo_overrides,
          [selectedHandClass]: normalizedOverrides,
        },
      };
    });
  }

  function applyActionToHandClasses(
    handClasses: string[],
    actionId: string | null,
  ) {
    if (isRangeMatrixBlocked || !actionId) return;

    setRangeState((current) => {
      const nextCells = { ...current.cells };
      const nextComboOverrides = { ...current.combo_overrides };

      handClasses.forEach((handClass) => {
        if (!isHandClassAvailableForActiveNode(handClass)) {
          return;
        }

        if (actionId === 'clear') {
          nextCells[handClass] = buildLocalRangeCell(handClass, null, {});
          nextComboOverrides[handClass] = {};
          return;
        }

        nextCells[handClass] = buildLocalRangeCell(handClass, actionId, {});
        nextComboOverrides[handClass] = {};
      });

      return {
        cells: nextCells,
        combo_overrides: nextComboOverrides,
      };
    });
  }

  function applyActionToHandClass(handClass: string, actionId: string | null) {
    applyActionToHandClasses([handClass], actionId);
  }

  function getHandClassesBetween(
    fromHandClass: string,
    toHandClass: string,
  ): string[] {
    function findMatrixPosition(handClass: string) {
      for (let row = 0; row < matrix.length; row += 1) {
        const column = matrix[row].indexOf(handClass);

        if (column !== -1) {
          return { row, column };
        }
      }

      return null;
    }

    const fromPosition = findMatrixPosition(fromHandClass);
    const toPosition = findMatrixPosition(toHandClass);

    if (!fromPosition || !toPosition) {
      return [toHandClass];
    }

    const rowDistance = toPosition.row - fromPosition.row;
    const columnDistance = toPosition.column - fromPosition.column;
    const steps = Math.max(Math.abs(rowDistance), Math.abs(columnDistance));

    if (steps === 0) {
      return [toHandClass];
    }

    const interpolatedHandClasses: string[] = [];

    for (let step = 0; step <= steps; step += 1) {
      const row = Math.round(
        fromPosition.row + (rowDistance * step) / steps,
      );
      const column = Math.round(
        fromPosition.column + (columnDistance * step) / steps,
      );
      const handClass = matrix[row][column];

      if (!interpolatedHandClasses.includes(handClass)) {
        interpolatedHandClasses.push(handClass);
      }
    }

    return interpolatedHandClasses;
  }

  function applyActionToAllAvailableHandClasses() {
    if (isRangeMatrixBlocked || !selectedActionId) return;

    setRangeState((current) => {
      const nextCells = { ...current.cells };
      const nextComboOverrides = { ...current.combo_overrides };

      matrix.flat().forEach((handClass) => {
        if (!isHandClassAvailableForActiveNode(handClass)) {
          return;
        }

        if (selectedActionId === 'clear') {
          nextCells[handClass] = buildLocalRangeCell(handClass, null, {});
          nextComboOverrides[handClass] = {};
          return;
        }

        nextCells[handClass] = buildLocalRangeCell(handClass, selectedActionId, {});
        nextComboOverrides[handClass] = {};
      });

      return {
        cells: nextCells,
        combo_overrides: nextComboOverrides,
      };
    });
  }

  function handleMatrixCellMouseDown(
    event: React.MouseEvent<HTMLDivElement>,
    handClass: string,
  ) {
    if (event.button !== 0 || !isHandClassAvailableForActiveNode(handClass)) {
      return;
    }

    if (!isRangePaintModeEnabled) {
      setSelectedHandClass(handClass);
      return;
    }

    event.preventDefault();

    lastPaintedHandClassRef.current = handClass;
    setSelectedHandClass(handClass);
    setIsMatrixPainting(true);
    applyActionToHandClass(handClass, selectedActionId);
  }

  function handleMatrixCellMouseEnter(handClass: string) {
    if (!isMatrixPainting || !isRangePaintModeEnabled) return;

    const previousHandClass = lastPaintedHandClassRef.current;
    const handClassesToPaint = previousHandClass
      ? getHandClassesBetween(previousHandClass, handClass)
      : [handClass];

    lastPaintedHandClassRef.current = handClass;
    setSelectedHandClass(handClass);
    applyActionToHandClasses(handClassesToPaint, selectedActionId);
  }

  function handleMatrixCellClick(handClass: string) {
    setSelectedHandClass(handClass);
  }

  if (isLoading) {
    return (
      <Box
        sx={{
          height: 'calc(100vh - 96px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (errorMessage && !workspace) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="error">{errorMessage}</Alert>
      </Box>
    );
  }

  if (!workspace || !activeNode) {
    return (
      <Box sx={{ p: 2 }}>
        <Alert severity="warning">No se ha encontrado información del workspace del board.</Alert>
      </Box>
    );
  }

  return (
      <Box
        sx={{
          minHeight: 'calc(100vh - 72px)',
          display: 'grid',
          gridTemplateRows: 'max-content max-content max-content',
          gap: 1,
          alignContent: 'start',
          overflowX: 'hidden',
          overflowY: 'visible',
        }}
      >
      <Card variant="outlined" sx={{ borderRadius: 2 }}>
        <CardContent sx={{ py: 0.65, px: 1.1, '&:last-child': { pb: 0.65 } }}>
          <Stack spacing={1}>
            <HandTimeline
              workspace={workspace}
              activeNode={activeNode}
              activePath={activePath}
              activeNodeId={activeNodeId}
              activeTurnNode={activeTurnNode}
              activeRiverNode={activeRiverNode}
              effectiveTurnCard={effectiveTurnCard}
              effectiveRiverCard={effectiveRiverCard}
              turnVariantNodes={turnVariantNodes}
              riverVariantNodes={riverVariantNodes}
              isWaitingForTurnCard={isWaitingForTurnCard}
              isWaitingForRiverCard={isWaitingForRiverCard}
              estimatedFlopPotBb={estimatedFlopPotBb}
              preflopRangeSummaries={preflopRangeSummaries}
              onSelectNode={(nodeId) => {
                void handleSelectNodeWithAutosave(nodeId);
              }}
              onBackToSpot={() => navigate(`/poker-study/spots/${workspace.board.spot_id}`)}
              onOpenStreetCardPicker={setStreetCardPickerOpen}
              onOpenPreflopRangeEditor={openBoardPreflopRangeEditor}
            />
          </Stack>
        </CardContent>
      </Card>

      {errorMessage ? <Alert severity="error">{errorMessage}</Alert> : null}

      <Grid container spacing={1} sx={{ minHeight: 0, overflow: 'visible' }}>
        <Grid item xs={12} lg={2} sx={{ minHeight: 0 }}>
          <Card variant="outlined" sx={{ minHeight: '100%', height: 'auto', borderRadius: 2 }}>
            <CardContent
              sx={{
                p: 1,
                '&:last-child': { pb: 1 },
                minHeight: '100%',
                overflow: 'visible',
              }}
            >
              <Stack spacing={1} sx={{ minHeight: '100%', height: 'auto' }}>
                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={950}>
                    LÍNEAS DEL BOARD
                  </Typography>

                  <Typography variant="subtitle2" fontWeight={950} noWrap>
                    Línea activa
                  </Typography>

                  <Typography variant="caption" color="text.secondary">
                    Navega ramas sin duplicar la historia superior
                  </Typography>
                </Box>

                <Divider />

                <Box
                  sx={{
                    border: '1px solid',
                    borderColor: 'primary.main',
                    borderRadius: 1.5,
                    p: 0.85,
                    bgcolor: 'rgba(76, 201, 216, 0.08)',
                  }}
                >
                  <Typography variant="caption" color="text.secondary" fontWeight={950}>
                    DECISIÓN ACTUAL
                  </Typography>

                  <Typography variant="body2" fontWeight={950} sx={{ mt: 0.35 }}>
                    {getStreetLabel(activeNode.street)} · Actúa{' '}
                    {getActorLabel(workspace, activeNode.acting_player)}
                  </Typography>

                  <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.35 }}>
                    {buildActivePathActionSummary(workspace, activePath)}
                  </Typography>
                </Box>

                <Box sx={{ minHeight: 0, overflow: 'auto', pr: 0.25 }}>
                  <Typography variant="caption" color="text.secondary" fontWeight={950}>
                    RUTA ACTIVA
                  </Typography>

                  <Stack spacing={0.65} sx={{ mt: 0.75 }}>
                    {activePath.map((node, index) => {
                      const parentNode = index > 0 ? activePath[index - 1] : null;
                      const actionStreet = parentNode ? parentNode.street : node.street;
                      const previousParentNode = index > 1 ? activePath[index - 2] : null;
                      const previousActionStreet =
                        index === 0
                          ? null
                          : previousParentNode
                            ? previousParentNode.street
                            : activePath[index - 1]?.street ?? null;

                      const streetChanged = index === 0 || actionStreet !== previousActionStreet;
                      const isActive = node.id === activeNodeId;
                      const isStreetTransition = Boolean(parentNode && node.street !== parentNode.street);

                      return (
                        <Box key={node.id}>
                          {streetChanged ? (
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              fontWeight={950}
                              sx={{ display: 'block', mt: index === 0 ? 0 : 0.8, mb: 0.35 }}
                            >
                              {getStreetLabel(actionStreet)}
                            </Typography>
                          ) : null}

                          <Button
                            fullWidth
                            size="small"
                            variant={isActive ? 'contained' : 'outlined'}
                            onClick={() => {
                              void handleSelectNodeWithAutosave(node.id);
                            }}
                            sx={{
                              justifyContent: 'flex-start',
                              textTransform: 'none',
                              fontWeight: 900,
                              minHeight: 28,
                              px: 0.85,
                            }}
                          >
                            {node.is_root
                              ? `Inicio ${getStreetLabel(node.street).toLowerCase()}`
                              : buildStoryActionLabel(workspace, node)}
                          </Button>

                          {isStreetTransition ? (
                            <Box
                              sx={{
                                mt: 0.55,
                                ml: 1,
                                borderLeft: '2px solid',
                                borderColor: 'primary.main',
                                pl: 0.85,
                              }}
                            >
                              <Typography
                                variant="caption"
                                color="text.secondary"
                                fontWeight={950}
                                sx={{ display: 'block' }}
                              >
                                {getStreetLabel(node.street)}
                              </Typography>

                              <Stack
                                direction="row"
                                spacing={0.75}
                                alignItems="center"
                                sx={{ mt: 0.35 }}
                              >
                                {node.street === 'turn' && node.turn_card ? (
                                  <PokerCard value={node.turn_card} />
                                ) : null}

                                {node.street === 'river' && node.river_card ? (
                                  <PokerCard value={node.river_card} />
                                ) : null}

                                <Typography variant="caption" color="text.secondary" fontWeight={850}>
                                  Actúa {getActorLabel(workspace, node.acting_player)}
                                </Typography>
                              </Stack>
                            </Box>
                          ) : null}
                        </Box>
                      );
                    })}
                  </Stack>
                </Box>

                <Divider />

                <Box>
                  <Typography variant="caption" color="text.secondary" fontWeight={950}>
                    ELEGIR ACCIÓN DE LA LÍNEA
                  </Typography>

                  {activeNode.actions.length > 0 ? (
                    <Stack spacing={0.55} sx={{ mt: 0.75 }}>
                      {activeNode.actions.map((action) => {
                        const assignedHandClassCount = getActionAssignedHandClassCount(
                          rangeState,
                          action.id,
                        );
                        const canChooseAction = assignedHandClassCount > 0;

                        const existingChildNode = childNodes.find(
                          (childNode) =>
                            childNode.action_taken_type === action.action_type &&
                            (childNode.action_taken_size === action.size_percentage ||
                              Number(childNode.action_taken_size) === Number(action.size_percentage)),
                        );

                        const actionLabel = formatActionLabel(
                          action.action_type,
                          action.size_percentage,
                        );

                        return (
                          <Tooltip
                            key={action.id}
                            title={
                              canChooseAction
                                ? existingChildNode
                                  ? `Abrir línea · ${assignedHandClassCount} manos pintadas`
                                  : `Crear línea · ${assignedHandClassCount} manos pintadas`
                                : 'Pinta al menos una mano para poder elegir esta acción'
                            }
                          >
                            <span>
                              <Chip
                                size="small"
                                label={
                                  canChooseAction
                                    ? `${actionLabel} · ${assignedHandClassCount}`
                                    : `${actionLabel} · sin rango`
                                }
                                variant={existingChildNode ? 'filled' : 'outlined'}
                                disabled={isCreatingChildNode || !canChooseAction}
                                onClick={() => void handleSelectOrCreateStoryAction(activeNode, action)}
                                sx={{
                                  height: 24,
                                  fontWeight: 900,
                                  justifyContent: 'flex-start',
                                  backgroundColor: canChooseAction
                                    ? getActionColorPreview(
                                        action.action_type,
                                        action.size_percentage,
                                      )
                                    : undefined,
                                  opacity: canChooseAction ? 1 : 0.55,
                                }}
                              />
                            </span>
                          </Tooltip>
                        );
                      })}
                    </Stack>
                  ) : (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: 'block', mt: 0.75 }}
                    >
                      {activeNode.node_kind === 'terminal'
                        ? 'La línea termina en este punto.'
                        : 'Pinta acciones en el 13x13 para construir ramas.'}
                    </Typography>
                  )}
                </Box>
              </Stack>
            </CardContent>
          </Card>
        </Grid>

        <Grid item xs={12} lg="auto" sx={{ minHeight: 0 }}>
          <Card
            variant="outlined"
            sx={{
              minHeight: '100%',
              height: 'auto',
              width: 640,
              maxWidth: '100%',
              borderRadius: 2,
              mx: 'auto',
            }}
          >
            <CardContent
              sx={{
                p: 1.25,
                '&:last-child': { pb: 1.25 },
                minHeight: '100%',
                display: 'grid',
                gridTemplateRows: 'auto auto max-content',
                gap: 0.85,
                overflow: 'visible',
              }}
            >
              <Stack
                direction="row"
                spacing={1}
                alignItems="flex-start"
                justifyContent="space-between"
                sx={{
                  position: 'relative',
                  minHeight: 72,
                  borderBottom: '1px solid',
                  borderColor: 'divider',
                  pb: 0.75,
                }}
              >
                <Box>
                  <Typography variant="subtitle2" fontWeight={950} lineHeight={1.1}>
                    Rango de {getActorLabel(workspace, activeNode.acting_player)} · {getStreetLabel(activeNode.street)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    {buildActivePathActionSummary(workspace, activePath)}
                  </Typography>

                  <Stack
                    direction="row"
                    spacing={0.65}
                    alignItems="center"
                    flexWrap="wrap"
                    sx={{ mt: 0.35 }}
                  >
                    <Typography
                      variant="caption"
                      color={isAvailableRangeFiltered ? 'primary.main' : 'text.secondary'}
                      sx={{ fontWeight: 900 }}
                    >
                      Combos: {availableComboCount}
                    </Typography>

                    <Chip
                      size="small"
                      variant="outlined"
                      label={
                        nodeRangeAutosaveStatus === 'saving'
                          ? 'Guardando...'
                          : nodeRangeAutosaveStatus === 'saved'
                            ? 'Guardado'
                            : nodeRangeAutosaveStatus === 'error'
                              ? 'Error'
                              : 'Autosave'
                      }
                      color={nodeRangeAutosaveStatus === 'error' ? 'error' : 'default'}
                      sx={{
                        height: 21,
                        fontSize: 10.5,
                        fontWeight: 900,
                        flexShrink: 0,
                        bgcolor:
                          nodeRangeAutosaveStatus === 'saved'
                            ? 'rgba(34, 197, 94, 0.1)'
                            : nodeRangeAutosaveStatus === 'saving'
                              ? 'rgba(76, 201, 216, 0.1)'
                              : undefined,
                        borderColor:
                          nodeRangeAutosaveStatus === 'saved'
                            ? 'rgba(34, 197, 94, 0.32)'
                            : nodeRangeAutosaveStatus === 'saving'
                              ? 'rgba(76, 201, 216, 0.32)'
                              : undefined,
                      }}
                    />
                  </Stack>
                </Box>

                <>
                  <Stack
                    spacing={0.35}
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                      position: 'absolute',
                      left: '50%',
                      top: 0,
                      transform: 'translateX(-50%)',
                      p: 0.35,
                      borderRadius: 2,
                      border: '1px solid',
                      borderColor: 'divider',
                      bgcolor: 'rgba(15,23,42,0.42)',
                      backdropFilter: 'blur(8px)',
                      zIndex: 2,
                    }}
                  >
                    <Stack direction="row" spacing={0.45} alignItems="center">
                      <Chip
                        size="small"
                        label="↖ Cursor"
                        color={!isRangePaintModeEnabled ? 'primary' : 'default'}
                        variant={!isRangePaintModeEnabled ? 'filled' : 'outlined'}
                        onClick={() => {
                          setIsRangePaintModeEnabled(false);
                          setIsMatrixPainting(false);
                        }}
                        sx={{
                          height: 28,
                          fontWeight: 950,
                          px: 0.45,
                        }}
                      />

                      <Chip
                        size="small"
                        label={`🖌 ${getBrushLabel(selectedAction, selectedActionId)}`}
                        color={isRangePaintModeEnabled ? 'primary' : 'default'}
                        variant={isRangePaintModeEnabled ? 'filled' : 'outlined'}
                        onClick={() => setIsRangePaintModeEnabled(true)}
                        sx={{
                          height: 28,
                          fontWeight: 950,
                          px: 0.45,
                        }}
                      />
                    </Stack>

                    <Button
                      size="small"
                      variant="outlined"
                      disabled={isRangeMatrixBlocked || !selectedActionId}
                      onClick={() => {
                        setIsRangePaintModeEnabled(true);
                        applyActionToAllAvailableHandClasses();
                      }}
                      sx={{
                        minHeight: 24,
                        px: 1,
                        borderRadius: 1.5,
                        fontSize: 11,
                        fontWeight: 950,
                        textTransform: 'none',
                        color: selectedAction ? '#ffffff' : '#94a3b8',
                        borderColor: selectedAction
                          ? 'rgba(255,255,255,0.28)'
                          : 'rgba(148, 163, 184, 0.24)',
                        bgcolor: selectedAction
                          ? getActionColorPreview(
                              selectedAction.action_type,
                              selectedAction.size_percentage,
                            )
                          : 'rgba(255,255,255,0.025)',
                        '&:hover': {
                          borderColor: 'rgba(255,255,255,0.42)',
                          bgcolor: selectedAction
                            ? getActionColorPreview(
                                selectedAction.action_type,
                                selectedAction.size_percentage,
                              )
                            : 'rgba(255,255,255,0.055)',
                        },
                        '&.Mui-disabled': {
                          color: 'rgba(148, 163, 184, 0.45)',
                          borderColor: 'rgba(148, 163, 184, 0.14)',
                          bgcolor: 'rgba(255,255,255,0.02)',
                        },
                      }}
                    >
                      {selectedActionId === 'clear' ? 'Limpiar todo' : 'Pintar todo'}
                    </Button>
                  </Stack>

                  <Box
                    sx={{
                      flex: 1,
                      minWidth: 0,
                      display: 'flex',
                      justifyContent: 'flex-end',
                      alignItems: 'flex-start',
                    }}
                  >
                    <Stack
                      direction="row-reverse"
                      spacing={0.55}
                      alignItems="flex-start"
                      justifyContent="flex-start"
                      sx={{ minWidth: 0 }}
                    >
                      {topBarActionColumns.map((column, columnIndex) => (
                        <Stack key={`top-action-column-${columnIndex}`} spacing={0.35}>
                          {column.map((item) => {
                            const isSelected = selectedActionId === item.id;

                            return (
                              <Chip
                                key={item.key}
                                size="small"
                                label={item.label}
                                color={isSelected ? 'primary' : 'default'}
                                variant={isSelected ? 'filled' : 'outlined'}
                                onClick={() => {
                                  setSelectedActionId(item.id);
                                  setIsRangePaintModeEnabled(true);
                                }}
                                sx={{
                                  height: 24,
                                  fontWeight: 950,
                                  backgroundColor:
                                    item.kind === 'action' && isSelected
                                      ? getActionColorPreview(
                                          item.action_type,
                                          item.size_percentage,
                                        )
                                      : undefined,
                                }}
                              />
                            );
                          })}
                        </Stack>
                      ))}
                    </Stack>
                  </Box>
                </>
              </Stack>

              {isRangeMatrixBlocked ? (
                <Box
                  sx={{
                    minHeight: 280,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    textAlign: 'center',
                    border: '1px dashed',
                    borderColor: 'divider',
                    borderRadius: 2,
                    px: 2,
                  }}
                >
                  <Box>
                    <Typography variant="subtitle2" fontWeight={950}>
                      {isWaitingForTurnCard
                        ? 'Selecciona la carta del turn'
                        : 'Selecciona la carta del river'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                      Antes de estudiar este nodo necesitas fijar la carta de la calle en la línea superior.
                    </Typography>
                  </Box>
                </Box>
              ) : isLoadingRangeState ? (
                <Box
                  sx={{
                    height: '100%',
                    minHeight: 320,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <CircularProgress size={28} />
                </Box>
              ) : (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(13, 43px)',
                    gridAutoRows: '43px',
                    gap: 0.6,
                    alignContent: 'start',
                    justifyContent: 'center',
                  }}
                >
                  {matrix.flat().map((handClass) => {
                    const isBoardImpactHighlighted = hoveredBoardImpactHandClasses.has(handClass);
                    const isSelected =
                      hoveredBoardImpactBucketKey === null &&
                      hoveredBoardDrawBucketKey === null &&
                      handClass === selectedHandClass;
                    const mixLabel = buildCellMixLabel(handClass, rangeState);
                    const cellBackgroundStyle = resolveCellBackgroundStyle(
                      handClass,
                      activeNodeActions,
                      rangeState,
                      isSelected,
                    );

                    const isHandClassAvailable = isHandClassAvailableForActiveNode(handClass);

                    return (
                      <Box
                        key={handClass}
                        onClick={() => handleMatrixCellClick(handClass)}
                        onMouseDown={(event) => handleMatrixCellMouseDown(event, handClass)}
                        onMouseEnter={() => {
                          handleMatrixCellMouseEnter(handClass);
                          setBoardImpactInspection(buildInspectionStateFromHandClass(handClass));
                        }}
                        onMouseLeave={() => {
                          setBoardImpactInspection(null);
                        }}
                        sx={{
                          width: 43,
                          minWidth: 43,
                          maxWidth: 43,
                          height: 43,
                          minHeight: 43,
                          maxHeight: 43,
                          borderRadius: isPocketPairHandClass(handClass) ? 1.6 : 1.15,
                          border: '1px solid',
                          borderColor: isBoardImpactHighlighted
                            ? 'primary.light'
                            : isSelected
                              ? 'primary.main'
                              : isPocketPairHandClass(handClass)
                                ? 'rgba(226, 232, 240, 0.34)'
                                : 'rgba(148, 163, 184, 0.18)',
                          backgroundColor: cellBackgroundStyle.backgroundColor,
                          backgroundImage: cellBackgroundStyle.backgroundImage,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          cursor: isHandClassAvailable
                            ? isRangeMatrixBlocked
                              ? 'not-allowed'
                              : isRangePaintModeEnabled
                                ? 'cell'
                                : 'pointer'
                            : 'not-allowed',
                          opacity: isHandClassAvailable ? 1 : 0.22,
                          filter: isHandClassAvailable ? 'none' : 'grayscale(0.8)',
                          px: 0.35,
                          textAlign: 'center',
                          transition:
                            'background-color 120ms ease, border-color 120ms ease, box-shadow 120ms ease, transform 120ms ease',
                          userSelect: 'none',
                          overflow: 'hidden',
                          boxSizing: 'border-box',
                          boxShadow: isBoardImpactHighlighted
                            ? '0 0 0 2px rgba(76, 201, 216, 0.72), 0 0 18px rgba(76, 201, 216, 0.28)'
                            : isSelected
                              ? '0 0 0 1px rgba(76, 201, 216, 0.25), 0 8px 18px rgba(76, 201, 216, 0.12)'
                              : isPocketPairHandClass(handClass)
                                ? 'inset 0 0 0 1px rgba(255,255,255,0.05)'
                              : 'none',
                          outline: isPocketPairHandClass(handClass)
                            ? '1px solid rgba(255,255,255,0.08)'
                            : 'none',
                          '&:hover': {
                            borderColor: isHandClassAvailable
                              ? isSelected
                                ? 'primary.main'
                                : 'rgba(76, 201, 216, 0.65)'
                              : 'rgba(148, 163, 184, 0.18)',
                            transform: isHandClassAvailable && !isRangeMatrixBlocked
                              ? 'translateY(-1px)'
                              : 'none',
                          },
                        }}
                      >
                        <Typography
                          component="span"
                          fontWeight={isPocketPairHandClass(handClass) ? 1000 : 900}
                          sx={{
                            fontSize: 12.5,
                            lineHeight: 1.05,
                            color: 'rgba(255,255,255,0.92)',
                          }}
                        >
                          {handClass}
                        </Typography>

                        {mixLabel ? (
                          <Typography
                            component="span"
                            sx={{
                              fontSize: 9,
                              lineHeight: 1,
                              opacity: 0.88,
                              mt: 0.15,
                              color: 'rgba(255,255,255,0.82)',
                            }}
                          >
                            {mixLabel}
                          </Typography>
                        ) : null}
                      </Box>
                    );
                  })}
                </Box>
              )}

              <Box
                sx={{
                  borderTop: '1px solid',
                  borderColor: 'divider',
                  pt: 0.9,
                  pb: 0.35,
                }}
              >
                <Stack spacing={0.85}>
                  <Stack
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={0.75}
                    justifyContent="space-between"
                    alignItems={{ xs: 'flex-start', sm: 'center' }}
                  >
                    <Box>
                      <Typography
                        sx={{
                          color: '#94a3b8',
                          fontSize: 11,
                          fontWeight: 950,
                          letterSpacing: 0.65,
                          lineHeight: 1.1,
                          textTransform: 'uppercase',
                        }}
                      >
                        Atajos de pintura
                      </Typography>

                      <Typography
                        sx={{
                          color: '#cbd5e1',
                          fontSize: 12,
                          fontWeight: 800,
                          mt: 0.35,
                        }}
                      >
                        Pincel activo · {getBrushLabel(selectedAction, selectedActionId)}
                        {selectedAction ? ` · ${selectedActionAssignedHandClassCount} manos pintadas` : ''}
                      </Typography>
                    </Box>

                    <Chip
                      size="small"
                      label="Selecciona una acción para pintar"
                      variant="outlined"
                      sx={{
                        height: 24,
                        fontSize: 11,
                        fontWeight: 900,
                        color: '#94a3b8',
                        borderColor: 'rgba(148, 163, 184, 0.22)',
                      }}
                    />
                  </Stack>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: 'repeat(2, minmax(0, 1fr))',
                        sm: 'repeat(3, minmax(0, 1fr))',
                        md: 'repeat(6, minmax(0, 1fr))',
                      },
                      gap: 0.65,
                    }}
                  >
                    {quickActionFamilies.map((family) => {
                      const isSelected =
                        family === 'clear'
                          ? selectedActionId === 'clear'
                          : selectedAction?.action_type === family;
                      const familyColor = getQuickActionFamilyColor(family);

                      return (
                        <Button
                          key={family}
                          variant={isSelected ? 'contained' : 'outlined'}
                          disabled={isCreatingAction}
                          onClick={() => void handleQuickActionFamilyClick(family)}
                          sx={{
                            minHeight: 38,
                            borderRadius: 2,
                            px: 1.15,
                            justifyContent: 'center',
                            textTransform: 'none',
                            fontSize: 13,
                            fontWeight: 950,
                            color: isSelected ? '#020617' : '#f8fafc',
                            bgcolor: isSelected ? familyColor : 'rgba(15, 23, 42, 0.34)',
                            borderColor: family === 'clear'
                              ? 'rgba(148, 163, 184, 0.32)'
                              : familyColor,
                            boxShadow: isSelected
                              ? `0 10px 22px ${alpha(familyColor, 0.18)}`
                              : 'none',
                            '&:hover': {
                              bgcolor: isSelected ? familyColor : alpha(familyColor, 0.16),
                              borderColor: familyColor,
                            },
                          }}
                        >
                          {formatQuickActionFamilyLabel(family)}
                        </Button>
                      );
                    })}
                  </Box>
                </Stack>
              </Box>

              <Dialog
                open={Boolean(quickActionModalFamily)}
                onClose={() => setQuickActionModalFamily(null)}
                maxWidth="sm"
                fullWidth
                PaperProps={{
                  sx: {
                    borderRadius: 3,
                    bgcolor: '#0f172a',
                    border: '1px solid rgba(148, 163, 184, 0.22)',
                    boxShadow: '0 28px 80px rgba(0,0,0,0.52)',
                  },
                }}
              >
                <DialogTitle sx={{ pb: 1.2 }}>
                  <Typography
                    sx={{
                      color: '#f8fafc',
                      fontSize: 20,
                      fontWeight: 950,
                      lineHeight: 1.1,
                    }}
                  >
                    {quickActionModalFamily
                      ? `Seleccionar ${formatQuickActionFamilyLabel(quickActionModalFamily)}`
                      : 'Seleccionar acción'}
                  </Typography>

                  <Typography
                    sx={{
                      color: '#94a3b8',
                      fontSize: 13,
                      fontWeight: 750,
                      mt: 0.6,
                    }}
                  >
                    Elige un tamaño para activar el pincel. Después podrás pintar manualmente o usar Pintar todo.
                  </Typography>
                </DialogTitle>

                <DialogContent sx={{ pt: 1 }}>
                  <Stack spacing={1.35}>
                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: {
                          xs: 'repeat(2, minmax(0, 1fr))',
                          sm: 'repeat(3, minmax(0, 1fr))',
                        },
                        gap: 0.75,
                      }}
                    >
                      {quickActionModalPresets.map((preset) => {
                        const matchingAction = findMatchingAction(activeNodeActions, preset);
                        const isSelected = matchingAction?.id === selectedActionId;
                        const previewColor = getActionColorPreview(
                          preset.action_type,
                          preset.size_percentage,
                        );

                        return (
                          <Button
                            key={`${preset.action_type}-${preset.size_percentage ?? 'none'}`}
                            variant={isSelected ? 'contained' : 'outlined'}
                            disabled={isCreatingAction}
                            onClick={async () => {
                              await handleEnsureAction(preset);
                              setIsRangePaintModeEnabled(true);
                              setQuickActionModalFamily(null);
                            }}
                            sx={{
                              minHeight: 48,
                              borderRadius: 2,
                              px: 1.25,
                              textTransform: 'none',
                              fontSize: 14,
                              fontWeight: 950,
                              color: isSelected ? '#020617' : '#f8fafc',
                              bgcolor: isSelected ? previewColor : alpha(previewColor, 0.12),
                              borderColor: alpha(previewColor, 0.45),
                              '&:hover': {
                                bgcolor: isSelected ? previewColor : alpha(previewColor, 0.2),
                                borderColor: alpha(previewColor, 0.72),
                              },
                            }}
                          >
                            {preset.label}
                          </Button>
                        );
                      })}
                    </Box>

                    {quickActionModalFamily === 'bet' || quickActionModalFamily === 'raise' ? (
                      <Box
                        sx={{
                          borderRadius: 2.5,
                          border: '1px solid rgba(148, 163, 184, 0.16)',
                          bgcolor: 'rgba(15, 23, 42, 0.38)',
                          p: 1.2,
                        }}
                      >
                        <Typography
                          sx={{
                            color: '#cbd5e1',
                            fontSize: 12,
                            fontWeight: 950,
                            letterSpacing: 0.55,
                            textTransform: 'uppercase',
                            mb: 0.85,
                          }}
                        >
                          Tamaño custom
                        </Typography>

                        <Stack
                          direction={{ xs: 'column', sm: 'row' }}
                          spacing={0.75}
                          alignItems="stretch"
                        >
                          <TextField
                            select
                            size="small"
                            label="Tipo"
                            value={customActionType}
                            onChange={(event) =>
                              setCustomActionType(event.target.value as 'bet' | 'raise')
                            }
                            sx={{ minWidth: 115 }}
                          >
                            <MenuItem value="bet">Bet</MenuItem>
                            <MenuItem value="raise">Raise</MenuItem>
                          </TextField>

                          <TextField
                            size="small"
                            label="Tamaño"
                            value={customActionSize}
                            onChange={(event) => setCustomActionSize(event.target.value)}
                            sx={{ minWidth: 110 }}
                            inputProps={{ inputMode: 'numeric' }}
                          />

                          <Button
                            variant="contained"
                            disabled={isCreatingAction}
                            onClick={async () => {
                              await handleCreateCustomAction();
                              setIsRangePaintModeEnabled(true);
                              setQuickActionModalFamily(null);
                            }}
                            sx={{
                              minHeight: 40,
                              borderRadius: 2,
                              px: 1.5,
                              fontWeight: 950,
                              textTransform: 'none',
                              boxShadow: 'none',
                            }}
                          >
                            Activar tamaño
                          </Button>
                        </Stack>
                      </Box>
                    ) : null}
                  </Stack>
                </DialogContent>

                <DialogActions sx={{ px: 3, pb: 2.2 }}>
                  <Button
                    variant="outlined"
                    onClick={() => setQuickActionModalFamily(null)}
                    sx={{
                      borderRadius: 2,
                      fontWeight: 900,
                      textTransform: 'none',
                    }}
                  >
                    Cerrar
                  </Button>
                </DialogActions>
              </Dialog>
            </CardContent>
          </Card>
        </Grid>

        <Grid
          item
          xs={12}
          sx={{
            minHeight: 0,
            flex: { lg: '1 1 0' },
            minWidth: { lg: 0 },
          }}
        >
          <Stack spacing={1} sx={{ minHeight: '100%', height: 'auto' }}>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: {
                  xs: '1fr',
                  xl: 'minmax(0, 1fr) minmax(0, 1fr)',
                },
                gap: 0.85,
                alignItems: 'stretch',
                width: '100%',
                minWidth: 0,
              }}
            >
              <Card
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  minWidth: 0,
                  height: 360,
                  borderColor: 'rgba(76, 201, 216, 0.28)',
                  bgcolor: 'rgba(76, 201, 216, 0.045)',
                  overflow: 'hidden',
                }}
              >
                <CardContent
                  sx={{
                    p: 0.75,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:last-child': { pb: 0.75 },
                  }}
                >
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    spacing={1}
                    sx={{ mb: 0.25 }}
                  >
                    <Stack spacing={0.1} sx={{ minWidth: 0 }}>
                      <Typography variant="caption" color="text.secondary" fontWeight={950}>
                        IMPACTO DEL BOARD
                      </Typography>

                      <Typography variant="caption" color="text.secondary" noWrap>
                        Rango pintado en este nodo
                      </Typography>
                    </Stack>

                    <Chip
                      size="small"
                      label={`${boardImpact?.totalCombos ?? 0} combos`}
                      color={(boardImpact?.totalCombos ?? 0) > 0 ? 'primary' : 'default'}
                      variant="outlined"
                      sx={{ height: 22, fontWeight: 900, flexShrink: 0 }}
                    />
                  </Stack>

                  <Stack
                    direction="row"
                    spacing={0.5}
                    flexWrap="wrap"
                    useFlexGap
                    sx={{ mb: 0.3 }}
                  >
                    {boardImpactActionFilterOptions.map((option) => {
                      const isActive = boardImpactActionFilter === option.id;

                      return (
                        <Chip
                          key={option.id}
                          size="small"
                          label={`${option.label} · ${option.comboCount}`}
                          color={isActive ? 'primary' : 'default'}
                          variant={isActive ? 'filled' : 'outlined'}
                          onClick={() => {
                            setBoardImpactActionFilter(option.id);
                            setHoveredBoardImpactBucketKey(null);
                            setHoveredBoardDrawBucketKey(null);
                            setBoardImpactInspection(null);
                          }}
                          sx={{
                            height: 18,
                            fontWeight: 900,
                            fontSize: 9.5,
                          }}
                        />
                      );
                    })}
                  </Stack>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: {
                        xs: '1fr',
                        md: 'minmax(0, 1fr) minmax(0, 1fr)',
                      },
                      gap: 0.7,
                      alignItems: 'start',
                      flex: 1,
                      minHeight: 0,
                    }}
                  >
                    <Box sx={{ minWidth: 0, minHeight: 0 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={950}
                        sx={{ display: 'block', mb: 0.35, fontSize: 10.8 }}
                      >
                        MADE HANDS
                      </Typography>

                      {visibleBoardImpactBuckets.length > 0 ? (
                        <Stack spacing={0.14}>
                          {visibleBoardImpactBuckets.map((bucket) => {
                            const isInspected = Boolean(
                              boardImpactInspection?.madeHandKeys.has(bucket.key),
                            );

                            return (
                              <Box
                                key={bucket.key}
                                onMouseEnter={() => setHoveredBoardImpactBucketKey(bucket.key)}
                                onMouseLeave={() => setHoveredBoardImpactBucketKey(null)}
                                sx={{
                                  px: 0.5,
                                  py: 0.38,
                                  borderRadius: 1.15,
                                  cursor: 'default',
                                  bgcolor:
                                    hoveredBoardImpactBucketKey === bucket.key || isInspected
                                      ? 'rgba(76, 201, 216, 0.12)'
                                      : 'transparent',
                                  border: isInspected
                                    ? '1px solid rgba(76, 201, 216, 0.38)'
                                    : '1px solid transparent',
                                }}
                              >
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  spacing={1}
                                  sx={{ mb: 0.08 }}
                                >
                                  <Typography
                                    variant="caption"
                                    fontWeight={900}
                                    noWrap
                                    sx={{ minWidth: 0, fontSize: 11.2, lineHeight: 1.08 }}
                                  >
                                    {bucket.label}
                                  </Typography>

                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight={850}
                                    sx={{ flexShrink: 0, fontSize: 11.2, lineHeight: 1.08 }}
                                  >
                                    {bucket.combos} · {formatBoardImpactPercentage(bucket.percentage)}
                                  </Typography>
                                </Stack>

                                <Box
                                  sx={{
                                    height: 4,
                                    borderRadius: 999,
                                    bgcolor: 'rgba(148, 163, 184, 0.16)',
                                    overflow: 'hidden',
                                  }}
                                >
                                  {boardImpactActionFilter === 'all' &&
                                  (boardImpactActionSegmentsByBucket[bucket.key]?.length ?? 0) > 0 ? (
                                    <Stack
                                      direction="row"
                                      sx={{
                                        height: '100%',
                                        width: `${Math.max(2, bucket.percentage)}%`,
                                        maxWidth: '100%',
                                        borderRadius: 999,
                                        overflow: 'hidden',
                                      }}
                                    >
                                      {boardImpactActionSegmentsByBucket[bucket.key].map(
                                        (segment) => (
                                          <Tooltip
                                            key={segment.actionId}
                                            title={`${segment.label}: ${segment.combos} combos · ${formatBoardImpactPercentage(
                                              segment.percentage,
                                            )}`}
                                            arrow
                                          >
                                            <Box
                                              sx={{
                                                height: '100%',
                                                width: `${segment.percentage}%`,
                                                bgcolor: segment.color,
                                                opacity:
                                                  hoveredBoardImpactBucketKey === bucket.key
                                                    ? 0.95
                                                    : 0.78,
                                                minWidth: segment.percentage > 0 ? 2 : 0,
                                              }}
                                            />
                                          </Tooltip>
                                        ),
                                      )}
                                    </Stack>
                                  ) : (
                                    <Box
                                      sx={{
                                        height: '100%',
                                        width: `${Math.max(2, bucket.percentage)}%`,
                                        maxWidth: '100%',
                                        borderRadius: 999,
                                        bgcolor:
                                          hoveredBoardImpactBucketKey === bucket.key
                                            ? 'primary.light'
                                            : 'primary.main',
                                        opacity: 0.86,
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                        </Stack>
                      ) : (
                        <Box
                          sx={{
                            borderRadius: 1.5,
                            border: '1px dashed',
                            borderColor: 'divider',
                            px: 1,
                            py: 0.85,
                            bgcolor: 'rgba(255,255,255,0.025)',
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            Pinta combos en la matriz para ver la composición del rango.
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <Box sx={{ minWidth: 0, minHeight: 0 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={950}
                        sx={{ display: 'block', mb: 0.35, fontSize: 10.8 }}
                      >
                        DRAWS
                      </Typography>

                      {visibleBoardDrawBuckets.length > 0 ? (
                        <Stack spacing={0.14}>
                          {visibleBoardDrawBuckets.map((bucket) => {
                            const isInspected = Boolean(
                              boardImpactInspection?.drawKeys.has(bucket.key),
                            );

                            return (
                              <Box
                                key={bucket.key}
                                onMouseEnter={() => {
                                  setHoveredBoardImpactBucketKey(null);
                                  setHoveredBoardDrawBucketKey(bucket.key);
                                }}
                                onMouseLeave={() => setHoveredBoardDrawBucketKey(null)}
                                sx={{
                                  px: 0.45,
                                  py: 0.1,
                                  borderRadius: 0.95,
                                  cursor: 'default',
                                  bgcolor:
                                    hoveredBoardDrawBucketKey === bucket.key || isInspected
                                      ? 'rgba(76, 201, 216, 0.12)'
                                      : 'transparent',
                                  border: isInspected
                                    ? '1px solid rgba(76, 201, 216, 0.38)'
                                    : '1px solid transparent',
                                }}
                              >
                                <Stack
                                  direction="row"
                                  justifyContent="space-between"
                                  alignItems="center"
                                  spacing={1}
                                  sx={{ mb: 0.08 }}
                                >
                                  <Typography
                                    variant="caption"
                                    fontWeight={900}
                                    noWrap
                                    sx={{ minWidth: 0, fontSize: 11.2, lineHeight: 1.08 }}
                                  >
                                    {bucket.label}
                                  </Typography>

                                  <Typography
                                    variant="caption"
                                    color="text.secondary"
                                    fontWeight={850}
                                    sx={{ flexShrink: 0, fontSize: 11.2, lineHeight: 1.08 }}
                                  >
                                    {bucket.combos} · {formatBoardImpactPercentage(bucket.percentage)}
                                  </Typography>
                                </Stack>

                                <Box
                                  sx={{
                                    height: 4,
                                    borderRadius: 999,
                                    bgcolor: 'rgba(148, 163, 184, 0.16)',
                                    overflow: 'hidden',
                                  }}
                                >
                                  {boardImpactActionFilter === 'all' &&
                                  (boardImpactActionSegmentsByDrawBucket[bucket.key]?.length ?? 0) >
                                    0 ? (
                                    <Stack
                                      direction="row"
                                      sx={{
                                        height: '100%',
                                        width: `${Math.max(2, bucket.percentage)}%`,
                                        maxWidth: '100%',
                                        borderRadius: 999,
                                        overflow: 'hidden',
                                      }}
                                    >
                                      {boardImpactActionSegmentsByDrawBucket[bucket.key].map(
                                        (segment) => (
                                          <Tooltip
                                            key={segment.actionId}
                                            title={`${segment.label}: ${segment.combos} combos · ${formatBoardImpactPercentage(
                                              segment.percentage,
                                            )}`}
                                            arrow
                                          >
                                            <Box
                                              sx={{
                                                height: '100%',
                                                width: `${segment.percentage}%`,
                                                bgcolor: segment.color,
                                                opacity:
                                                  hoveredBoardDrawBucketKey === bucket.key
                                                    ? 0.95
                                                    : 0.78,
                                                minWidth: segment.percentage > 0 ? 2 : 0,
                                              }}
                                            />
                                          </Tooltip>
                                        ),
                                      )}
                                    </Stack>
                                  ) : (
                                    <Box
                                      sx={{
                                        height: '100%',
                                        width: `${Math.max(2, bucket.percentage)}%`,
                                        maxWidth: '100%',
                                        borderRadius: 999,
                                        bgcolor:
                                          hoveredBoardDrawBucketKey === bucket.key
                                            ? 'primary.light'
                                            : 'primary.main',
                                        opacity: 0.86,
                                      }}
                                    />
                                  )}
                                </Box>
                              </Box>
                            );
                          })}
                        </Stack>
                      ) : (
                        <Box
                          sx={{
                            borderRadius: 1.5,
                            border: '1px dashed',
                            borderColor: 'divider',
                            px: 1,
                            py: 0.85,
                            bgcolor: 'rgba(255,255,255,0.025)',
                          }}
                        >
                          <Typography variant="caption" color="text.secondary">
                            No hay draws relevantes en el rango filtrado.
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  </Box>
                </CardContent>
              </Card>

              <Card
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  minWidth: 0,
                  height: 360,
                  overflow: 'hidden',
                }}
              >
                <CardContent
                  sx={{
                    p: 0.75,
                    height: '100%',
                    display: 'flex',
                    flexDirection: 'column',
                    '&:last-child': { pb: 0.75 },
                  }}
                >
                  <Stack spacing={0.65} sx={{ height: '100%', minHeight: 0 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="stretch"
                      spacing={1}
                      sx={{ minWidth: 0, position: 'relative' }}
                    >
                      <Stack spacing={0.35} sx={{ minWidth: 0, flex: 1, pr: 18 }}>
                        <Stack
                          direction="row"
                          spacing={0.65}
                          alignItems="baseline"
                          sx={{ minWidth: 0 }}
                        >
                          <Typography variant="caption" color="text.secondary" fontWeight={950}>
                            COMBOS
                          </Typography>

                          <Typography variant="subtitle1" fontWeight={950} lineHeight={1}>
                            {selectedHandClass}
                          </Typography>
                        </Stack>

                        <Stack
                          direction="row"
                          spacing={0.55}
                          alignItems="center"
                          sx={{ minWidth: 0 }}
                        >
                          <Chip
                            size="small"
                            label={`Pincel: ${getBrushLabel(selectedAction, selectedActionId)}`}
                            color={selectedActionId ? 'primary' : 'default'}
                            variant="outlined"
                            sx={{ height: 21, fontWeight: 900, maxWidth: 146 }}
                          />

                          <Typography
                            variant="caption"
                            color="text.secondary"
                            noWrap
                            sx={{ minWidth: 0 }}
                          >
                            Base:{' '}
                            {selectedHandAssignedAction
                              ? formatActionLabel(
                                  selectedHandAssignedAction.action_type,
                                  selectedHandAssignedAction.size_percentage,
                                )
                              : 'Sin asignar'}
                          </Typography>
                        </Stack>
                      </Stack>

                      <Chip
                        size="small"
                        label={`${selectedHandAvailableCombos.length}/${theoreticalCombos.length} vivos`}
                        color={selectedHandBlockedComboCount > 0 ? 'warning' : 'default'}
                        sx={{
                          height: 21,
                          fontWeight: 900,
                          flexShrink: 0,
                          position: 'relative',
                          zIndex: 2,
                        }}
                      />

                      <Box
                        onMouseLeave={() => setHoveredComboSuit(null)}
                        sx={{
                          position: 'absolute',
                          left: '50%',
                          top: '50%',
                          transform: 'translate(-50%, -50%)',
                          display: 'flex',
                          alignItems: 'stretch',
                          justifyContent: 'center',
                          borderRadius: 2,
                          overflow: 'hidden',
                          border: '1px solid rgba(255, 255, 255, 0.18)',
                          bgcolor: 'rgba(15, 23, 42, 0.42)',
                          boxShadow: hoveredComboSuit
                            ? '0 0 0 1px rgba(76, 201, 216, 0.22), 0 10px 24px rgba(0,0,0,0.22)'
                            : '0 8px 18px rgba(0,0,0,0.18)',
                          zIndex: 3,
                        }}
                      >
                        {CARD_SUITS.map((suit) => {
                          const meta = SUIT_META[suit];
                          const isActive = hoveredComboSuit === suit;

                          return (
                            <Box
                              key={suit}
                              onMouseEnter={() => setHoveredComboSuit(suit)}
                              sx={{
                                width: 32,
                                height: 42,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                cursor: 'default',
                                bgcolor: meta.color,
                                borderRight:
                                  suit === CARD_SUITS[CARD_SUITS.length - 1]
                                    ? 'none'
                                    : '1px solid rgba(255, 255, 255, 0.22)',
                                boxShadow: isActive
                                  ? 'inset 0 0 0 2px rgba(255,255,255,0.72)'
                                  : 'inset 0 0 0 1px rgba(255,255,255,0.18)',
                                filter: isActive ? 'brightness(1.12)' : 'none',
                                transition: 'box-shadow 120ms ease, filter 120ms ease',
                              }}
                            >
                              <Typography
                                component="span"
                                sx={{
                                  fontSize: 20,
                                  fontWeight: 950,
                                  lineHeight: 1,
                                  color: '#ffffff',
                                  textShadow: '0 1px 3px rgba(0,0,0,0.35)',
                                }}
                              >
                                {meta.symbol}
                              </Typography>
                            </Box>
                          );
                        })}
                      </Box>
                    </Stack>

                    {(() => {
                      const getComboGroupMeta = (
                        item: (typeof selectedHandSortedComboItems)[number],
                      ) => {
                        const action = item.comboAssignedAction;
                        const actionType = (action?.action_type || '').toLowerCase();
                        const actionSize =
                          typeof action?.size_percentage === 'number'
                            ? action.size_percentage
                            : Number(action?.size_percentage ?? 0);
                        const actionColor = getActionColorPreview(
                          actionType,
                          action?.size_percentage ?? null,
                        );

                        if (item.isBlockedCombo) {
                          return {
                            sortBucket: 4,
                            sizeRank: 0,
                            groupKey: 'blocked',
                            label: 'Blocked',
                            color: 'rgba(148,163,184,0.62)',
                          };
                        }

                        if (actionType === 'raise') {
                          return {
                            sortBucket: 0,
                            sizeRank: -actionSize,
                            groupKey: `raise-${actionSize}`,
                            label: `R${actionSize}`,
                            color: actionColor,
                          };
                        }

                        if (actionType === 'bet') {
                          return {
                            sortBucket: 0,
                            sizeRank: -actionSize,
                            groupKey: `bet-${actionSize}`,
                            label: `B${actionSize}`,
                            color: actionColor,
                          };
                        }

                        if (actionType === 'call') {
                          return {
                            sortBucket: 1,
                            sizeRank: 0,
                            groupKey: 'call',
                            label: 'Call',
                            color: actionColor,
                          };
                        }

                        if (actionType === 'check') {
                          return {
                            sortBucket: 1,
                            sizeRank: 0,
                            groupKey: 'check',
                            label: 'Check',
                            color: actionColor,
                          };
                        }

                        if (actionType === 'fold') {
                          return {
                            sortBucket: 2,
                            sizeRank: 0,
                            groupKey: 'fold',
                            label: 'Fold',
                            color: actionColor,
                          };
                        }

                        return {
                          sortBucket: 3,
                          sizeRank: 0,
                          groupKey: 'unassigned',
                          label: '—',
                          color: 'rgba(148,163,184,0.72)',
                        };
                      };

                      const orderedItems = [...selectedHandSortedComboItems].sort((left, right) => {
                        const leftMeta = getComboGroupMeta(left);
                        const rightMeta = getComboGroupMeta(right);

                        if (leftMeta.sortBucket !== rightMeta.sortBucket) {
                          return leftMeta.sortBucket - rightMeta.sortBucket;
                        }

                        if (leftMeta.sizeRank !== rightMeta.sizeRank) {
                          return leftMeta.sizeRank - rightMeta.sizeRank;
                        }

                        return left.combo.localeCompare(right.combo);
                      });

                      const rows: Array<{
                        rowKey: string;
                        items: typeof orderedItems;
                        groups: Array<{
                          key: string;
                          label: string;
                          color: string;
                          start: number;
                          span: number;
                        }>;
                      }> = [];

                      for (let index = 0; index < orderedItems.length; index += 4) {
                        const rowItems = orderedItems.slice(index, index + 4);
                        const groups: Array<{
                          key: string;
                          label: string;
                          color: string;
                          start: number;
                          span: number;
                        }> = [];

                        rowItems.forEach((item, itemIndex) => {
                          const meta = getComboGroupMeta(item);
                          const previousGroup = groups[groups.length - 1];

                          if (previousGroup && previousGroup.key === meta.groupKey) {
                            previousGroup.span += 1;
                            return;
                          }

                          groups.push({
                            key: meta.groupKey,
                            label: meta.label,
                            color: meta.color,
                            start: itemIndex,
                            span: 1,
                          });
                        });

                        rows.push({
                          rowKey: `row-${index}`,
                          items: rowItems,
                          groups,
                        });
                      }

                      return (
                        <Stack
                          spacing={0.35}
                          sx={{
                            flex: 1,
                            minHeight: 0,
                            justifyContent: 'flex-start',
                            overflow: 'hidden',
                            pt: 0.15,
                            pb: 0,
                          }}
                        >
                          {rows.map((row) => (
                            <Box key={row.rowKey} sx={{ minWidth: 0 }}>
                              <Box
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: {
                                    xs: 'repeat(4, 74px)',
                                    sm: 'repeat(4, 88px)',
                                    xl: 'repeat(4, 96px)',
                                  },
                                  columnGap: {
                                    xs: '7px',
                                    sm: '8px',
                                    xl: '9px',
                                  },
                                  alignItems: 'end',
                                  mb: 0.05,
                                  minHeight: 14,
                                }}
                              >
                                {row.groups.map((group, groupIndex) => (
                                  <Box
                                    key={`${row.rowKey}-${group.key}-${groupIndex}`}
                                    sx={{
                                      gridColumn: `${group.start + 1} / span ${group.span}`,
                                      minWidth: 0,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        position: 'relative',
                                        height: 14,
                                        display: 'flex',
                                        justifyContent: 'center',
                                        alignItems: 'flex-start',
                                      }}
                                    >
                                      <Typography
                                        variant="caption"
                                        sx={{
                                          position: 'relative',
                                          zIndex: 1,
                                          px: 0.45,
                                          fontSize: 9.5,
                                          fontWeight: 950,
                                          lineHeight: 1,
                                          color: group.color,
                                          bgcolor: '#18263b',
                                        }}
                                      >
                                        {group.label}
                                      </Typography>

                                      <Box
                                        sx={{
                                          position: 'absolute',
                                          left: 3,
                                          right: 3,
                                          top: 7,
                                          height: 6,
                                          borderTop: '2px solid',
                                          borderLeft: '2px solid',
                                          borderRight: '2px solid',
                                          borderColor: group.color,
                                          borderTopLeftRadius: 8,
                                          borderTopRightRadius: 8,
                                          opacity: 0.95,
                                        }}
                                      />
                                    </Box>
                                  </Box>
                                ))}
                              </Box>

                              <Box
                                sx={{
                                  display: 'grid',
                                  gridTemplateColumns: {
                                    xs: 'repeat(4, 74px)',
                                    sm: 'repeat(4, 88px)',
                                    xl: 'repeat(4, 96px)',
                                  },
                                  columnGap: {
                                    xs: '7px',
                                    sm: '8px',
                                    xl: '9px',
                                  },
                                  justifyContent: 'flex-start',
                                  alignItems: 'center',
                                }}
                              >
                                {row.items.map((item) => {
                                  const firstCard = item.combo.slice(0, 2);
                                  const secondCard = item.combo.slice(2, 4);

                                  const actionColor = item.comboAssignedAction
                                    ? getActionColorPreview(
                                        item.comboAssignedAction.action_type,
                                        item.comboAssignedAction.size_percentage,
                                      )
                                    : 'rgba(148,163,184,0.55)';

                                  return (
                                    <Tooltip
                                      key={item.combo}
                                      title={
                                        item.isBlockedCombo
                                          ? `${item.combo} bloqueado por cartas del board/runout`
                                          : item.comboAssignedAction
                                          ? `${item.combo} · ${formatActionLabel(
                                              item.comboAssignedAction.action_type,
                                              item.comboAssignedAction.size_percentage,
                                            )}`
                                          : `${item.combo} · Sin asignar`
                                      }
                                      arrow
                                    >
                                      <Box
                                        onClick={() => handleComboClick(item.combo)}
                                        onMouseEnter={() => {
                                          if (!item.isBlockedCombo) {
                                            setBoardImpactInspection(
                                              buildInspectionStateFromCombo(item.combo),
                                            );
                                          }
                                        }}
                                        onMouseLeave={() => {
                                          setBoardImpactInspection(null);
                                        }}
                                        sx={{
                                          width: {
                                            xs: 74,
                                            sm: 88,
                                            xl: 96,
                                          },
                                          height: {
                                            xs: 54,
                                            sm: 64,
                                            xl: 70,
                                          },
                                          borderRadius: 1.25,
                                          border: '4px solid',
                                          borderColor: item.isBlockedCombo
                                            ? 'rgba(148,163,184,0.38)'
                                            : actionColor,
                                          bgcolor: item.isBlockedCombo
                                            ? 'rgba(148,163,184,0.06)'
                                            : item.comboAssignedAction
                                            ? `${actionColor}1F`
                                            : 'rgba(15,23,42,0.18)',
                                          display: 'flex',
                                          alignItems: 'center',
                                          justifyContent: 'center',
                                          cursor: item.isBlockedCombo ? 'not-allowed' : 'pointer',
                                          opacity: item.isBlockedCombo ? 0.32 : 1,
                                          filter: item.isBlockedCombo ? 'grayscale(1)' : 'none',
                                          boxSizing: 'border-box',
                                          overflow: 'hidden',
                                          transition:
                                            'transform 120ms ease, border-color 120ms ease, background-color 120ms ease',
                                          '&:hover': item.isBlockedCombo
                                            ? undefined
                                            : {
                                                transform: 'translateY(-2px)',
                                                bgcolor: item.comboAssignedAction
                                                  ? `${actionColor}30`
                                                  : 'rgba(148,163,184,0.12)',
                                              },
                                        }}
                                      >
                                        <Box
                                          sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center',
                                            gap: 0.04,
                                            transform: {
                                              xs: 'scale(0.9)',
                                              sm: 'scale(1.08)',
                                              xl: 'scale(1.22)',
                                            },
                                            transformOrigin: 'center',
                                          }}
                                        >
                                          {[firstCard, secondCard].map((card) => {
                                            const isSuitHighlighted = Boolean(
                                              hoveredComboSuit && card.endsWith(hoveredComboSuit),
                                            );

                                            return (
                                              <Box
                                                key={card}
                                                sx={{
                                                  borderRadius: 1.2,
                                                  boxShadow: isSuitHighlighted
                                                    ? '0 0 0 2px rgba(255,255,255,0.95), 0 0 14px rgba(76, 201, 216, 0.62)'
                                                    : 'none',
                                                  transform: isSuitHighlighted
                                                    ? 'translateY(-1px) scale(1.04)'
                                                    : 'none',
                                                  transition:
                                                    'box-shadow 120ms ease, transform 120ms ease',
                                                }}
                                              >
                                                <MiniPokerCard value={card} />
                                              </Box>
                                            );
                                          })}
                                        </Box>
                                      </Box>
                                    </Tooltip>
                                  );
                                })}
                              </Box>
                            </Box>
                          ))}
                        </Stack>
                      );
                    })()}
                  </Stack>
                </CardContent>
              </Card>
            </Box>

            <Card variant="outlined" sx={{ borderRadius: 2, flex: 1, minHeight: 0 }}>
              <CardContent
                sx={{
                  p: 1.1,
                  '&:last-child': { pb: 1.1 },
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  minHeight: 0,
                }}
              >
                <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 0.85 }}>
                  <Stack direction="row" spacing={0.75} alignItems="center" sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={950}>
                      APUNTES DE LA RAMA
                    </Typography>

                    <Button
                      size="small"
                      variant="outlined"
                      onClick={() => setIsSpotTheoryDialogOpen(true)}
                      disabled={isLoadingSpotTheory}
                      sx={{
                        minHeight: 24,
                        px: 1,
                        borderRadius: 1.5,
                        fontSize: 11,
                        fontWeight: 950,
                        textTransform: 'none',
                        color: '#67e8f9',
                        borderColor: 'rgba(76, 201, 216, 0.32)',
                        bgcolor: 'rgba(76, 201, 216, 0.06)',
                        '&:hover': {
                          borderColor: 'rgba(76, 201, 216, 0.52)',
                          bgcolor: 'rgba(76, 201, 216, 0.12)',
                        },
                      }}
                    >
                      Teoría del spot
                    </Button>

                    <Typography variant="caption" color="text.secondary" noWrap>
                      Nota propia por punto del árbol
                    </Typography>
                  </Stack>

                  <Chip
                    size="small"
                    label={
                      nodeNotesAutosaveStatus === 'saving'
                        ? 'Guardando...'
                        : nodeNotesAutosaveStatus === 'saved'
                        ? 'Guardado'
                        : nodeNotesAutosaveStatus === 'error'
                        ? 'Error'
                        : 'Autosave'
                    }
                    color={
                      nodeNotesAutosaveStatus === 'error'
                        ? 'error'
                        : nodeNotesAutosaveStatus === 'saved'
                        ? 'success'
                        : 'default'
                    }
                    sx={{ fontWeight: 900, flexShrink: 0 }}
                  />
                </Stack>

                <Stack spacing={0.85} sx={{ flex: 1, minHeight: 0, overflow: 'visible' }}>
                  <Box>
                    <Typography variant="caption" color="text.secondary" fontWeight={950}>
                      APUNTES ANTERIORES
                    </Typography>

                    <Stack spacing={0.45} sx={{ mt: 0.5 }}>
                      {previousPathNodes.length === 0 ? (
                        <Typography variant="caption" color="text.secondary">
                          Sin pasos anteriores en esta rama.
                        </Typography>
                      ) : (
                        previousPathNodes.map((node) => {
                          const note = previousNodeNotesByNodeId.get(node.id);
                          const preview = note ? stripHtmlForPreview(note.content) : '';
                          const isExpanded = expandedPreviousNoteNodeId === node.id;

                          if (!preview) {
                            return null;
                          }

                          return (
                            <Box
                              key={node.id}
                              sx={{
                                border: '1px solid',
                                borderColor: isExpanded ? 'primary.main' : 'divider',
                                borderRadius: 1.25,
                                overflow: 'hidden',
                                bgcolor: isExpanded ? 'rgba(76, 201, 216, 0.06)' : 'background.paper',
                              }}
                            >
                              <Button
                                fullWidth
                                size="small"
                                onClick={() =>
                                  setExpandedPreviousNoteNodeId((current) =>
                                    current === node.id ? null : node.id,
                                  )
                                }
                                sx={{
                                  justifyContent: 'flex-start',
                                  textTransform: 'none',
                                  minHeight: 28,
                                  px: 0.85,
                                  py: 0.25,
                                  overflow: 'hidden',
                                }}
                              >
                                <Typography
                                  component="span"
                                  variant="caption"
                                  fontWeight={950}
                                  sx={{ mr: 0.65, flexShrink: 0 }}
                                >
                                  {isExpanded ? '▾' : '▸'} {buildNodeNoteTitle(workspace, node)}
                                </Typography>

                                <Typography
                                  component="span"
                                  variant="caption"
                                  color="text.secondary"
                                  noWrap
                                  sx={{ minWidth: 0 }}
                                >
                                  {preview}
                                </Typography>
                              </Button>

                              {isExpanded && note ? (
                                <Box
                                  sx={{
                                    px: 1,
                                    pb: 0.85,
                                    pt: 0.25,
                                    typography: 'body2',
                                    color: 'text.primary',
                                    '& p': { my: 0.5 },
                                    '& ul': { my: 0.5, pl: 2 },
                                  }}
                                  dangerouslySetInnerHTML={{ __html: note.content }}
                                />
                              ) : null}
                            </Box>
                          );
                        })
                      )}
                    </Stack>
                  </Box>

                  <Divider />

                  <Box sx={{ minHeight: 0, display: 'flex', flexDirection: 'column', flex: 1 }}>
                    <Stack
                      direction="row"
                      justifyContent="space-between"
                      alignItems="flex-start"
                      spacing={1}
                      sx={{ mb: 0.75 }}
                    >
                      <Box sx={{ minWidth: 0 }}>
                        <Typography variant="caption" color="text.secondary" fontWeight={950}>
                          APUNTES DEL MOMENTO ACTUAL
                        </Typography>

                        <Typography
                          variant="body2"
                          sx={{
                            mt: 0.2,
                            color: 'text.primary',
                            fontWeight: 800,
                            lineHeight: 1.25,
                          }}
                        >
                          {activeNode
                            ? `${getStreetLabel(activeNode.street)} · Actúa ${getActorLabel(
                                workspace,
                                activeNode.acting_player,
                              )}`
                            : 'Sin nodo activo'}
                        </Typography>
                      </Box>

                      <Stack
                        direction="row"
                        spacing={0.45}
                        alignItems="center"
                        sx={{ flexShrink: 0, pt: 0.1 }}
                      >
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => applyNodeNotesCommand('bold')}
                          sx={{
                            minWidth: 30,
                            px: 0.65,
                            fontWeight: 950,
                            color: 'text.primary',
                            borderColor: 'rgba(76, 201, 216, 0.45)',
                            bgcolor: 'rgba(15, 23, 42, 0.22)',
                          }}
                        >
                          B
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => applyNodeNotesCommand('italic')}
                          sx={{
                            minWidth: 30,
                            px: 0.65,
                            fontStyle: 'italic',
                            fontWeight: 900,
                            color: 'text.primary',
                            borderColor: 'rgba(76, 201, 216, 0.45)',
                            bgcolor: 'rgba(15, 23, 42, 0.22)',
                          }}
                        >
                          I
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => applyNodeNotesCommand('insertUnorderedList')}
                          sx={{
                            minWidth: 30,
                            px: 0.65,
                            fontWeight: 950,
                            color: 'text.primary',
                            borderColor: 'rgba(76, 201, 216, 0.45)',
                            bgcolor: 'rgba(15, 23, 42, 0.22)',
                          }}
                        >
                          •
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => insertSuitInNodeNotes('♠', '#e5e7eb')}
                          sx={{
                            minWidth: 30,
                            px: 0.65,
                            fontWeight: 950,
                            color: '#e5e7eb',
                            borderColor: 'rgba(229, 231, 235, 0.45)',
                            bgcolor: 'rgba(15, 23, 42, 0.22)',
                          }}
                        >
                          ♠
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => insertSuitInNodeNotes('♥', '#ef4444')}
                          sx={{
                            minWidth: 30,
                            px: 0.65,
                            fontWeight: 950,
                            color: '#ef4444',
                            borderColor: 'rgba(239, 68, 68, 0.45)',
                            bgcolor: 'rgba(15, 23, 42, 0.22)',
                          }}
                        >
                          ♥
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => insertSuitInNodeNotes('♦', '#3b82f6')}
                          sx={{
                            minWidth: 30,
                            px: 0.65,
                            fontWeight: 950,
                            color: '#3b82f6',
                            borderColor: 'rgba(59, 130, 246, 0.45)',
                            bgcolor: 'rgba(15, 23, 42, 0.22)',
                          }}
                        >
                          ♦
                        </Button>

                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => insertSuitInNodeNotes('♣', '#22c55e')}
                          sx={{
                            minWidth: 30,
                            px: 0.65,
                            fontWeight: 950,
                            color: '#22c55e',
                            borderColor: 'rgba(34, 197, 94, 0.45)',
                            bgcolor: 'rgba(15, 23, 42, 0.22)',
                          }}
                        >
                          ♣
                        </Button>
                      </Stack>
                    </Stack>

                    <Box
                      ref={nodeNotesEditorRef}
                      contentEditable
                      suppressContentEditableWarning
                      onInput={syncNodeNotesContentFromEditor}
                      onBlur={() => void persistCurrentNodeNotes()}
                      sx={{
                        flex: 1,
                        minHeight: 260,
                        border: '1px solid',
                        borderColor: 'rgba(148, 163, 184, 0.28)',
                        borderRadius: 1.5,
                        p: 1.15,
                        overflow: 'auto',
                        outline: 'none',
                        typography: 'body2',
                        fontSize: 14,
                        lineHeight: 1.65,
                        color: 'rgba(255,255,255,0.92)',
                        bgcolor: 'rgba(15, 23, 42, 0.38)',
                        boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.03)',
                        '&:focus': {
                          borderColor: 'primary.main',
                          boxShadow: '0 0 0 1px rgba(76, 201, 216, 0.35)',
                        },
                        '& p': {
                          my: 0.5,
                          color: 'rgba(255,255,255,0.92)',
                        },
                        '& ul': {
                          my: 0.5,
                          pl: 2,
                          color: 'rgba(255,255,255,0.92)',
                        },
                        '& li': {
                          color: 'rgba(255,255,255,0.92)',
                        },
                        '& strong': {
                          fontWeight: 900,
                          color: '#ffffff',
                        },
                        '& em': {
                          fontStyle: 'italic',
                          color: 'rgba(255,255,255,0.96)',
                        },
                      }}
                    />
                  </Box>

                  {workspace.example_hands.length > 0 ? (
                    <Box>
                      <Divider sx={{ my: 1 }} />

                      <Typography variant="caption" color="text.secondary" fontWeight={950}>
                        MANOS EJEMPLO
                      </Typography>

                      <Stack spacing={0.8} sx={{ mt: 0.6 }}>
                        {workspace.example_hands.map((hand) => (
                          <Box key={hand.id}>
                            <Typography variant="subtitle2" fontWeight={900}>
                              {hand.title}
                            </Typography>
                            <Typography variant="caption" color="text.secondary">
                              {hand.hero_hand}
                              {hand.linked_hand_class ? ` · ${hand.linked_hand_class}` : ''}
                            </Typography>
                            {hand.summary ? (
                              <Typography variant="body2" sx={{ mt: 0.25 }}>
                                {hand.summary}
                              </Typography>
                            ) : null}
                          </Box>
                        ))}
                      </Stack>
                    </Box>
                  ) : null}
                </Stack>
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>

      <Dialog
        open={isSpotTheoryDialogOpen}
        onClose={() => setIsSpotTheoryDialogOpen(false)}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 3,
            bgcolor: '#0f172a',
            border: '1px solid rgba(148, 163, 184, 0.22)',
            boxShadow: '0 28px 80px rgba(0,0,0,0.52)',
            overflow: 'hidden',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2.4,
            py: 1.8,
            borderBottom: '1px solid rgba(148, 163, 184, 0.16)',
            background:
              'linear-gradient(135deg, rgba(76,201,216,0.14), rgba(15,23,42,0.1) 55%, rgba(2,6,23,0.2))',
          }}
        >
          <Stack
            direction={{ xs: 'column', sm: 'row' }}
            spacing={1}
            justifyContent="space-between"
            alignItems={{ xs: 'flex-start', sm: 'center' }}
          >
            <Box>
              <Typography
                sx={{
                  color: '#94a3b8',
                  fontSize: 11,
                  fontWeight: 950,
                  letterSpacing: 0.65,
                  textTransform: 'uppercase',
                  lineHeight: 1.1,
                }}
              >
                Consulta rápida
              </Typography>

              <Typography
                sx={{
                  color: '#f8fafc',
                  fontSize: 22,
                  fontWeight: 950,
                  lineHeight: 1.1,
                  mt: 0.45,
                }}
              >
                Teoría del spot
              </Typography>

              <Typography
                sx={{
                  color: '#94a3b8',
                  fontSize: 13,
                  fontWeight: 750,
                  mt: 0.5,
                }}
              >
                Lectura de la teoría general del spot. La edición se realiza desde la página del spot.
              </Typography>
            </Box>

            <Button
              size="small"
              variant="outlined"
              disabled={!workspace}
              onClick={() => {
                if (workspace?.board.spot_id) {
                  navigate(`/poker-study/spots/${workspace.board.spot_id}`);
                }
              }}
              sx={{
                borderRadius: 2,
                fontWeight: 900,
                textTransform: 'none',
                flexShrink: 0,
              }}
            >
              Editar teoría
            </Button>
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ bgcolor: 'rgba(15, 23, 42, 0.18)', p: 2 }}>
          {isLoadingSpotTheory ? (
            <Box
              sx={{
                minHeight: 320,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <CircularProgress size={28} />
            </Box>
          ) : stripHtmlForPreview(spotTheoryContent).length > 0 ? (
            <Box
              sx={{
                minHeight: 420,
                maxHeight: '68vh',
                overflowY: 'auto',
                overflowX: 'hidden',
                scrollbarWidth: 'thin',
                scrollbarColor: 'rgba(76, 201, 216, 0.65) rgba(15, 23, 42, 0.75)',
                borderRadius: 2.5,
                border: '1px solid rgba(148, 163, 184, 0.18)',
                bgcolor: 'rgba(2, 6, 23, 0.34)',
                p: 1.6,
                '&::-webkit-scrollbar': {
                  width: 9,
                },
                '&::-webkit-scrollbar-track': {
                  bgcolor: 'rgba(15, 23, 42, 0.75)',
                  borderRadius: 999,
                },
                '&::-webkit-scrollbar-thumb': {
                  bgcolor: 'rgba(76, 201, 216, 0.55)',
                  borderRadius: 999,
                  border: '2px solid rgba(15, 23, 42, 0.75)',
                },
                '&::-webkit-scrollbar-thumb:hover': {
                  bgcolor: 'rgba(76, 201, 216, 0.85)',
                },
                typography: 'body2',
                color: 'rgba(255,255,255,0.92)',
                lineHeight: 1.65,
                '& p': { my: 0.85 },
                '& ul': { my: 0.85, pl: 2.4 },
                '& ol': { my: 0.85, pl: 2.4 },
                '& li': { mb: 0.35 },
                '& strong': {
                  fontWeight: 950,
                  color: '#ffffff',
                },
                '& em': {
                  fontStyle: 'italic',
                  color: 'rgba(255,255,255,0.96)',
                },
              }}
              dangerouslySetInnerHTML={{ __html: spotTheoryContent }}
            />
          ) : (
            <Box
              sx={{
                minHeight: 320,
                borderRadius: 2.5,
                border: '1px dashed rgba(148, 163, 184, 0.22)',
                bgcolor: 'rgba(2, 6, 23, 0.24)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                textAlign: 'center',
                p: 3,
              }}
            >
              <Box>
                <Typography
                  sx={{
                    color: '#f8fafc',
                    fontSize: 18,
                    fontWeight: 950,
                    mb: 0.75,
                  }}
                >
                  Este spot todavía no tiene teoría escrita
                </Typography>

                <Typography
                  sx={{
                    color: '#94a3b8',
                    fontSize: 13,
                    fontWeight: 750,
                    maxWidth: 520,
                  }}
                >
                  Usa “Editar teoría” para abrir la página del spot y completar los apuntes generales.
                </Typography>
              </Box>
            </Box>
          )}
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.4,
            py: 1.6,
            borderTop: '1px solid rgba(148, 163, 184, 0.16)',
            bgcolor: 'rgba(2, 6, 23, 0.92)',
          }}
        >
          <Button
            variant="outlined"
            onClick={() => setIsSpotTheoryDialogOpen(false)}
            sx={{
              borderRadius: 2,
              fontWeight: 900,
              textTransform: 'none',
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(editingBoardPreflopRangeSummary)}
        onClose={closeBoardPreflopRangeEditor}
        fullWidth
        maxWidth="md"
        PaperProps={{
          sx: {
            borderRadius: 4,
            overflow: 'hidden',
            bgcolor: 'rgba(2, 6, 23, 0.98)',
            border: '1px solid rgba(148, 163, 184, 0.22)',
            boxShadow: '0 32px 90px rgba(0,0,0,0.55)',
          },
        }}
      >
        <DialogTitle
          sx={{
            px: 2.5,
            py: 2,
            borderBottom: '1px solid rgba(148, 163, 184, 0.16)',
            background:
              'linear-gradient(135deg, rgba(34,197,94,0.16), rgba(15,23,42,0.1) 55%, rgba(2,6,23,0.2))',
          }}
        >
          <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
            <Box>
              <Typography variant="caption" color="text.secondary" fontWeight={950}>
                RANGO PREFLOP DEL BOARD
              </Typography>
              <Typography variant="h6" fontWeight={950}>
                {editingBoardPreflopRangeSummary
                  ? `${editingBoardPreflopRangeSummary.title} · ${formatPositionLabel(
                      editingBoardPreflopRangeSummary.position,
                    )}`
                  : 'Rango'}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Se guarda como rango propio de este board. No modifica el rango base del spot.
              </Typography>
            </Box>

            <Chip
              size="small"
              label={`${getPreflopRangeComboCount(boardPreflopRangeDraft)} combos · ${formatPreflopRangePercentage(
                getPreflopRangeComboCount(boardPreflopRangeDraft),
              )}`}
              sx={{
                fontWeight: 900,
                bgcolor: 'rgba(34, 197, 94, 0.12)',
                border: '1px solid rgba(34, 197, 94, 0.32)',
              }}
            />
          </Stack>
        </DialogTitle>

        <DialogContent sx={{ bgcolor: 'rgba(15, 23, 42, 0.18)' }}>
          <Stack spacing={1.5} sx={{ pt: 1.5 }}>
            <Card
              variant="outlined"
              sx={{
                borderRadius: 3,
                bgcolor: 'rgba(255,255,255,0.035)',
                borderColor: 'rgba(148, 163, 184, 0.16)',
              }}
            >
              <CardContent sx={{ p: 1.35, '&:last-child': { pb: 1.35 } }}>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  spacing={1.2}
                  alignItems={{ xs: 'flex-start', md: 'center' }}
                  justifyContent="space-between"
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="caption" color="text.secondary" fontWeight={950}>
                      IMPORTAR PLANTILLA AL BOARD
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      Copia una plantilla al rango propio de este board. No modifica el spot ni la plantilla original.
                    </Typography>
                  </Box>

                  <Stack
                    direction="row"
                    spacing={0.75}
                    alignItems="center"
                    flexWrap="wrap"
                    justifyContent={{ xs: 'flex-start', md: 'flex-end' }}
                  >
                    {isLoadingRangeTemplates ? (
                      <Chip
                        size="small"
                        label="Cargando plantillas..."
                        sx={{ fontWeight: 900 }}
                      />
                    ) : compatibleBoardRangeTemplates.length === 0 ? (
                      <Chip
                        size="small"
                        variant="outlined"
                        label="Sin plantillas compatibles"
                        sx={{ fontWeight: 900 }}
                      />
                    ) : (
                      compatibleBoardRangeTemplates.slice(0, 6).map((template) => {
                        const templateHandClasses = getBoardRangeTemplateHandClasses(template);
                        const comboCount = getPreflopRangeComboCount(templateHandClasses);

                        return (
                          <Button
                            key={template.id}
                            size="small"
                            variant="outlined"
                            onClick={() => importTemplateIntoBoardPreflopDraft(template)}
                            sx={{
                              minHeight: 30,
                              borderRadius: 1.75,
                              px: 1.15,
                              fontSize: 11.5,
                              fontWeight: 900,
                              whiteSpace: 'nowrap',
                              flexShrink: 0,
                              textTransform: 'none',
                              borderColor: 'rgba(34, 197, 94, 0.36)',
                              bgcolor: 'rgba(34, 197, 94, 0.07)',
                              '&:hover': {
                                bgcolor: 'rgba(34, 197, 94, 0.13)',
                                borderColor: 'rgba(34, 197, 94, 0.58)',
                              },
                            }}
                          >
                            {template.name} · {comboCount} combos
                          </Button>
                        );
                      })
                    )}
                  </Stack>
                </Stack>

                {compatibleBoardRangeTemplates.length > 6 ? (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: 'block', mt: 1 }}
                  >
                    Mostrando las 6 primeras plantillas compatibles. Ajustaremos buscador/importador avanzado más adelante.
                  </Typography>
                ) : null}
              </CardContent>
            </Card>
            <Box
              sx={{
                display: 'grid',
                gridTemplateColumns: 'repeat(13, 42px)',
                gap: 0.45,
                justifyContent: 'center',
                p: 1.25,
                borderRadius: 3,
                bgcolor: 'rgba(15, 23, 42, 0.42)',
                border: '1px solid rgba(148, 163, 184, 0.16)',
                overflowX: 'auto',
              }}
            >
              {buildMatrix().flat().map((handClass) => {
                const isSelected = boardPreflopRangeDraft.has(handClass);
                const isPair = isPocketPairHandClass(handClass);

                return (
                  <Box
                    key={handClass}
                    onMouseDown={(event) => {
                      if (event.button !== 0) return;
                      event.preventDefault();
                      startBoardPreflopRangePaint(handClass);
                    }}
                    onMouseEnter={() => continueBoardPreflopRangePaint(handClass)}
                    sx={{
                      width: 42,
                      height: 34,
                      borderRadius: isPair ? 1.6 : 1.15,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      userSelect: 'none',
                      fontSize: 12,
                      fontWeight: isPair ? 1000 : 900,
                      color: isSelected ? '#052e16' : 'rgba(255,255,255,0.88)',
                      bgcolor: isSelected
                        ? '#22c55e'
                        : isPair
                          ? 'rgba(148, 163, 184, 0.12)'
                          : 'rgba(255,255,255,0.035)',
                      border: '1px solid',
                      borderColor: isSelected
                        ? 'rgba(134, 239, 172, 0.95)'
                        : isPair
                          ? 'rgba(226, 232, 240, 0.34)'
                          : 'rgba(148, 163, 184, 0.18)',
                      boxShadow: isSelected
                        ? '0 0 0 1px rgba(34, 197, 94, 0.22), 0 8px 18px rgba(34, 197, 94, 0.12)'
                        : isPair
                          ? 'inset 0 0 0 1px rgba(255,255,255,0.05)'
                          : 'none',
                      outline: isPair ? '1px solid rgba(255,255,255,0.08)' : 'none',
                      transition:
                        'background-color 120ms ease, border-color 120ms ease, transform 120ms ease',
                      '&:hover': {
                        borderColor: isSelected ? '#bbf7d0' : 'primary.main',
                        bgcolor: isSelected ? '#4ade80' : 'rgba(76, 201, 216, 0.12)',
                      },
                    }}
                  >
                    {handClass}
                  </Box>
                );
              })}
            </Box>

            <Typography variant="caption" color="text.secondary" textAlign="center">
              Arrastra para pintar o limpiar. Al guardar, este board tendrá rango propio y dejará de depender del rango heredado.
            </Typography>
          </Stack>
        </DialogContent>

        <DialogActions
          sx={{
            px: 2.5,
            py: 1.5,
            borderTop: '1px solid rgba(148, 163, 184, 0.16)',
            bgcolor: 'rgba(2, 6, 23, 0.92)',
          }}
        >
          <Button
            onClick={() => setBoardPreflopRangeDraft(new Set())}
            disabled={isSavingBoardPreflopRange || boardPreflopRangeDraft.size === 0}
          >
            Limpiar
          </Button>

          <Box sx={{ flex: 1 }} />

          <Button onClick={closeBoardPreflopRangeEditor} disabled={isSavingBoardPreflopRange}>
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={handleSaveBoardPreflopRange}
            disabled={isSavingBoardPreflopRange || !editingBoardPreflopRangeSummary}
            sx={{
              borderRadius: 2,
              fontWeight: 900,
              px: 2.4,
              boxShadow: '0 12px 28px rgba(34, 197, 94, 0.18)',
            }}
          >
            {isSavingBoardPreflopRange ? 'Guardando...' : 'Guardar rango board'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={Boolean(streetCardPickerOpen)}
        onClose={() => setStreetCardPickerOpen(null)}
        maxWidth={false}
        PaperProps={{
          sx: {
            borderRadius: 2,
            width: 'min(560px, calc(100vw - 32px))',
          },
        }}
      >
        <DialogTitle sx={{ pb: 0.5, fontWeight: 950 }}>
          {streetCardPickerOpen === 'river' ? 'RIVER' : 'TURN'}
        </DialogTitle>

        <DialogContent sx={{ pt: 1 }}>
          <CardPickerGrid
            layout="modal"
            blockedCards={streetCardPickerBlockedCards}
            onSelectCard={(card) => {
              void handleSelectStreetCard(card);
            }}
          />
        </DialogContent>

        <DialogActions sx={{ pt: 0.5 }}>
          <Button onClick={() => setStreetCardPickerOpen(null)}>
            Cancelar
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}